// Page-level strings (education, private model studio, admin and settings pages).
// English is the default; Simplified Chinese is the source copy for the other locales.
export const appPageEn = {
  admissionsDaily: {
    priority: {
      high: "Verify first: may involve a deadline",
      attention: "Please verify: may involve application requirements",
      normal: "Page content updated",
    },
    action: {
      deadline: "Check the deadline first",
      requirements: "Check materials and eligibility",
      content: "Read the official change",
    },
    deliveryStatus: {
      sent: "Sent",
      failed: "Failed to send",
      processing: "Processing",
    },
    listSeparator: ", ",
    partialError: "Some admissions data is temporarily unavailable ({{count}} items); the rest can still be viewed. Please refresh later.",
    title: "Official admissions change daily",
    intro: "Only registered official undergraduate admissions pages are monitored. A first capture does not mean the school just published it; fetch failures are shown explicitly and are never treated as \"no change\".",
    reload: "Reload",
    coverage: {
      total: "Reviewed official sources",
      checked: "Verified",
      failed: "Failed, awaiting retry",
      stale: "Stale / unsuccessful",
      overHours: "Over {{hours}} hours",
    },
    lastScan: "Last scan: {{start}} · {{status}} · checked {{checked}} / changed {{changed}} / failed {{failed}}",
    completedAt: " · completed {{time}}",
    attention: {
      title: "Source health issues needing attention",
      desc: "Fetch results for these sources are not enough to conclude \"no updates\". Please open the official page to verify; admins can review or replace the entry under source governance.",
      fetchFailed: "Fetch failed: {{error}}",
      noSuccess: "No successful fetch yet, or not updated for over {{hours}} hours",
    },
    cohort: {
      title: "Institution scope and source governance",
      body: "{{source}} {{edition}}: {{target}} default candidate institutions (20 each in the US, China and Canada). {{verified}} official undergraduate sources verified; the remaining {{pending}} are awaiting manual review and are not included in fetching or the daily digest.",
      verified: "Verified {{verified}}/{{target}}",
      pending: "{{pending}} awaiting manual review",
    },
    cohortDaily: {
      title: "Top 20 official updates today across three countries",
      desc: "Since {{start}}, counting only reviewed official undergraduate sources; summary generated {{generated}}. Schools awaiting review are not counted as \"no updates\".",
      today: "Changes today {{changed}} · covered {{active}}/{{target}}",
      failedLine: "Fetch failed {{failed}} · stale/unsuccessful {{stale}} · pending review {{pending}}",
      none: "No page changes recorded today for reviewed sources; the baseline from a first scan is not counted as a change.",
    },
    mail: {
      title: "Daily email digest",
      desc: "When enabled, a daily digest is sent after the official-source scan completes; it only includes changes for schools you follow.",
      lastDelivered: " Last delivered: {{date}}",
      notConfigured: "The email service has not been configured by an admin, so delivery cannot be enabled yet.",
      lastFailed: "Last delivery failed: {{error}}",
      contactAdmin: "Please contact an admin to check the email configuration.",
      setupLink: "Go to email service settings (admin)",
    },
    deliveries: {
      title: "Email delivery log",
      desc: "Recent daily digest delivery attempts. Failed ones are safely retried in later scans.",
      row: "{{date}} · {{status}} · {{count}} changes",
      sentAt: "Sent {{time}}",
      waiting: "Waiting for delivery to finish",
    },
    suggestions: {
      title: "Follow by your target countries",
      body: "Profile targets: {{countries}}. {{count}} reviewed official sources can be added; this does not treat major exploration results as admission predictions.",
      unsupported: "{{countries}} is not yet in this phase's US/China/Canada Top 20 official source scope, so it will not be shown as covered.",
      followAll: "Follow all reviewed sources",
    },
    digest: {
      title: "Related to schools you follow",
      window: "Digest window: from {{start}} · generated {{generated}}",
      countryLine: "{{country}}: following {{watched}} · changed today {{changed}} · failed {{failed}} · stale/unsuccessful {{stale}}",
      emptyWatched: "No new page changes found for schools you follow since 00:00 UTC today.",
      emptyNone: "Select schools below and save to generate a personalized digest.",
    },
    suggestedAction: "Suggested: {{hint}}",
    newContent: "Content added to the page",
    removedContent: "Content removed or replaced on the page",
    viewOfficial: "View official source and verify",
    changes: {
      title: "Detected substantive page changes",
      empty: "No changes recorded yet. The first scan establishes a baseline; only later scans produce reviewable change records.",
    },
    watch: {
      title: "Followed schools and coverage status",
      hint: "Select and save; the digest only summarizes changes for these schools.",
      save: "Save followed schools",
      country: "Country",
      filterCountry: "Filter followed schools by country",
      status: "Source status",
      filterHealth: "Filter followed schools by source health",
      all: "All",
      attention: "Needs attention",
      healthy: "Healthy",
      showing: "Showing {{shown}}/{{total}} schools",
      officialPage: "Official undergraduate admissions page",
      lastSuccess: "Last successful fetch: {{time}}",
      noSuccess: "No successful fetch yet.",
      staleOver: "Data is over {{hours}} hours old.",
      noMatch: "No official sources match the current filters.",
    },
  },
  educationCenter: {
    title: "College Planning & Admissions Updates",
    desc: "Save a study profile, explore majors, and track admissions from official sources. Results are not admission predictions.",
    planning: "College planning",
    admissions: "Admissions daily",
  },
  educationPlanning: {
    defaultCountry: "China",
    defaultCurriculum: "Chinese national high school curriculum",
    subject: {
      chinese: "Chinese",
      math: "Mathematics",
      foreign: "Foreign language",
      physics: "Physics",
      history: "History",
      chemistry: "Chemistry",
      biology: "Biology",
      geography: "Geography",
      politics: "Politics",
    },
    category: {
      strong: "Explore first",
      consider: "Worth a closer look",
      not_recommended: "Conflicts found",
      ineligible: "Subject requirements not met",
    },
    title: "Major selection and study paths",
    intro: "Record your real courses and preferences first, then explore major paths. Institution eligibility and admissions updates are verified against official rules in later stages.",
    readiness: {
      title: "Study decision readiness",
      progress: "{{done}}/{{total}} complete. This is an action checklist, not an admission probability.",
      viewAdmissions: "View official admissions daily",
    },
    field: {
      name: "Name",
      namePlaceholder: "e.g. Danny",
      province: "Gaokao province",
      provincePlaceholder: "e.g. Shaanxi",
      year: "Expected enrollment year",
      curriculum: "Curriculum",
      countries: "Target countries",
      countriesPlaceholder: "China, Canada, United States",
      mode: "New gaokao mode",
      electives: "Elective subjects",
      electivesPlaceholder: "e.g. Physics, Chemistry, Biology",
      favorites: "Favorite subjects",
      favoritesPlaceholder: "e.g. Mathematics, Physics",
      dislikes: "Subjects you dislike or want to avoid",
      dislikesPlaceholder: "e.g. Chemistry",
      interests: "Interests",
      interestsPlaceholder: "AI, healthcare",
      budget: "Budget or constraints (optional)",
      budgetPlaceholder: "e.g. prioritize scholarships, prefer to stay in Vancouver.",
    },
    saveProfile: "Save profile",
    exploreMajors: "Explore Chinese majors by scores",
    cmpt: {
      title: "Career direction exploration (CMPT310)",
      desc: "An experimental model independent of the gaokao major exploration. It does not predict admission or employment; submitted data is written only to a temporary CSV for a subprocess and deleted before the response.",
      unavailable: "The connector is currently unavailable.",
      adminRequires: "An admin needs to configure: {{items}}.",
      listSeparator: ", ",
      run: "Run experimental career exploration",
      confidence: "Model confidence {{percent}}%",
    },
    results: {
      title: "Major exploration results",
      nextStep: "Next: view official admissions daily",
      noConflict: "No obvious conflicts found in subject and elective requirements.",
      match: "Subject match {{percent}}%",
    },
  },
  privateModel: {
    loadFailed: "Unable to load the model studio",
    actionFailed: "Action failed",
    uploaded: "Uploaded, awaiting review",
    viewFailed: "Unable to view the content",
    logFailed: "Unable to read the log",
    queryFailed: "Local model query failed",
    title: "Private Model Studio",
    intro: "Review company materials, train a dedicated small model, check held-out samples, then publish it to local Ollama.",
    status: "Local training: {{ready}} · approved documents {{documents}} · approved training samples {{examples}}",
    ready: "ready",
    notReady: "not ready",
    upload: {
      title: "Upload & review",
      desc: "Use .txt, .md or .pdf for documents; use JSONL for training samples, with instruction and output on each line. All content starts in a pending-review state.",
      aria: "Upload private data",
    },
    item: {
      document: "Document",
      example: "Sample",
      viewFull: "View full text",
      approve: "Approve",
      reject: "Reject",
      approved: "Approved",
      rejected: "Rejected",
      empty: "No materials yet",
    },
    qa: {
      title: "Local knowledge Q&A",
      desc: "Searches only approved documents and shows the sources used. The model runs on the server's local Ollama.",
      base06: "Qwen3 0.6B base model",
      base17: "Qwen3 1.7B base model",
      noModels: "Local Ollama has no models yet; install a base model first, or finish training and publish.",
      placeholder: "Ask a question about company knowledge",
      ask: "Ask",
      source: "Source: {{filename}} #{{id}}",
    },
    train: {
      title: "Train, evaluate & publish",
      desc: "Approve at least 5 samples. Training holds out some samples for independent evaluation; scores are for reference only, so check each answer before publishing.",
      workerNeeded: "Enable the worker on the local training host and install the LLaMA-Factory CLI and Ollama.",
      start: "Start training",
      submitted: "Training job submitted",
    },
    job: {
      title: "Job #{{id}} · {{model}}",
      samples: "{{state}} · {{count}} samples",
      viewLog: "View local log",
      localModel: "Local model: ",
      evaluate: "Evaluate candidate model",
      evaluating: "Evaluation of the candidate model started",
      holdout: "Held-out samples {{count}} · token F1 {{f1}} (manual review still required)",
      sample: "Sample #{{id}} · F1 {{f1}}",
      expected: "Reference answer: {{text}}",
      actual: "Model answer: {{text}}",
      reviewed: "I have reviewed each held-out sample answer",
      publish: "Approve & publish",
      publishing: "Publishing to local Ollama",
      empty: "No training jobs yet",
    },
    dialog: {
      aria: "Full text of material under review",
      close: "Close",
      question: "Question: {{text}}",
      answer: "Answer: {{text}}",
    },
  },
  adminAdmissions: {
    runStatus: {
      completed: "Completed",
      partial: "Partially completed",
      failed: "Run failed",
      running: "Running",
    },
    queueNoWorker: "Review queue loaded, but daily job status is unavailable",
    queueNoHealth: "Review queue loaded, but source health is unavailable",
    urlRequired: "Enter the HTTPS URL of the official undergraduate admissions page",
    enabled: "Official admissions monitoring enabled for {{university}}",
    notSaved: "Review was not saved",
    syncDone: "Sync complete: checked {{checked}}, changed {{changed}}, failed {{failed}}",
    syncFailed: "Official source sync failed",
    title: "Official admissions source review",
    desc: "Only HTTPS official undergraduate admissions pages approved by manual review are fetched daily. The reviewer, time and note are recorded when saved.",
    syncNow: "Sync reviewed sources now",
    showPending: "Show pending only",
    showVerified: "Show verified ({{count}})",
    filterAria: "Filter review queue by country",
    allCountries: "All countries",
    counts: "Pending review: {{pending}}; showing {{shown}}.",
    countsSources: " Reviewed sources: {{total}}, of which {{failed}} failed and {{stale}} stale/unsuccessful (threshold {{hours}} hours).",
    lastScan: "Last scan: {{start}} · {{status}} · checked {{checked}} / changed {{changed}} / failed {{failed}}",
    completedAt: " · completed {{time}}",
    workerOn: "Daily monitoring job is enabled, polling every {{hours}} hours.",
    workerOff: "Daily monitoring job is not enabled; data can only be updated with \"Sync now\".",
    rank: "{{country}} · rank in scope {{rank}}",
    verified: "Verified",
    pending: "Pending review",
    fetchFailed: "Fetch failed: {{error}}",
    staleHint: "Source is stale or has no successful fetch yet; open the official page, review it, then sync now.",
    urlPlaceholder: "https://… official undergraduate admissions page",
    notePlaceholder: "Review note (state the applicant category and enrollment year it applies to)",
    openSource: "Open registered source",
    updateReview: "Update reviewed source",
    approve: "Review and enable monitoring",
  },
  openOii: {
    requestFailed: "OpenOii config request failed ({{status}})",
    invalidFormat: "OpenOii returned an invalid config format",
    readFailed: "Failed to load",
    saved: "OpenOii config saved",
    restart: "Restart the OpenOii service to apply the config: {{keys}}",
    saveFailed: "Save failed",
    title: "OpenOii configuration",
    tokenLabel: "OpenOii admin token",
    tokenPlaceholder: "An admin token is required to save the config; used for this session only",
    loading: "Loading configuration…",
    retry: "Retry",
    maskedPlaceholder: "Configured; leave blank to keep the current value",
    save: "Save configuration",
  },
  stockIntelligence: {
    saved: "Stock intelligence settings saved",
    title: "Stock Intelligence",
    intro: "Manage default settings for AlphaSift, Daily Stock Analysis and Vibe Research in one place.",
    alpha: {
      desc: "Set the strategy, candidate count and research context prefilled each time the stock screener opens.",
      strategy: "Default strategy",
      strategyPlaceholder: "Leave blank to use the first available strategy",
      maxResults: "Default candidate count",
      context: "Research context (optional)",
      contextPlaceholder: "e.g. focus on companies with high free cash flow and low debt",
      useLlm: "Enable AI ranking by default",
    },
    dsa: {
      desc: "Optionally enter the URL of a deployed Daily Stock Analysis service; leave blank to use the desktop built-in service.",
      url: "Service URL",
      urlPlaceholder: "e.g. http://127.0.0.1:8000",
    },
    vibe: {
      desc: "Set the Vibe Research workspace URL. Defaults to the workspace hosted by Hanggent.",
      url: "Workspace URL",
      urlPlaceholder: "/vibe-research/ or https://…",
    },
    restored: "Defaults restored",
    restore: "Restore defaults",
    open: "Open Stock Intelligence",
    save: "Save settings",
    alertsSaved: "Alert settings saved; the project settings page is in sync",
    alertsSaveFailed: "Failed to save Stock Alerts settings",
    introWithAlerts: "Manage default settings for Stock Alerts, AlphaSift, Daily Stock Analysis and Vibe Research in one place.",
    alerts: {
      desc: "Set price-move alerts and email notifications per market.",
      loading: "Loading…",
      trigger: "Trigger threshold (%)",
      step: "Step threshold (%)",
      enable: "Enable alerts",
      email: "Email notifications",
      extended: "Include pre-market and after-hours",
      save: "Save {{market}} settings",
    },
    market: {
      cn: "A-shares",
      us: "US stocks",
    },
  },
  subprojects: {
    err: {
      protocol: "Service URLs must use HTTP or HTTPS",
      httpsRequired: "HTTPS pages require HTTPS service URLs",
      comfyui: "Use this site's proxy URL for ComfyUI",
      url: "Service URLs must be HTTP, HTTPS or a path on this site",
    },
  },
  settingsMisc: {
    saveAlphaDefaults: "Save as defaults (syncs with Hanggent Settings)",
    openProjectSettings: "Open project settings",
    emailService: "Email service",
  },
  settingsErrors: {
    stockAlertsRequest: "Stock Alerts settings request failed ({{status}})",
    stockAlertsFormat: "Invalid Stock Alerts settings format",
    stockAlertsMissing: "The settings for this market no longer exist; please reload",
  },
  zhituCareer: {
    heading: "China job search and career matching",
    description: "Upload a résumé, search jobs in China, and shortlist the best roles using Zhitu Career's Chinese career matching and Career-Ops evaluation reports.",
    runLabel: "Search jobs in China",
    resultsLabel: "Licensed China job data sources",
  },
  adminMail: {
    loadFailed: "Unable to load the email service status",
    verified: "QQ mailbox verified and saved",
    saveFailed: "Verification or saving failed",
    testSent: "Test email sent to {{email}}",
    testFailed: "Failed to send the test email",
    title: "Unified email service",
    desc: "Admissions digests, stock alerts and vehicle/market alerts share this QQ sender account. The authorization code is stored encrypted and is never shown again.",
    configured: "Configured: {{email}}",
    notConfigured: "Not configured yet",
    qqEmail: "QQ mailbox",
    emailPlaceholder: "Enter your QQ mailbox",
    smtpCode: "SMTP authorization code",
    hint: "QQ SMTP login is verified on save; the test email is sent only to the QQ mailbox above.",
    verifySave: "Verify and save",
    sendTest: "Send test email",
  },
};

export const appPageZhHans: typeof appPageEn = {
  admissionsDaily: {
    priority: {
      high: "优先核验：可能涉及截止日期",
      attention: "建议核验：可能涉及申请要求",
      normal: "页面内容更新",
    },
    action: {
      deadline: "先核对截止日期",
      requirements: "核对材料与资格",
      content: "阅读官方变更",
    },
    deliveryStatus: {
      sent: "已发送",
      failed: "发送失败",
      processing: "处理中",
    },
    listSeparator: "、",
    partialError: "部分招生数据暂不可用（{{count}} 项）；其余内容仍可查看。请稍后刷新。",
    title: "官方招生变更日报",
    intro: "仅监测登记的官方本科招生入口。首次收录不等同于学校刚发布；抓取失败会明确显示，不会被视为“无变化”。",
    reload: "重新加载",
    coverage: {
      total: "已审核官方来源",
      checked: "已完成核验",
      failed: "失败待重试",
      stale: "数据陈旧 / 未成功",
      overHours: "超过 {{hours}} 小时",
    },
    lastScan: "最近扫描：{{start}} · {{status}} · 检查 {{checked}} / 变化 {{changed}} / 失败 {{failed}}",
    completedAt: " · 完成于 {{time}}",
    attention: {
      title: "需要处理的来源健康问题",
      desc: "这些来源的抓取结果不足以支持“暂无更新”的判断。请打开官方页面核验；管理员可在来源治理中复核或替换入口。",
      fetchFailed: "抓取失败：{{error}}",
      noSuccess: "尚无成功抓取或超过 {{hours}} 小时未更新",
    },
    cohort: {
      title: "院校范围与来源治理",
      body: "{{source}} {{edition}}：{{target}} 所默认候选院校（美、中、加各 20 所）。已核验官方本科来源 {{verified}} 所；其余 {{pending}} 所仍待人工复核，未加入抓取或日报。",
      verified: "已核验 {{verified}}/{{target}}",
      pending: "待人工复核 {{pending}} 所",
    },
    cohortDaily: {
      title: "三国 Top20 当日官方更新",
      desc: "自 {{start}} 起，仅统计已审核官方本科来源；摘要生成于 {{generated}}。待复核学校不会被计为“无更新”。",
      today: "今日变化 {{changed}} · 已覆盖 {{active}}/{{target}}",
      failedLine: "抓取失败 {{failed}} · 陈旧/未成功 {{stale}} · 待复核 {{pending}}",
      none: "当日尚未记录到已审核来源的页面变更；首次扫描建立基线不计为变更。",
    },
    mail: {
      title: "每日邮件摘要",
      desc: "开启后，完成官方来源扫描时会发送当日摘要；仅包含你关注学校的变化。",
      lastDelivered: " 最近投递：{{date}}",
      notConfigured: "邮件服务尚未由管理员配置，暂时不能开启投递。",
      lastFailed: "最近投递失败：{{error}}",
      contactAdmin: "请联系管理员检查邮件配置。",
      setupLink: "前往邮件服务设置（管理员）",
    },
    deliveries: {
      title: "邮件投递记录",
      desc: "最近的日报投递尝试。失败记录会在后续扫描中安全重试。",
      row: "{{date}} · {{status}} · {{count}} 项变化",
      sentAt: "发送于 {{time}}",
      waiting: "等待发送完成",
    },
    suggestions: {
      title: "按你的目标国家关注",
      body: "档案目标：{{countries}}。可加入 {{count}} 所已审核官方来源；这不会把专业探索结果误当作录取预测。",
      unsupported: "{{countries}} 暂未纳入本期美、中、加 Top20 官方来源范围，因此不会显示为已覆盖。",
      followAll: "关注全部已审核来源",
    },
    digest: {
      title: "与你关注学校相关",
      window: "日报窗口：{{start}} 起 · 生成于 {{generated}}",
      countryLine: "{{country}}：关注 {{watched}} · 今日变更 {{changed}} · 失败 {{failed}} · 陈旧/未成功 {{stale}}",
      emptyWatched: "自今日 UTC 00:00 起，暂未发现你关注学校的新页面变更。",
      emptyNone: "在下方勾选学校并保存，即可生成个性化日报。",
    },
    suggestedAction: "建议：{{hint}}",
    newContent: "页面新增内容",
    removedContent: "页面移除或替换内容",
    viewOfficial: "查看官方来源并核验",
    changes: {
      title: "检测到的实质页面变更",
      empty: "尚无已记录变更。首次扫描会建立基线；后续扫描才会形成可复查的变化记录。",
    },
    watch: {
      title: "关注学校与覆盖状态",
      hint: "勾选后保存；日报只汇总这些学校的变更。",
      save: "保存关注学校",
      country: "国家",
      filterCountry: "按国家筛选关注学校",
      status: "来源状态",
      filterHealth: "按来源健康筛选关注学校",
      all: "全部",
      attention: "需处理",
      healthy: "健康",
      showing: "显示 {{shown}}/{{total}} 所",
      officialPage: "官方本科招生页",
      lastSuccess: "最近成功抓取：{{time}}",
      noSuccess: "尚无成功抓取。",
      staleOver: "数据已超过 {{hours}} 小时。",
      noMatch: "没有符合当前筛选条件的官方来源。",
    },
  },
  educationCenter: {
    title: "升学规划与招生动态",
    desc: "保存学习档案、探索专业，并按官方来源跟踪招生信息。结果不代表录取预测。",
    planning: "升学规划",
    admissions: "招生日报",
  },
  educationPlanning: {
    defaultCountry: "中国",
    defaultCurriculum: "中国普通高中",
    subject: {
      chinese: "语文",
      math: "数学",
      foreign: "外语",
      physics: "物理",
      history: "历史",
      chemistry: "化学",
      biology: "生物",
      geography: "地理",
      politics: "政治",
    },
    category: {
      strong: "优先探索",
      consider: "可进一步了解",
      not_recommended: "存在冲突",
      ineligible: "当前不满足选科",
    },
    title: "专业选择与升学路径",
    intro: "先记录真实课程与偏好，再探索专业路径。院校资格和招生动态将在后续阶段以官方规则核验。",
    readiness: {
      title: "升学决策就绪度",
      progress: "完成 {{done}}/{{total}} 项。这是行动清单，不是录取概率。",
      viewAdmissions: "查看官方招生日报",
    },
    field: {
      name: "称呼",
      namePlaceholder: "例如：Danny",
      province: "高考省份",
      provincePlaceholder: "例如：陕西",
      year: "预计入学年份",
      curriculum: "课程体系",
      countries: "目标国家",
      countriesPlaceholder: "中国, 加拿大, 美国",
      mode: "新高考模式",
      electives: "选考科目",
      electivesPlaceholder: "物理, 化学, 生物",
      favorites: "喜欢的科目",
      favoritesPlaceholder: "数学, 物理",
      dislikes: "不喜欢或想避免的科目",
      dislikesPlaceholder: "化学",
      interests: "兴趣方向",
      interestsPlaceholder: "人工智能, 医疗健康",
      budget: "预算或限制（可选）",
      budgetPlaceholder: "例如：优先奖学金、希望留在温哥华。",
    },
    saveProfile: "保存档案",
    exploreMajors: "按成绩探索中国专业",
    cmpt: {
      title: "职业方向探索（CMPT310）",
      desc: "独立于高考专业探索的实验性模型。它不预测录取或就业；提交的数据仅写入临时 CSV 供子进程计算，响应前删除。",
      unavailable: "当前连接器不可用。",
      adminRequires: "管理员需配置：{{items}}。",
      listSeparator: "、",
      run: "实验性职业方向探索",
      confidence: "模型置信度 {{percent}}%",
    },
    results: {
      title: "专业探索结果",
      nextStep: "下一步：查看官方招生日报",
      noConflict: "学科和选科条件没有发现明显冲突。",
      match: "学科匹配 {{percent}}%",
    },
  },
  privateModel: {
    loadFailed: "无法读取模型工作台",
    actionFailed: "操作失败",
    uploaded: "已上传，等待审核",
    viewFailed: "无法查看内容",
    logFailed: "无法读取日志",
    queryFailed: "本地模型查询失败",
    title: "私有模型工作台",
    intro: "审核企业资料，训练专属小模型，检查留出样本，再发布到本地 Ollama。",
    status: "本地训练：{{ready}} · 已批准文档 {{documents}} · 已批准训练样本 {{examples}}",
    ready: "就绪",
    notReady: "未就绪",
    upload: {
      title: "上传与审核",
      desc: "文档使用 .txt、.md 或 .pdf；训练样本使用 JSONL，每行包含 instruction 和 output。所有内容先进入待审核状态。",
      aria: "上传私有数据",
    },
    item: {
      document: "文档",
      example: "样本",
      viewFull: "查看全文",
      approve: "批准",
      reject: "拒绝",
      approved: "已批准",
      rejected: "已拒绝",
      empty: "暂无资料",
    },
    qa: {
      title: "本地知识问答",
      desc: "只检索已批准的文档，并显示使用的来源。模型运行在服务器本机 Ollama。",
      base06: "Qwen3 0.6B 基础模型",
      base17: "Qwen3 1.7B 基础模型",
      noModels: "本机 Ollama 尚无模型；请先安装基础模型，或完成训练并发布。",
      placeholder: "输入企业知识问题",
      ask: "提问",
      source: "来源：{{filename}} #{{id}}",
    },
    train: {
      title: "训练、评测与发布",
      desc: "至少批准 5 条样本。训练会保留一部分样本用于独立评测；评测分数仅供参考，发布前请逐条检查回答。",
      workerNeeded: "需要在本地训练主机启用 worker，并安装 LLaMA-Factory CLI 与 Ollama。",
      start: "开始训练",
      submitted: "训练任务已提交",
    },
    job: {
      title: "任务 #{{id}} · {{model}}",
      samples: "{{state}} · {{count}} 条样本",
      viewLog: "查看本地日志",
      localModel: "本地模型：",
      evaluate: "评测候选模型",
      evaluating: "开始评测候选模型",
      holdout: "留出样本 {{count}} 条 · 词项 F1 {{f1}}（人工复核仍必需）",
      sample: "样本 #{{id}} · F1 {{f1}}",
      expected: "参考答案：{{text}}",
      actual: "模型回答：{{text}}",
      reviewed: "我已逐条复核留出样本的模型回答",
      publish: "审核通过并发布",
      publishing: "正在发布到本地 Ollama",
      empty: "暂无训练任务",
    },
    dialog: {
      aria: "审核资料全文",
      close: "关闭",
      question: "问题：{{text}}",
      answer: "答案：{{text}}",
    },
  },
  adminAdmissions: {
    runStatus: {
      completed: "已完成",
      partial: "部分完成",
      failed: "运行失败",
      running: "运行中",
    },
    queueNoWorker: "审核队列已加载，但每日任务状态暂不可用",
    queueNoHealth: "审核队列已加载，但来源健康状态暂不可用",
    urlRequired: "请填写官方本科招生页 HTTPS URL",
    enabled: "{{university}} 已启用官方招生监测",
    notSaved: "审核未保存",
    syncDone: "同步完成：检查 {{checked}}，变化 {{changed}}，失败 {{failed}}",
    syncFailed: "官方来源同步失败",
    title: "招生官方来源审核",
    desc: "只有人工审核通过的 HTTPS 官方本科招生页才会被每日抓取。保存时记录审核人、时间与说明。",
    syncNow: "立即同步已审核来源",
    showPending: "仅显示待审核",
    showVerified: "显示已核验 ({{count}})",
    filterAria: "按国家筛选审核队列",
    allCountries: "全部国家",
    counts: "待审核 {{pending}} 所；当前显示 {{shown}} 所。",
    countsSources: " 已审核来源 {{total}} 所，其中失败 {{failed}} 所、陈旧/未成功 {{stale}} 所（阈值 {{hours}} 小时）。",
    lastScan: "最近扫描：{{start}} · {{status}} · 检查 {{checked}} / 变化 {{changed}} / 失败 {{failed}}",
    completedAt: " · 完成于 {{time}}",
    workerOn: "每日监测任务已启用，轮询周期 {{hours}} 小时。",
    workerOff: "每日监测任务当前未启用；只能依靠“立即同步”更新数据。",
    rank: "{{country}} · 范围内序号 {{rank}}",
    verified: "已核验",
    pending: "待审核",
    fetchFailed: "抓取失败：{{error}}",
    staleHint: "来源陈旧或尚无成功抓取；请打开官方页面复核后立即同步。",
    urlPlaceholder: "https://… 官方本科招生页",
    notePlaceholder: "审核说明（建议写明适用申请人类别与入学年份）",
    openSource: "打开已登记来源",
    updateReview: "更新审核来源",
    approve: "审核并启用监测",
  },
  openOii: {
    requestFailed: "OpenOii 配置请求失败 ({{status}})",
    invalidFormat: "OpenOii 返回的配置格式无效",
    readFailed: "读取失败",
    saved: "OpenOii 配置已保存",
    restart: "需要重启 OpenOii 服务以应用配置：{{keys}}",
    saveFailed: "保存失败",
    title: "OpenOii 配置",
    tokenLabel: "OpenOii 管理员令牌",
    tokenPlaceholder: "保存配置需要管理员令牌；仅用于本次会话",
    loading: "正在读取配置…",
    retry: "重试",
    maskedPlaceholder: "已配置；留空保留原值",
    save: "保存配置",
  },
  stockIntelligence: {
    saved: "股票智能配置已保存",
    title: "股票智能",
    intro: "集中管理 AlphaSift、Daily Stock Analysis 与 Vibe Research 的默认配置。",
    alpha: {
      desc: "设定每次打开选股页面时预填的策略、候选数量和研究背景。",
      strategy: "默认策略",
      strategyPlaceholder: "留空以使用首个可用策略",
      maxResults: "默认候选数量",
      context: "研究背景（可选）",
      contextPlaceholder: "例如：关注高自由现金流与低负债公司",
      useLlm: "默认启用 AI 排名",
    },
    dsa: {
      desc: "可选填入已部署的 Daily Stock Analysis 服务地址；留空则使用桌面端内置服务。",
      url: "服务地址",
      urlPlaceholder: "例如：http://127.0.0.1:8000",
    },
    vibe: {
      desc: "设置 Vibe Research 工作区地址。默认使用 Hanggent 托管的工作区。",
      url: "工作区地址",
      urlPlaceholder: "/vibe-research/ 或 https://…",
    },
    restored: "已恢复默认配置",
    restore: "恢复默认",
    open: "打开股票智能",
    save: "保存配置",
    alertsSaved: "告警配置已保存，项目配置页同步更新",
    alertsSaveFailed: "保存 Stock Alerts 配置失败",
    introWithAlerts: "集中管理 Stock Alerts、AlphaSift、Daily Stock Analysis 与 Vibe Research 的默认配置。",
    alerts: {
      desc: "按市场设置价格变动提醒与邮件通知。",
      loading: "正在加载…",
      trigger: "触发阈值 (%)",
      step: "递进阈值 (%)",
      enable: "启用告警",
      email: "邮件通知",
      extended: "包含盘前和盘后",
      save: "保存 {{market}}配置",
    },
    market: {
      cn: "A 股",
      us: "美股",
    },
  },
  subprojects: {
    err: {
      protocol: "服务地址必须使用 HTTP 或 HTTPS",
      httpsRequired: "HTTPS 页面需要 HTTPS 服务地址",
      comfyui: "ComfyUI 请使用本站代理地址",
      url: "服务地址必须是 HTTP、HTTPS 地址或本站路径",
    },
  },
  settingsMisc: {
    saveAlphaDefaults: "保存为默认配置（同步 Hanggent Settings）",
    openProjectSettings: "打开项目配置页",
    emailService: "邮件服务",
  },
  settingsErrors: {
    stockAlertsRequest: "Stock Alerts 配置请求失败 ({{status}})",
    stockAlertsFormat: "Stock Alerts 配置格式无效",
    stockAlertsMissing: "该市场的配置已不存在，请重新加载",
  },
  zhituCareer: {
    heading: "中国职位搜索与职业匹配",
    description: "上传简历，搜索中国职位，并使用 Zhitu Career 的中文职业匹配与 Career-Ops 评估报告筛选最合适的岗位。",
    runLabel: "搜索中国职位",
    resultsLabel: "授权中国职位数据源",
  },
  adminMail: {
    loadFailed: "无法读取邮件服务状态",
    verified: "QQ 邮箱已验证并保存",
    saveFailed: "验证或保存失败",
    testSent: "测试邮件已发送到 {{email}}",
    testFailed: "测试邮件发送失败",
    title: "统一邮件服务",
    desc: "招生日报、股票提醒和车辆/市场提醒共用此 QQ 发件账号。授权码加密保存，不会再显示。",
    configured: "已配置：{{email}}",
    notConfigured: "尚未配置",
    qqEmail: "QQ 邮箱",
    emailPlaceholder: "请输入 QQ 邮箱",
    smtpCode: "SMTP 授权码",
    hint: "保存时验证 QQ SMTP 登录；测试邮件仅发送到上述 QQ 邮箱。",
    verifySave: "验证并保存",
    sendTest: "发送测试邮件",
  },
};

const appPage_ar: typeof appPageEn = {
  admissionsDaily: {
    priority: {
      high: "تحقق أولاً: قد يتعلق بموعد نهائي",
      attention: "يُنصح بالتحقق: قد يتعلق بمتطلبات التقديم",
      normal: "تم تحديث محتوى الصفحة",
    },
    action: {
      deadline: "تحقق من الموعد النهائي أولاً",
      requirements: "تحقق من المستندات والأهلية",
      content: "اقرأ التغيير الرسمي",
    },
    deliveryStatus: {
      sent: "تم الإرسال",
      failed: "فشل الإرسال",
      processing: "قيد المعالجة",
    },
    listSeparator: "،",
    partialError: "بعض بيانات القبول غير متاحة مؤقتاً ({{count}} عنصر)؛ يمكن الاطلاع على بقية المحتوى. يرجى التحديث لاحقاً.",
    title: "الملخص اليومي لتغييرات القبول الرسمية",
    intro: "تتم مراقبة صفحات القبول الرسمية المسجلة لمرحلة البكالوريوس فقط. لا يعني الجمع الأول أن الجامعة نشرته للتو؛ وتُعرض حالات فشل الجلب صراحةً ولا تُعد أبداً «بلا تغيير».",
    reload: "إعادة التحميل",
    coverage: {
      total: "المصادر الرسمية التي تمت مراجعتها",
      checked: "تم التحقق",
      failed: "فشل، بانتظار إعادة المحاولة",
      stale: "قديم / غير ناجح",
      overHours: "أكثر من {{hours}} ساعة",
    },
    lastScan: "آخر فحص: {{start}} · {{status}} · تم فحص {{checked}} / تغيّر {{changed}} / فشل {{failed}}",
    completedAt: " · اكتمل {{time}}",
    attention: {
      title: "مشكلات في حالة المصادر تحتاج إلى اهتمام",
      desc: "نتائج الجلب لهذه المصادر لا تكفي للاستنتاج بأنه «لا توجد تحديثات». يرجى فتح الصفحة الرسمية للتحقق؛ ويمكن للمسؤولين مراجعة الإدخال أو استبداله من إدارة المصادر.",
      fetchFailed: "فشل الجلب: {{error}}",
      noSuccess: "لا يوجد جلب ناجح بعد، أو لم يتم التحديث منذ أكثر من {{hours}} ساعة",
    },
    cohort: {
      title: "نطاق الجامعات وإدارة المصادر",
      body: "{{source}} {{edition}}: {{target}} جامعة مرشحة افتراضياً (20 لكل من الولايات المتحدة والصين وكندا). تم التحقق من {{verified}} مصدراً رسمياً لمرحلة البكالوريوس؛ و{{pending}} المتبقية بانتظار المراجعة اليدوية وغير مشمولة في الجلب أو الملخص اليومي.",
      verified: "تم التحقق من {{verified}}/{{target}}",
      pending: "{{pending}} بانتظار المراجعة اليدوية",
    },
    cohortDaily: {
      title: "التحديثات الرسمية اليوم لأفضل 20 جامعة في ثلاث دول",
      desc: "منذ {{start}}، تُحتسب المصادر الرسمية المراجَعة لمرحلة البكالوريوس فقط؛ تم إنشاء الملخص في {{generated}}. لا تُحتسب الجامعات قيد المراجعة على أنها «بلا تحديثات».",
      today: "تغييرات اليوم {{changed}} · مغطاة {{active}}/{{target}}",
      failedLine: "فشل الجلب {{failed}} · قديم/غير ناجح {{stale}} · بانتظار المراجعة {{pending}}",
      none: "لم تُسجَّل اليوم تغييرات في صفحات المصادر المراجَعة؛ ولا يُحتسب خط الأساس من الفحص الأول تغييراً.",
    },
    mail: {
      title: "الملخص اليومي بالبريد الإلكتروني",
      desc: "عند التفعيل، يُرسل ملخص يومي بعد اكتمال فحص المصادر الرسمية؛ ويتضمن فقط تغييرات الجامعات التي تتابعها.",
      lastDelivered: " آخر تسليم: {{date}}",
      notConfigured: "لم يقم أحد المسؤولين بإعداد خدمة البريد الإلكتروني بعد، لذا لا يمكن تفعيل التسليم حالياً.",
      lastFailed: "فشل آخر تسليم: {{error}}",
      contactAdmin: "يرجى التواصل مع أحد المسؤولين للتحقق من إعدادات البريد الإلكتروني.",
      setupLink: "الانتقال إلى إعدادات خدمة البريد الإلكتروني (للمسؤول)",
    },
    deliveries: {
      title: "سجل تسليم البريد الإلكتروني",
      desc: "محاولات تسليم الملخص اليومي الأخيرة. تتم إعادة محاولة الفاشلة منها بأمان في الفحوصات اللاحقة.",
      row: "{{date}} · {{status}} · {{count}} تغييرات",
      sentAt: "أُرسل {{time}}",
      waiting: "بانتظار اكتمال التسليم",
    },
    suggestions: {
      title: "المتابعة حسب دولك المستهدفة",
      body: "أهداف الملف الشخصي: {{countries}}. يمكن إضافة {{count}} مصادر رسمية تمت مراجعتها؛ ولا تُعامل نتائج استكشاف التخصصات على أنها تنبؤات بالقبول.",
      unsupported: "{{countries}} ليست ضمن نطاق المصادر الرسمية لأفضل 20 جامعة في الولايات المتحدة/الصين/كندا في هذه المرحلة، لذا لن تُعرض كمغطاة.",
      followAll: "متابعة كل المصادر التي تمت مراجعتها",
    },
    digest: {
      title: "متعلق بالجامعات التي تتابعها",
      window: "نافذة الملخص: من {{start}} · أُنشئ {{generated}}",
      countryLine: "{{country}}: متابَع {{watched}} · تغيّر اليوم {{changed}} · فشل {{failed}} · قديم/غير ناجح {{stale}}",
      emptyWatched: "لم تُعثر على تغييرات جديدة في صفحات الجامعات التي تتابعها منذ الساعة 00:00 بالتوقيت العالمي اليوم.",
      emptyNone: "اختر الجامعات أدناه ثم احفظ لإنشاء ملخص مخصص.",
    },
    suggestedAction: "مقترح: {{hint}}",
    newContent: "محتوى أُضيف إلى الصفحة",
    removedContent: "محتوى أُزيل أو استُبدل في الصفحة",
    viewOfficial: "عرض المصدر الرسمي والتحقق",
    changes: {
      title: "التغييرات الجوهرية المكتشفة في الصفحات",
      empty: "لا توجد تغييرات مسجلة بعد. الفحص الأول ينشئ خط أساس؛ ولا تنتج سجلات تغيير قابلة للمراجعة إلا من الفحوصات اللاحقة.",
    },
    watch: {
      title: "الجامعات المتابَعة وحالة التغطية",
      hint: "اختر ثم احفظ؛ يلخص الملخص تغييرات هذه الجامعات فقط.",
      save: "حفظ الجامعات المتابَعة",
      country: "الدولة",
      filterCountry: "تصفية الجامعات المتابَعة حسب الدولة",
      status: "حالة المصدر",
      filterHealth: "تصفية الجامعات المتابَعة حسب سلامة المصدر",
      all: "الكل",
      attention: "تحتاج إلى اهتمام",
      healthy: "سليم",
      showing: "عرض {{shown}}/{{total}} جامعة",
      officialPage: "صفحة القبول الرسمية لمرحلة البكالوريوس",
      lastSuccess: "آخر جلب ناجح: {{time}}",
      noSuccess: "لا يوجد جلب ناجح بعد.",
      staleOver: "عمر البيانات أكثر من {{hours}} ساعة.",
      noMatch: "لا توجد مصادر رسمية تطابق عوامل التصفية الحالية.",
    },
  },
  educationCenter: {
    title: "التخطيط الدراسي وأخبار القبول",
    desc: "احفظ ملفاً دراسياً، واستكشف التخصصات، وتابع القبول من مصادر رسمية. النتائج ليست تنبؤات بالقبول.",
    planning: "التخطيط الدراسي",
    admissions: "الملخص اليومي للقبول",
  },
  educationPlanning: {
    defaultCountry: "الصين",
    defaultCurriculum: "المنهج الوطني الصيني للمرحلة الثانوية العامة",
    subject: {
      chinese: "اللغة الصينية",
      math: "الرياضيات",
      foreign: "اللغة الأجنبية",
      physics: "الفيزياء",
      history: "التاريخ",
      chemistry: "الكيمياء",
      biology: "الأحياء",
      geography: "الجغرافيا",
      politics: "السياسة",
    },
    category: {
      strong: "استكشف أولاً",
      consider: "يستحق نظرة أقرب",
      not_recommended: "توجد تعارضات",
      ineligible: "متطلبات المواد غير مستوفاة",
    },
    title: "اختيار التخصص ومسارات الدراسة",
    intro: "سجّل أولاً مقرراتك وتفضيلاتك الفعلية، ثم استكشف مسارات التخصصات. سيجري التحقق من أهلية الجامعات وأخبار القبول وفق القواعد الرسمية في مراحل لاحقة.",
    readiness: {
      title: "الجاهزية لقرار الدراسة",
      progress: "اكتمل {{done}}/{{total}}. هذه قائمة إجراءات وليست احتمالية قبول.",
      viewAdmissions: "عرض الملخص اليومي الرسمي للقبول",
    },
    field: {
      name: "الاسم",
      namePlaceholder: "مثال: Danny",
      province: "مقاطعة القاوكاو",
      provincePlaceholder: "مثال: شانشي",
      year: "سنة الالتحاق المتوقعة",
      curriculum: "المنهج",
      countries: "الدول المستهدفة",
      countriesPlaceholder: "الصين، كندا، الولايات المتحدة",
      mode: "نمط القاوكاو الجديد",
      electives: "المواد الاختيارية",
      electivesPlaceholder: "مثال: 物理, 化学, 生物 (أسماء المواد بالصينية)",
      favorites: "المواد المفضلة",
      favoritesPlaceholder: "مثال: 数学, 物理 (أسماء المواد بالصينية)",
      dislikes: "المواد التي لا تحبها أو تريد تجنبها",
      dislikesPlaceholder: "مثال: 化学 (اسم المادة بالصينية)",
      interests: "الاهتمامات",
      interestsPlaceholder: "الذكاء الاصطناعي، الرعاية الصحية",
      budget: "الميزانية أو القيود (اختياري)",
      budgetPlaceholder: "مثال: إعطاء الأولوية للمنح الدراسية، الرغبة في البقاء في فانكوفر.",
    },
    saveProfile: "حفظ الملف الشخصي",
    exploreMajors: "استكشاف التخصصات الصينية حسب الدرجات",
    cmpt: {
      title: "استكشاف الاتجاه المهني (CMPT310)",
      desc: "نموذج تجريبي مستقل عن استكشاف تخصصات القاوكاو. لا يتنبأ بالقبول أو التوظيف؛ وتُكتب البيانات المرسلة فقط في ملف CSV مؤقت لعملية فرعية وتُحذف قبل الاستجابة.",
      unavailable: "الموصل غير متاح حالياً.",
      adminRequires: "يحتاج المسؤول إلى إعداد: {{items}}.",
      listSeparator: "،",
      run: "تشغيل الاستكشاف المهني التجريبي",
      confidence: "ثقة النموذج {{percent}}%",
    },
    results: {
      title: "نتائج استكشاف التخصصات",
      nextStep: "التالي: عرض الملخص اليومي الرسمي للقبول",
      noConflict: "لم يتم العثور على تعارضات واضحة في متطلبات المواد والمواد الاختيارية.",
      match: "مطابقة المواد {{percent}}%",
    },
  },
  privateModel: {
    loadFailed: "تعذر تحميل استوديو النماذج",
    actionFailed: "فشل الإجراء",
    uploaded: "تم الرفع، بانتظار المراجعة",
    viewFailed: "تعذر عرض المحتوى",
    logFailed: "تعذر قراءة السجل",
    queryFailed: "فشل الاستعلام من النموذج المحلي",
    title: "استوديو النماذج الخاصة",
    intro: "راجع مواد الشركة، ودرّب نموذجاً صغيراً مخصصاً، وافحص العينات المحجوزة، ثم انشره على Ollama المحلي.",
    status: "التدريب المحلي: {{ready}} · المستندات المعتمدة {{documents}} · عينات التدريب المعتمدة {{examples}}",
    ready: "جاهز",
    notReady: "غير جاهز",
    upload: {
      title: "الرفع والمراجعة",
      desc: "استخدم .txt أو .md أو .pdf للمستندات؛ واستخدم JSONL لعينات التدريب، مع instruction وoutput في كل سطر. يبدأ كل المحتوى بحالة بانتظار المراجعة.",
      aria: "رفع بيانات خاصة",
    },
    item: {
      document: "مستند",
      example: "عينة",
      viewFull: "عرض النص الكامل",
      approve: "اعتماد",
      reject: "رفض",
      approved: "معتمد",
      rejected: "مرفوض",
      empty: "لا توجد مواد بعد",
    },
    qa: {
      title: "أسئلة وأجوبة المعرفة المحلية",
      desc: "يبحث في المستندات المعتمدة فقط ويعرض المصادر المستخدمة. يعمل النموذج على Ollama المحلي في الخادم.",
      base06: "النموذج الأساسي Qwen3 0.6B",
      base17: "النموذج الأساسي Qwen3 1.7B",
      noModels: "لا توجد نماذج في Ollama المحلي بعد؛ ثبّت نموذجاً أساسياً أولاً، أو أكمل التدريب وانشر.",
      placeholder: "اطرح سؤالاً عن معرفة الشركة",
      ask: "اسأل",
      source: "المصدر: {{filename}} #{{id}}",
    },
    train: {
      title: "التدريب والتقييم والنشر",
      desc: "اعتمد 5 عينات على الأقل. يحجز التدريب بعض العينات لتقييم مستقل؛ والدرجات للاسترشاد فقط، لذا افحص كل إجابة قبل النشر.",
      workerNeeded: "فعّل worker على مضيف التدريب المحلي وثبّت LLaMA-Factory CLI وOllama.",
      start: "بدء التدريب",
      submitted: "تم إرسال مهمة التدريب",
    },
    job: {
      title: "المهمة #{{id}} · {{model}}",
      samples: "{{state}} · {{count}} عينات",
      viewLog: "عرض السجل المحلي",
      localModel: "النموذج المحلي:",
      evaluate: "تقييم النموذج المرشح",
      evaluating: "بدأ تقييم النموذج المرشح",
      holdout: "العينات المحجوزة {{count}} · F1 للرموز {{f1}} (ما زالت المراجعة اليدوية مطلوبة)",
      sample: "العينة #{{id}} · F1 {{f1}}",
      expected: "الإجابة المرجعية: {{text}}",
      actual: "إجابة النموذج: {{text}}",
      reviewed: "راجعت كل إجابة للنموذج على العينات المحجوزة",
      publish: "اعتماد ونشر",
      publishing: "جارٍ النشر إلى Ollama المحلي",
      empty: "لا توجد مهام تدريب بعد",
    },
    dialog: {
      aria: "النص الكامل للمادة قيد المراجعة",
      close: "إغلاق",
      question: "السؤال: {{text}}",
      answer: "الإجابة: {{text}}",
    },
  },
  adminAdmissions: {
    runStatus: {
      completed: "اكتمل",
      partial: "اكتمل جزئياً",
      failed: "فشل التشغيل",
      running: "قيد التشغيل",
    },
    queueNoWorker: "تم تحميل قائمة المراجعة، لكن حالة المهمة اليومية غير متاحة",
    queueNoHealth: "تم تحميل قائمة المراجعة، لكن سلامة المصادر غير متاحة",
    urlRequired: "أدخل رابط HTTPS لصفحة القبول الرسمية لمرحلة البكالوريوس",
    enabled: "تم تفعيل مراقبة القبول الرسمية لـ {{university}}",
    notSaved: "لم تُحفظ المراجعة",
    syncDone: "اكتملت المزامنة: تم فحص {{checked}}، تغيّر {{changed}}، فشل {{failed}}",
    syncFailed: "فشلت مزامنة المصادر الرسمية",
    title: "مراجعة مصادر القبول الرسمية",
    desc: "لا تُجلب يومياً إلا صفحات القبول الرسمية عبر HTTPS لمرحلة البكالوريوس التي اعتُمدت بمراجعة يدوية. يُسجَّل المراجع والوقت والملاحظة عند الحفظ.",
    syncNow: "مزامنة المصادر التي تمت مراجعتها الآن",
    showPending: "عرض المعلقة فقط",
    showVerified: "عرض ما تم التحقق منه ({{count}})",
    filterAria: "تصفية قائمة المراجعة حسب الدولة",
    allCountries: "كل الدول",
    counts: "بانتظار المراجعة: {{pending}}؛ المعروض: {{shown}}.",
    countsSources: " المصادر التي تمت مراجعتها: {{total}}، منها {{failed}} فاشلة و{{stale}} قديمة/غير ناجحة (الحد {{hours}} ساعة).",
    lastScan: "آخر فحص: {{start}} · {{status}} · تم فحص {{checked}} / تغيّر {{changed}} / فشل {{failed}}",
    completedAt: " · اكتمل {{time}}",
    workerOn: "مهمة المراقبة اليومية مفعّلة وتستعلم كل {{hours}} ساعة.",
    workerOff: "مهمة المراقبة اليومية غير مفعّلة؛ يمكن تحديث البيانات فقط عبر «مزامنة الآن».",
    rank: "{{country}} · الترتيب ضمن النطاق {{rank}}",
    verified: "تم التحقق",
    pending: "بانتظار المراجعة",
    fetchFailed: "فشل الجلب: {{error}}",
    staleHint: "المصدر قديم أو لا يوجد له جلب ناجح بعد؛ افتح الصفحة الرسمية وراجعها ثم زامن الآن.",
    urlPlaceholder: "https://… صفحة القبول الرسمية لمرحلة البكالوريوس",
    notePlaceholder: "ملاحظة المراجعة (يُنصح بذكر فئة المتقدمين وسنة الالتحاق التي تنطبق عليها)",
    openSource: "فتح المصدر المسجَّل",
    updateReview: "تحديث المصدر الذي تمت مراجعته",
    approve: "مراجعة وتفعيل المراقبة",
  },
  openOii: {
    requestFailed: "فشل طلب إعدادات OpenOii ({{status}})",
    invalidFormat: "أعاد OpenOii تنسيق إعدادات غير صالح",
    readFailed: "فشل التحميل",
    saved: "تم حفظ إعدادات OpenOii",
    restart: "أعد تشغيل خدمة OpenOii لتطبيق الإعدادات: {{keys}}",
    saveFailed: "فشل الحفظ",
    title: "إعدادات OpenOii",
    tokenLabel: "رمز مسؤول OpenOii",
    tokenPlaceholder: "يلزم رمز مسؤول لحفظ الإعدادات؛ يُستخدم لهذه الجلسة فقط",
    loading: "جارٍ تحميل الإعدادات…",
    retry: "إعادة المحاولة",
    maskedPlaceholder: "مُعدّ؛ اتركه فارغاً للاحتفاظ بالقيمة الحالية",
    save: "حفظ الإعدادات",
  },
  stockIntelligence: {
    saved: "تم حفظ إعدادات ذكاء الأسهم",
    title: "ذكاء الأسهم",
    intro: "أدر الإعدادات الافتراضية لـ AlphaSift وDaily Stock Analysis وVibe Research في مكان واحد.",
    alpha: {
      desc: "عيّن الاستراتيجية وعدد المرشحين وسياق البحث التي تُملأ مسبقاً عند كل فتح لمرشّح الأسهم.",
      strategy: "الاستراتيجية الافتراضية",
      strategyPlaceholder: "اتركه فارغاً لاستخدام أول استراتيجية متاحة",
      maxResults: "عدد المرشحين الافتراضي",
      context: "سياق البحث (اختياري)",
      contextPlaceholder: "مثال: التركيز على الشركات ذات التدفق النقدي الحر المرتفع والديون المنخفضة",
      useLlm: "تفعيل الترتيب بالذكاء الاصطناعي افتراضياً",
    },
    dsa: {
      desc: "أدخل اختيارياً رابط خدمة Daily Stock Analysis المنشورة؛ اتركه فارغاً لاستخدام الخدمة المدمجة في سطح المكتب.",
      url: "رابط الخدمة",
      urlPlaceholder: "مثال: http://127.0.0.1:8000",
    },
    vibe: {
      desc: "عيّن رابط مساحة عمل Vibe Research. يُستخدم افتراضياً ما تستضيفه Hanggent.",
      url: "رابط مساحة العمل",
      urlPlaceholder: "/vibe-research/ أو https://…",
    },
    restored: "تمت استعادة الإعدادات الافتراضية",
    restore: "استعادة الإعدادات الافتراضية",
    open: "فتح ذكاء الأسهم",
    save: "حفظ الإعدادات",
    alertsSaved: "تم حفظ إعدادات التنبيهات؛ وصفحة إعدادات المشروع متزامنة",
    alertsSaveFailed: "فشل حفظ إعدادات Stock Alerts",
    introWithAlerts: "أدر الإعدادات الافتراضية لـ Stock Alerts وAlphaSift وDaily Stock Analysis وVibe Research في مكان واحد.",
    alerts: {
      desc: "اضبط تنبيهات تحرك الأسعار وإشعارات البريد الإلكتروني لكل سوق.",
      loading: "جارٍ التحميل…",
      trigger: "حد التفعيل (%)",
      step: "الحد المتدرج (%)",
      enable: "تفعيل التنبيهات",
      email: "إشعارات البريد الإلكتروني",
      extended: "تشمل ما قبل السوق وما بعد الإغلاق",
      save: "حفظ إعدادات {{market}}",
    },
    market: {
      cn: "أسهم A",
      us: "الأسهم الأمريكية",
    },
  },
  subprojects: {
    err: {
      protocol: "يجب أن تستخدم روابط الخدمات HTTP أو HTTPS",
      httpsRequired: "تتطلب صفحات HTTPS روابط خدمات HTTPS",
      comfyui: "استخدم رابط الوكيل الخاص بهذا الموقع لـ ComfyUI",
      url: "يجب أن تكون روابط الخدمات HTTP أو HTTPS أو مساراً داخل هذا الموقع",
    },
  },
  settingsMisc: {
    saveAlphaDefaults: "حفظ كإعدادات افتراضية (تتزامن مع إعدادات Hanggent)",
    openProjectSettings: "فتح إعدادات المشروع",
    emailService: "خدمة البريد الإلكتروني",
  },
  settingsErrors: {
    stockAlertsRequest: "فشل طلب إعدادات Stock Alerts ({{status}})",
    stockAlertsFormat: "تنسيق إعدادات Stock Alerts غير صالح",
    stockAlertsMissing: "لم تعد إعدادات هذا السوق موجودة؛ يرجى إعادة التحميل",
  },
  zhituCareer: {
    heading: "البحث عن وظائف في الصين ومطابقة المسار المهني",
    description: "ارفع سيرة ذاتية وابحث عن وظائف في الصين وصفِّ أنسب المناصب باستخدام مطابقة المسار المهني الصينية من Zhitu Career وتقارير تقييم Career-Ops.",
    runLabel: "البحث عن وظائف في الصين",
    resultsLabel: "مصادر بيانات الوظائف الصينية المرخّصة",
  },
  adminMail: {
    loadFailed: "تعذر قراءة حالة خدمة البريد الإلكتروني",
    verified: "تم التحقق من صندوق QQ البريدي وحفظه",
    saveFailed: "فشل التحقق أو الحفظ",
    testSent: "تم إرسال رسالة اختبار إلى {{email}}",
    testFailed: "فشل إرسال رسالة الاختبار",
    title: "خدمة البريد الإلكتروني الموحدة",
    desc: "يشترك الملخص اليومي للقبول وتنبيهات الأسهم وتنبيهات المركبات/السوق في حساب المرسل QQ هذا. يُخزَّن رمز التفويض مشفراً ولا يُعرض مرة أخرى.",
    configured: "تم الإعداد: {{email}}",
    notConfigured: "لم يُعدّ بعد",
    qqEmail: "صندوق QQ البريدي",
    emailPlaceholder: "أدخل صندوق QQ البريدي",
    smtpCode: "رمز تفويض SMTP",
    hint: "يتم التحقق من تسجيل دخول QQ SMTP عند الحفظ؛ وتُرسل رسالة الاختبار إلى صندوق QQ المذكور أعلاه فقط.",
    verifySave: "تحقق واحفظ",
    sendTest: "إرسال رسالة اختبار",
  },
};

const appPage_de: typeof appPageEn = {
  admissionsDaily: {
    priority: {
      high: "Zuerst prüfen: kann eine Frist betreffen",
      attention: "Bitte prüfen: kann Bewerbungsanforderungen betreffen",
      normal: "Seiteninhalt aktualisiert",
    },
    action: {
      deadline: "Zuerst die Frist prüfen",
      requirements: "Unterlagen und Voraussetzungen prüfen",
      content: "Offizielle Änderung lesen",
    },
    deliveryStatus: {
      sent: "Gesendet",
      failed: "Senden fehlgeschlagen",
      processing: "Wird verarbeitet",
    },
    listSeparator: ",",
    partialError: "Einige Zulassungsdaten sind vorübergehend nicht verfügbar ({{count}} Elemente); der Rest kann weiterhin angezeigt werden. Bitte später aktualisieren.",
    title: "Tägliche Übersicht offizieller Zulassungsänderungen",
    intro: "Überwacht werden nur registrierte offizielle Zulassungsseiten für Bachelorstudiengänge. Eine Erstaufnahme bedeutet nicht, dass die Hochschule es gerade erst veröffentlicht hat; Abruffehler werden ausdrücklich angezeigt und nie als „keine Änderung“ gewertet.",
    reload: "Neu laden",
    coverage: {
      total: "Geprüfte offizielle Quellen",
      checked: "Verifiziert",
      failed: "Fehlgeschlagen, Wiederholung ausstehend",
      stale: "Veraltet / erfolglos",
      overHours: "Über {{hours}} Stunden",
    },
    lastScan: "Letzter Scan: {{start}} · {{status}} · geprüft {{checked}} / geändert {{changed}} / fehlgeschlagen {{failed}}",
    completedAt: " · abgeschlossen {{time}}",
    attention: {
      title: "Quellenprobleme, die Aufmerksamkeit erfordern",
      desc: "Die Abrufergebnisse dieser Quellen reichen nicht aus, um „keine Updates“ festzustellen. Bitte öffnen Sie die offizielle Seite zur Prüfung; Admins können den Eintrag in der Quellenverwaltung erneut prüfen oder ersetzen.",
      fetchFailed: "Abruf fehlgeschlagen: {{error}}",
      noSuccess: "Noch kein erfolgreicher Abruf oder seit über {{hours}} Stunden nicht aktualisiert",
    },
    cohort: {
      title: "Hochschulumfang und Quellenverwaltung",
      body: "{{source}} {{edition}}: {{target}} Standard-Kandidatenhochschulen (je 20 in den USA, China und Kanada). {{verified}} offizielle Bachelor-Quellen verifiziert; die übrigen {{pending}} warten auf manuelle Prüfung und sind weder im Abruf noch in der Tagesübersicht enthalten.",
      verified: "Verifiziert {{verified}}/{{target}}",
      pending: "{{pending}} warten auf manuelle Prüfung",
    },
    cohortDaily: {
      title: "Top 20 – heutige offizielle Updates in drei Ländern",
      desc: "Seit {{start}}, nur geprüfte offizielle Bachelor-Quellen; Zusammenfassung erstellt {{generated}}. Hochschulen in Prüfung zählen nicht als „keine Updates“.",
      today: "Änderungen heute {{changed}} · abgedeckt {{active}}/{{target}}",
      failedLine: "Abruf fehlgeschlagen {{failed}} · veraltet/erfolglos {{stale}} · Prüfung ausstehend {{pending}}",
      none: "Heute wurden für geprüfte Quellen keine Seitenänderungen erfasst; die Basis eines ersten Scans zählt nicht als Änderung.",
    },
    mail: {
      title: "Tägliche E-Mail-Übersicht",
      desc: "Wenn aktiviert, wird nach Abschluss des Scans der offiziellen Quellen eine Tagesübersicht gesendet; sie enthält nur Änderungen der Hochschulen, denen Sie folgen.",
      lastDelivered: " Zuletzt zugestellt: {{date}}",
      notConfigured: "Der E-Mail-Dienst wurde von einem Admin noch nicht konfiguriert, daher kann die Zustellung noch nicht aktiviert werden.",
      lastFailed: "Letzte Zustellung fehlgeschlagen: {{error}}",
      contactAdmin: "Bitte wenden Sie sich an einen Admin, um die E-Mail-Konfiguration zu prüfen.",
      setupLink: "Zu den E-Mail-Dienst-Einstellungen (Admin)",
    },
    deliveries: {
      title: "E-Mail-Zustellungsprotokoll",
      desc: "Letzte Zustellversuche der Tagesübersicht. Fehlgeschlagene werden in späteren Scans sicher wiederholt.",
      row: "{{date}} · {{status}} · {{count}} Änderungen",
      sentAt: "Gesendet {{time}}",
      waiting: "Warten auf Abschluss der Zustellung",
    },
    suggestions: {
      title: "Nach Ihren Zielländern folgen",
      body: "Profilziele: {{countries}}. {{count}} geprüfte offizielle Quellen können hinzugefügt werden; Ergebnisse der Studienfacherkundung werden dabei nicht als Zulassungsprognose gewertet.",
      unsupported: "{{countries}} liegt noch nicht im Umfang der offiziellen Top-20-Quellen für USA/China/Kanada in dieser Phase und wird daher nicht als abgedeckt angezeigt.",
      followAll: "Allen geprüften Quellen folgen",
    },
    digest: {
      title: "Relevant für Hochschulen, denen Sie folgen",
      window: "Zeitraum der Übersicht: ab {{start}} · erstellt {{generated}}",
      countryLine: "{{country}}: folgt {{watched}} · heute geändert {{changed}} · fehlgeschlagen {{failed}} · veraltet/erfolglos {{stale}}",
      emptyWatched: "Seit 00:00 UTC heute wurden keine neuen Seitenänderungen bei Hochschulen gefunden, denen Sie folgen.",
      emptyNone: "Wählen Sie unten Hochschulen aus und speichern Sie, um eine personalisierte Übersicht zu erzeugen.",
    },
    suggestedAction: "Empfohlen: {{hint}}",
    newContent: "Der Seite hinzugefügter Inhalt",
    removedContent: "Von der Seite entfernter oder ersetzter Inhalt",
    viewOfficial: "Offizielle Quelle ansehen und prüfen",
    changes: {
      title: "Erkannte inhaltliche Seitenänderungen",
      empty: "Noch keine Änderungen erfasst. Der erste Scan legt eine Basis an; erst spätere Scans erzeugen nachprüfbare Änderungsprotokolle.",
    },
    watch: {
      title: "Verfolgte Hochschulen und Abdeckungsstatus",
      hint: "Auswählen und speichern; die Übersicht fasst nur Änderungen dieser Hochschulen zusammen.",
      save: "Verfolgte Hochschulen speichern",
      country: "Land",
      filterCountry: "Verfolgte Hochschulen nach Land filtern",
      status: "Quellenstatus",
      filterHealth: "Verfolgte Hochschulen nach Quellenzustand filtern",
      all: "Alle",
      attention: "Erfordert Aufmerksamkeit",
      healthy: "In Ordnung",
      showing: "{{shown}}/{{total}} Hochschulen angezeigt",
      officialPage: "Offizielle Zulassungsseite für Bachelorstudiengänge",
      lastSuccess: "Letzter erfolgreicher Abruf: {{time}}",
      noSuccess: "Noch kein erfolgreicher Abruf.",
      staleOver: "Die Daten sind über {{hours}} Stunden alt.",
      noMatch: "Keine offiziellen Quellen entsprechen den aktuellen Filtern.",
    },
  },
  educationCenter: {
    title: "Studienplanung und Zulassungsneuigkeiten",
    desc: "Speichern Sie ein Lernprofil, erkunden Sie Studienfächer und verfolgen Sie Zulassungen anhand offizieller Quellen. Ergebnisse sind keine Zulassungsprognosen.",
    planning: "Studienplanung",
    admissions: "Zulassungs-Tagesübersicht",
  },
  educationPlanning: {
    defaultCountry: "China",
    defaultCurriculum: "Chinesischer allgemeiner Oberschullehrplan",
    subject: {
      chinese: "Chinesisch",
      math: "Mathematik",
      foreign: "Fremdsprache",
      physics: "Physik",
      history: "Geschichte",
      chemistry: "Chemie",
      biology: "Biologie",
      geography: "Geografie",
      politics: "Politik",
    },
    category: {
      strong: "Zuerst erkunden",
      consider: "Näher ansehen",
      not_recommended: "Konflikte gefunden",
      ineligible: "Fächeranforderungen nicht erfüllt",
    },
    title: "Studienfachwahl und Studienwege",
    intro: "Erfassen Sie zuerst Ihre tatsächlichen Kurse und Präferenzen und erkunden Sie dann Studienwege. Zulassungsvoraussetzungen und Neuigkeiten werden in späteren Phasen anhand offizieller Regeln geprüft.",
    readiness: {
      title: "Bereitschaft für die Studienentscheidung",
      progress: "{{done}}/{{total}} erledigt. Dies ist eine Aufgabenliste, keine Zulassungswahrscheinlichkeit.",
      viewAdmissions: "Offizielle Zulassungs-Tagesübersicht ansehen",
    },
    field: {
      name: "Name",
      namePlaceholder: "z. B. Danny",
      province: "Gaokao-Provinz",
      provincePlaceholder: "z. B. Shaanxi",
      year: "Voraussichtliches Einschreibungsjahr",
      curriculum: "Lehrplan",
      countries: "Zielländer",
      countriesPlaceholder: "China, Kanada, Vereinigte Staaten",
      mode: "Neuer Gaokao-Modus",
      electives: "Wahlfächer",
      electivesPlaceholder: "z. B. 物理, 化学, 生物 (chinesische Fächernamen)",
      favorites: "Lieblingsfächer",
      favoritesPlaceholder: "z. B. 数学, 物理 (chinesische Fächernamen)",
      dislikes: "Fächer, die Sie nicht mögen oder vermeiden möchten",
      dislikesPlaceholder: "z. B. 化学 (chinesischer Fächername)",
      interests: "Interessen",
      interestsPlaceholder: "KI, Gesundheitswesen",
      budget: "Budget oder Einschränkungen (optional)",
      budgetPlaceholder: "z. B. Stipendien bevorzugt, möchte in Vancouver bleiben.",
    },
    saveProfile: "Profil speichern",
    exploreMajors: "Chinesische Studienfächer nach Noten erkunden",
    cmpt: {
      title: "Erkundung der Berufsrichtung (CMPT310)",
      desc: "Ein experimentelles Modell, unabhängig von der Gaokao-Studienfacherkundung. Es sagt weder Zulassung noch Beschäftigung voraus; übermittelte Daten werden nur in eine temporäre CSV für einen Unterprozess geschrieben und vor der Antwort gelöscht.",
      unavailable: "Der Connector ist derzeit nicht verfügbar.",
      adminRequires: "Ein Admin muss konfigurieren: {{items}}.",
      listSeparator: ",",
      run: "Experimentelle Berufsrichtungserkundung starten",
      confidence: "Modellkonfidenz {{percent}}%",
    },
    results: {
      title: "Ergebnisse der Studienfacherkundung",
      nextStep: "Weiter: offizielle Zulassungs-Tagesübersicht ansehen",
      noConflict: "Keine offensichtlichen Konflikte bei Fächer- und Wahlfachanforderungen gefunden.",
      match: "Fächerübereinstimmung {{percent}}%",
    },
  },
  privateModel: {
    loadFailed: "Das Modellstudio kann nicht geladen werden",
    actionFailed: "Aktion fehlgeschlagen",
    uploaded: "Hochgeladen, wartet auf Prüfung",
    viewFailed: "Der Inhalt kann nicht angezeigt werden",
    logFailed: "Das Protokoll kann nicht gelesen werden",
    queryFailed: "Abfrage des lokalen Modells fehlgeschlagen",
    title: "Privates Modellstudio",
    intro: "Unternehmensunterlagen prüfen, ein eigenes kleines Modell trainieren, Hold-out-Beispiele kontrollieren und dann in das lokale Ollama veröffentlichen.",
    status: "Lokales Training: {{ready}} · genehmigte Dokumente {{documents}} · genehmigte Trainingsbeispiele {{examples}}",
    ready: "bereit",
    notReady: "nicht bereit",
    upload: {
      title: "Hochladen und prüfen",
      desc: "Verwenden Sie für Dokumente .txt, .md oder .pdf; für Trainingsbeispiele JSONL mit instruction und output in jeder Zeile. Alle Inhalte beginnen im Status „Prüfung ausstehend“.",
      aria: "Private Daten hochladen",
    },
    item: {
      document: "Dokument",
      example: "Beispiel",
      viewFull: "Volltext anzeigen",
      approve: "Genehmigen",
      reject: "Ablehnen",
      approved: "Genehmigt",
      rejected: "Abgelehnt",
      empty: "Noch keine Unterlagen",
    },
    qa: {
      title: "Lokale Wissens-Q&A",
      desc: "Durchsucht nur genehmigte Dokumente und zeigt die verwendeten Quellen an. Das Modell läuft auf dem lokalen Ollama des Servers.",
      base06: "Qwen3 0.6B Basismodell",
      base17: "Qwen3 1.7B Basismodell",
      noModels: "Das lokale Ollama hat noch keine Modelle; installieren Sie zuerst ein Basismodell oder schließen Sie das Training ab und veröffentlichen Sie es.",
      placeholder: "Frage zum Unternehmenswissen stellen",
      ask: "Fragen",
      source: "Quelle: {{filename}} #{{id}}",
    },
    train: {
      title: "Trainieren, bewerten und veröffentlichen",
      desc: "Genehmigen Sie mindestens 5 Beispiele. Beim Training werden einige Beispiele für eine unabhängige Bewertung zurückgehalten; die Werte dienen nur zur Orientierung, prüfen Sie daher vor der Veröffentlichung jede Antwort.",
      workerNeeded: "Aktivieren Sie den Worker auf dem lokalen Trainingshost und installieren Sie die LLaMA-Factory-CLI sowie Ollama.",
      start: "Training starten",
      submitted: "Trainingsauftrag eingereicht",
    },
    job: {
      title: "Auftrag #{{id}} · {{model}}",
      samples: "{{state}} · {{count}} Beispiele",
      viewLog: "Lokales Protokoll anzeigen",
      localModel: "Lokales Modell:",
      evaluate: "Kandidatenmodell bewerten",
      evaluating: "Bewertung des Kandidatenmodells gestartet",
      holdout: "Hold-out-Beispiele {{count}} · Token-F1 {{f1}} (manuelle Prüfung weiterhin erforderlich)",
      sample: "Beispiel #{{id}} · F1 {{f1}}",
      expected: "Referenzantwort: {{text}}",
      actual: "Modellantwort: {{text}}",
      reviewed: "Ich habe jede Modellantwort auf die Hold-out-Beispiele geprüft",
      publish: "Genehmigen und veröffentlichen",
      publishing: "Veröffentlichung im lokalen Ollama",
      empty: "Noch keine Trainingsaufträge",
    },
    dialog: {
      aria: "Volltext des zu prüfenden Materials",
      close: "Schließen",
      question: "Frage: {{text}}",
      answer: "Antwort: {{text}}",
    },
  },
  adminAdmissions: {
    runStatus: {
      completed: "Abgeschlossen",
      partial: "Teilweise abgeschlossen",
      failed: "Lauf fehlgeschlagen",
      running: "Läuft",
    },
    queueNoWorker: "Prüfwarteschlange geladen, aber der Status des Tagesjobs ist nicht verfügbar",
    queueNoHealth: "Prüfwarteschlange geladen, aber der Quellenzustand ist nicht verfügbar",
    urlRequired: "Geben Sie die HTTPS-URL der offiziellen Zulassungsseite für Bachelorstudiengänge ein",
    enabled: "Offizielle Zulassungsüberwachung für {{university}} aktiviert",
    notSaved: "Die Prüfung wurde nicht gespeichert",
    syncDone: "Synchronisierung abgeschlossen: geprüft {{checked}}, geändert {{changed}}, fehlgeschlagen {{failed}}",
    syncFailed: "Synchronisierung der offiziellen Quellen fehlgeschlagen",
    title: "Prüfung offizieller Zulassungsquellen",
    desc: "Nur per manueller Prüfung genehmigte offizielle HTTPS-Zulassungsseiten für Bachelorstudiengänge werden täglich abgerufen. Beim Speichern werden Prüfer, Zeitpunkt und Notiz erfasst.",
    syncNow: "Geprüfte Quellen jetzt synchronisieren",
    showPending: "Nur ausstehende anzeigen",
    showVerified: "Verifizierte anzeigen ({{count}})",
    filterAria: "Prüfwarteschlange nach Land filtern",
    allCountries: "Alle Länder",
    counts: "Prüfung ausstehend: {{pending}}; angezeigt: {{shown}}.",
    countsSources: " Geprüfte Quellen: {{total}}, davon {{failed}} fehlgeschlagen und {{stale}} veraltet/erfolglos (Schwellenwert {{hours}} Stunden).",
    lastScan: "Letzter Scan: {{start}} · {{status}} · geprüft {{checked}} / geändert {{changed}} / fehlgeschlagen {{failed}}",
    completedAt: " · abgeschlossen {{time}}",
    workerOn: "Der tägliche Überwachungsjob ist aktiviert und fragt alle {{hours}} Stunden ab.",
    workerOff: "Der tägliche Überwachungsjob ist nicht aktiviert; Daten können nur über „Jetzt synchronisieren“ aktualisiert werden.",
    rank: "{{country}} · Rang im Umfang {{rank}}",
    verified: "Verifiziert",
    pending: "Prüfung ausstehend",
    fetchFailed: "Abruf fehlgeschlagen: {{error}}",
    staleHint: "Die Quelle ist veraltet oder wurde noch nie erfolgreich abgerufen; öffnen Sie die offizielle Seite, prüfen Sie sie und synchronisieren Sie dann jetzt.",
    urlPlaceholder: "https://… offizielle Zulassungsseite für Bachelorstudiengänge",
    notePlaceholder: "Prüfnotiz (Bewerbergruppe und Einschreibungsjahr angeben, für die sie gilt)",
    openSource: "Registrierte Quelle öffnen",
    updateReview: "Geprüfte Quelle aktualisieren",
    approve: "Prüfen und Überwachung aktivieren",
  },
  openOii: {
    requestFailed: "OpenOii-Konfigurationsanfrage fehlgeschlagen ({{status}})",
    invalidFormat: "OpenOii hat ein ungültiges Konfigurationsformat zurückgegeben",
    readFailed: "Laden fehlgeschlagen",
    saved: "OpenOii-Konfiguration gespeichert",
    restart: "Starten Sie den OpenOii-Dienst neu, um die Konfiguration anzuwenden: {{keys}}",
    saveFailed: "Speichern fehlgeschlagen",
    title: "OpenOii-Konfiguration",
    tokenLabel: "OpenOii-Admin-Token",
    tokenPlaceholder: "Zum Speichern der Konfiguration ist ein Admin-Token erforderlich; es wird nur für diese Sitzung verwendet",
    loading: "Konfiguration wird geladen…",
    retry: "Erneut versuchen",
    maskedPlaceholder: "Konfiguriert; leer lassen, um den aktuellen Wert beizubehalten",
    save: "Konfiguration speichern",
  },
  stockIntelligence: {
    saved: "Einstellungen für Aktienintelligenz gespeichert",
    title: "Aktienintelligenz",
    intro: "Standardeinstellungen für AlphaSift, Daily Stock Analysis und Vibe Research an einem Ort verwalten.",
    alpha: {
      desc: "Legen Sie Strategie, Kandidatenzahl und Recherchekontext fest, die beim Öffnen des Aktienscreeners vorbelegt werden.",
      strategy: "Standardstrategie",
      strategyPlaceholder: "Leer lassen, um die erste verfügbare Strategie zu verwenden",
      maxResults: "Standardanzahl der Kandidaten",
      context: "Recherchekontext (optional)",
      contextPlaceholder: "z. B. Fokus auf Unternehmen mit hohem freiem Cashflow und geringer Verschuldung",
      useLlm: "KI-Ranking standardmäßig aktivieren",
    },
    dsa: {
      desc: "Optional die URL eines bereitgestellten Daily-Stock-Analysis-Dienstes eingeben; leer lassen, um den integrierten Desktop-Dienst zu verwenden.",
      url: "Dienst-URL",
      urlPlaceholder: "z. B. http://127.0.0.1:8000",
    },
    vibe: {
      desc: "Legen Sie die URL des Vibe-Research-Arbeitsbereichs fest. Standardmäßig wird der von Hanggent gehostete Arbeitsbereich verwendet.",
      url: "Arbeitsbereich-URL",
      urlPlaceholder: "/vibe-research/ oder https://…",
    },
    restored: "Standardwerte wiederhergestellt",
    restore: "Standardwerte wiederherstellen",
    open: "Aktienintelligenz öffnen",
    save: "Einstellungen speichern",
    alertsSaved: "Warnungseinstellungen gespeichert; die Projekteinstellungsseite ist synchronisiert",
    alertsSaveFailed: "Stock-Alerts-Einstellungen konnten nicht gespeichert werden",
    introWithAlerts: "Standardeinstellungen für Stock Alerts, AlphaSift, Daily Stock Analysis und Vibe Research an einem Ort verwalten.",
    alerts: {
      desc: "Kursbewegungswarnungen und E-Mail-Benachrichtigungen pro Markt festlegen.",
      loading: "Wird geladen…",
      trigger: "Auslöseschwelle (%)",
      step: "Stufenschwelle (%)",
      enable: "Warnungen aktivieren",
      email: "E-Mail-Benachrichtigungen",
      extended: "Vor- und nachbörslich einschließen",
      save: "{{market}}-Einstellungen speichern",
    },
    market: {
      cn: "A-Aktien",
      us: "US-Aktien",
    },
  },
  subprojects: {
    err: {
      protocol: "Dienst-URLs müssen HTTP oder HTTPS verwenden",
      httpsRequired: "HTTPS-Seiten erfordern HTTPS-Dienst-URLs",
      comfyui: "Verwenden Sie für ComfyUI die Proxy-URL dieser Website",
      url: "Dienst-URLs müssen HTTP, HTTPS oder ein Pfad auf dieser Website sein",
    },
  },
  settingsMisc: {
    saveAlphaDefaults: "Als Standard speichern (synchronisiert mit den Hanggent-Einstellungen)",
    openProjectSettings: "Projekteinstellungen öffnen",
    emailService: "E-Mail-Dienst",
  },
  settingsErrors: {
    stockAlertsRequest: "Anfrage der Stock-Alerts-Einstellungen fehlgeschlagen ({{status}})",
    stockAlertsFormat: "Ungültiges Format der Stock-Alerts-Einstellungen",
    stockAlertsMissing: "Die Einstellungen für diesen Markt existieren nicht mehr; bitte neu laden",
  },
  zhituCareer: {
    heading: "Jobsuche in China und Karriere-Matching",
    description: "Laden Sie einen Lebenslauf hoch, suchen Sie Stellen in China und grenzen Sie mit dem chinesischen Karriere-Matching von Zhitu Career und den Career-Ops-Bewertungsberichten die passendsten Positionen ein.",
    runLabel: "Stellen in China suchen",
    resultsLabel: "Lizenzierte Datenquellen für Stellen in China",
  },
  adminMail: {
    loadFailed: "Der Status des E-Mail-Dienstes kann nicht gelesen werden",
    verified: "QQ-Postfach verifiziert und gespeichert",
    saveFailed: "Verifizierung oder Speichern fehlgeschlagen",
    testSent: "Test-E-Mail an {{email}} gesendet",
    testFailed: "Test-E-Mail konnte nicht gesendet werden",
    title: "Einheitlicher E-Mail-Dienst",
    desc: "Zulassungs-Tagesübersichten, Aktienwarnungen und Fahrzeug-/Marktwarnungen nutzen dieses QQ-Absenderkonto gemeinsam. Der Autorisierungscode wird verschlüsselt gespeichert und nicht erneut angezeigt.",
    configured: "Konfiguriert: {{email}}",
    notConfigured: "Noch nicht konfiguriert",
    qqEmail: "QQ-Postfach",
    emailPlaceholder: "QQ-Postfach eingeben",
    smtpCode: "SMTP-Autorisierungscode",
    hint: "Beim Speichern wird die QQ-SMTP-Anmeldung geprüft; die Test-E-Mail geht nur an das oben genannte QQ-Postfach.",
    verifySave: "Verifizieren und speichern",
    sendTest: "Test-E-Mail senden",
  },
};

const appPage_es: typeof appPageEn = {
  admissionsDaily: {
    priority: {
      high: "Verificar primero: puede afectar a una fecha límite",
      attention: "Se recomienda verificar: puede afectar a los requisitos de solicitud",
      normal: "Contenido de la página actualizado",
    },
    action: {
      deadline: "Comprobar primero la fecha límite",
      requirements: "Comprobar documentos y elegibilidad",
      content: "Leer el cambio oficial",
    },
    deliveryStatus: {
      sent: "Enviado",
      failed: "Error de envío",
      processing: "Procesando",
    },
    listSeparator: ",",
    partialError: "Algunos datos de admisión no están disponibles temporalmente ({{count}} elementos); el resto aún puede consultarse. Actualice más tarde.",
    title: "Resumen diario de cambios oficiales de admisión",
    intro: "Solo se supervisan las páginas oficiales de admisión de grado registradas. Una primera captura no significa que la institución acabe de publicarlo; los fallos de obtención se muestran de forma explícita y nunca se consideran «sin cambios».",
    reload: "Recargar",
    coverage: {
      total: "Fuentes oficiales revisadas",
      checked: "Verificado",
      failed: "Fallido, pendiente de reintento",
      stale: "Obsoleto / sin éxito",
      overHours: "Más de {{hours}} horas",
    },
    lastScan: "Último análisis: {{start}} · {{status}} · comprobados {{checked}} / cambiados {{changed}} / fallidos {{failed}}",
    completedAt: " · completado {{time}}",
    attention: {
      title: "Problemas de estado de fuentes que requieren atención",
      desc: "Los resultados de obtención de estas fuentes no bastan para concluir «sin novedades». Abra la página oficial para verificar; los administradores pueden revisar o sustituir la entrada en la gestión de fuentes.",
      fetchFailed: "Error de obtención: {{error}}",
      noSuccess: "Aún sin obtención correcta, o sin actualizar desde hace más de {{hours}} horas",
    },
    cohort: {
      title: "Alcance de instituciones y gestión de fuentes",
      body: "{{source}} {{edition}}: {{target}} instituciones candidatas por defecto (20 en EE. UU., China y Canadá cada uno). {{verified}} fuentes oficiales de grado verificadas; las {{pending}} restantes esperan revisión manual y no se incluyen en la obtención ni en el resumen diario.",
      verified: "Verificadas {{verified}}/{{target}}",
      pending: "{{pending}} pendientes de revisión manual",
    },
    cohortDaily: {
      title: "Actualizaciones oficiales de hoy del Top 20 de tres países",
      desc: "Desde {{start}}, solo se cuentan las fuentes oficiales de grado revisadas; resumen generado {{generated}}. Las instituciones pendientes de revisión no se cuentan como «sin novedades».",
      today: "Cambios hoy {{changed}} · cubiertas {{active}}/{{target}}",
      failedLine: "Obtención fallida {{failed}} · obsoletas/sin éxito {{stale}} · revisión pendiente {{pending}}",
      none: "Hoy no se han registrado cambios de página en las fuentes revisadas; la línea base de un primer análisis no cuenta como cambio.",
    },
    mail: {
      title: "Resumen diario por correo",
      desc: "Si se activa, se envía un resumen diario al terminar el análisis de las fuentes oficiales; solo incluye cambios de las instituciones que sigue.",
      lastDelivered: " Última entrega: {{date}}",
      notConfigured: "Un administrador aún no ha configurado el servicio de correo, por lo que todavía no se puede activar la entrega.",
      lastFailed: "Falló la última entrega: {{error}}",
      contactAdmin: "Póngase en contacto con un administrador para revisar la configuración del correo.",
      setupLink: "Ir a los ajustes del servicio de correo (administrador)",
    },
    deliveries: {
      title: "Registro de entregas de correo",
      desc: "Intentos recientes de entrega del resumen diario. Los fallidos se reintentan de forma segura en análisis posteriores.",
      row: "{{date}} · {{status}} · {{count}} cambios",
      sentAt: "Enviado {{time}}",
      waiting: "Esperando a que termine la entrega",
    },
    suggestions: {
      title: "Seguir según sus países de destino",
      body: "Destinos del perfil: {{countries}}. Se pueden añadir {{count}} fuentes oficiales revisadas; esto no trata los resultados de exploración de carreras como predicciones de admisión.",
      unsupported: "{{countries}} aún no está en el alcance de fuentes oficiales Top 20 de EE. UU./China/Canadá de esta fase, por lo que no se mostrará como cubierto.",
      followAll: "Seguir todas las fuentes revisadas",
    },
    digest: {
      title: "Relacionado con las instituciones que sigue",
      window: "Ventana del resumen: desde {{start}} · generado {{generated}}",
      countryLine: "{{country}}: siguiendo {{watched}} · cambiadas hoy {{changed}} · fallidas {{failed}} · obsoletas/sin éxito {{stale}}",
      emptyWatched: "Desde las 00:00 UTC de hoy no se han encontrado nuevos cambios de página en las instituciones que sigue.",
      emptyNone: "Seleccione instituciones abajo y guarde para generar un resumen personalizado.",
    },
    suggestedAction: "Sugerencia: {{hint}}",
    newContent: "Contenido añadido a la página",
    removedContent: "Contenido eliminado o sustituido en la página",
    viewOfficial: "Ver la fuente oficial y verificar",
    changes: {
      title: "Cambios sustanciales detectados en las páginas",
      empty: "Aún no hay cambios registrados. El primer análisis establece una línea base; solo los análisis posteriores generan registros de cambios revisables.",
    },
    watch: {
      title: "Instituciones seguidas y estado de cobertura",
      hint: "Seleccione y guarde; el resumen solo reúne los cambios de estas instituciones.",
      save: "Guardar instituciones seguidas",
      country: "País",
      filterCountry: "Filtrar instituciones seguidas por país",
      status: "Estado de la fuente",
      filterHealth: "Filtrar instituciones seguidas por salud de la fuente",
      all: "Todas",
      attention: "Requiere atención",
      healthy: "Correcta",
      showing: "Mostrando {{shown}}/{{total}} instituciones",
      officialPage: "Página oficial de admisión de grado",
      lastSuccess: "Última obtención correcta: {{time}}",
      noSuccess: "Aún no hay obtenciones correctas.",
      staleOver: "Los datos tienen más de {{hours}} horas.",
      noMatch: "Ninguna fuente oficial coincide con los filtros actuales.",
    },
  },
  educationCenter: {
    title: "Planificación de estudios y novedades de admisión",
    desc: "Guarde un perfil de estudios, explore carreras y siga las admisiones a partir de fuentes oficiales. Los resultados no son predicciones de admisión.",
    planning: "Planificación de estudios",
    admissions: "Resumen diario de admisión",
  },
  educationPlanning: {
    defaultCountry: "China",
    defaultCurriculum: "Currículo nacional chino de bachillerato general",
    subject: {
      chinese: "Chino",
      math: "Matemáticas",
      foreign: "Lengua extranjera",
      physics: "Física",
      history: "Historia",
      chemistry: "Química",
      biology: "Biología",
      geography: "Geografía",
      politics: "Política",
    },
    category: {
      strong: "Explorar primero",
      consider: "Vale la pena revisar",
      not_recommended: "Conflictos detectados",
      ineligible: "No cumple los requisitos de asignaturas",
    },
    title: "Elección de carrera y rutas de estudio",
    intro: "Registre primero sus cursos reales y preferencias y luego explore rutas de carrera. La elegibilidad de las instituciones y las novedades de admisión se verificarán con normas oficiales en fases posteriores.",
    readiness: {
      title: "Preparación para la decisión de estudios",
      progress: "{{done}}/{{total}} completados. Es una lista de acciones, no una probabilidad de admisión.",
      viewAdmissions: "Ver el resumen diario oficial de admisión",
    },
    field: {
      name: "Nombre",
      namePlaceholder: "p. ej. Danny",
      province: "Provincia del gaokao",
      provincePlaceholder: "p. ej. Shaanxi",
      year: "Año de ingreso previsto",
      curriculum: "Currículo",
      countries: "Países de destino",
      countriesPlaceholder: "China, Canadá, Estados Unidos",
      mode: "Nuevo modo del gaokao",
      electives: "Asignaturas optativas",
      electivesPlaceholder: "p. ej. 物理, 化学, 生物 (nombres de asignaturas en chino)",
      favorites: "Asignaturas favoritas",
      favoritesPlaceholder: "p. ej. 数学, 物理 (nombres de asignaturas en chino)",
      dislikes: "Asignaturas que no le gustan o quiere evitar",
      dislikesPlaceholder: "p. ej. 化学 (nombre de asignatura en chino)",
      interests: "Intereses",
      interestsPlaceholder: "IA, salud",
      budget: "Presupuesto o restricciones (opcional)",
      budgetPlaceholder: "p. ej. priorizar becas, preferir quedarse en Vancouver.",
    },
    saveProfile: "Guardar perfil",
    exploreMajors: "Explorar carreras chinas según las notas",
    cmpt: {
      title: "Exploración de orientación profesional (CMPT310)",
      desc: "Un modelo experimental independiente de la exploración de carreras del gaokao. No predice admisión ni empleo; los datos enviados solo se escriben en un CSV temporal para un subproceso y se eliminan antes de responder.",
      unavailable: "El conector no está disponible actualmente.",
      adminRequires: "Un administrador debe configurar: {{items}}.",
      listSeparator: ",",
      run: "Ejecutar exploración experimental de orientación profesional",
      confidence: "Confianza del modelo {{percent}} %",
    },
    results: {
      title: "Resultados de la exploración de carreras",
      nextStep: "Siguiente: ver el resumen diario oficial de admisión",
      noConflict: "No se encontraron conflictos evidentes en los requisitos de asignaturas y optativas.",
      match: "Coincidencia de asignaturas {{percent}} %",
    },
  },
  privateModel: {
    loadFailed: "No se puede cargar el estudio de modelos",
    actionFailed: "La acción falló",
    uploaded: "Subido, pendiente de revisión",
    viewFailed: "No se puede ver el contenido",
    logFailed: "No se puede leer el registro",
    queryFailed: "Falló la consulta al modelo local",
    title: "Estudio de modelos privados",
    intro: "Revise materiales de la empresa, entrene un modelo pequeño dedicado, compruebe las muestras reservadas y luego publíquelo en Ollama local.",
    status: "Entrenamiento local: {{ready}} · documentos aprobados {{documents}} · muestras de entrenamiento aprobadas {{examples}}",
    ready: "listo",
    notReady: "no listo",
    upload: {
      title: "Subir y revisar",
      desc: "Use .txt, .md o .pdf para documentos; use JSONL para muestras de entrenamiento, con instruction y output en cada línea. Todo el contenido comienza en estado pendiente de revisión.",
      aria: "Subir datos privados",
    },
    item: {
      document: "Documento",
      example: "Muestra",
      viewFull: "Ver texto completo",
      approve: "Aprobar",
      reject: "Rechazar",
      approved: "Aprobado",
      rejected: "Rechazado",
      empty: "Aún no hay materiales",
    },
    qa: {
      title: "P&R de conocimiento local",
      desc: "Busca solo en documentos aprobados y muestra las fuentes utilizadas. El modelo se ejecuta en el Ollama local del servidor.",
      base06: "Modelo base Qwen3 0.6B",
      base17: "Modelo base Qwen3 1.7B",
      noModels: "El Ollama local aún no tiene modelos; instale primero un modelo base, o termine el entrenamiento y publique.",
      placeholder: "Haga una pregunta sobre el conocimiento de la empresa",
      ask: "Preguntar",
      source: "Fuente: {{filename}} #{{id}}",
    },
    train: {
      title: "Entrenar, evaluar y publicar",
      desc: "Apruebe al menos 5 muestras. El entrenamiento reserva algunas muestras para una evaluación independiente; las puntuaciones son solo orientativas, así que revise cada respuesta antes de publicar.",
      workerNeeded: "Active el worker en el host de entrenamiento local e instale la CLI de LLaMA-Factory y Ollama.",
      start: "Iniciar entrenamiento",
      submitted: "Tarea de entrenamiento enviada",
    },
    job: {
      title: "Tarea #{{id}} · {{model}}",
      samples: "{{state}} · {{count}} muestras",
      viewLog: "Ver registro local",
      localModel: "Modelo local:",
      evaluate: "Evaluar modelo candidato",
      evaluating: "Evaluación del modelo candidato iniciada",
      holdout: "Muestras reservadas {{count}} · F1 por tokens {{f1}} (sigue siendo necesaria la revisión manual)",
      sample: "Muestra #{{id}} · F1 {{f1}}",
      expected: "Respuesta de referencia: {{text}}",
      actual: "Respuesta del modelo: {{text}}",
      reviewed: "He revisado cada respuesta del modelo en las muestras reservadas",
      publish: "Aprobar y publicar",
      publishing: "Publicando en Ollama local",
      empty: "Aún no hay tareas de entrenamiento",
    },
    dialog: {
      aria: "Texto completo del material en revisión",
      close: "Cerrar",
      question: "Pregunta: {{text}}",
      answer: "Respuesta: {{text}}",
    },
  },
  adminAdmissions: {
    runStatus: {
      completed: "Completado",
      partial: "Parcialmente completado",
      failed: "Ejecución fallida",
      running: "En ejecución",
    },
    queueNoWorker: "Cola de revisión cargada, pero el estado de la tarea diaria no está disponible",
    queueNoHealth: "Cola de revisión cargada, pero el estado de salud de las fuentes no está disponible",
    urlRequired: "Introduzca la URL HTTPS de la página oficial de admisión de grado",
    enabled: "Supervisión oficial de admisión activada para {{university}}",
    notSaved: "La revisión no se guardó",
    syncDone: "Sincronización completada: comprobados {{checked}}, cambiados {{changed}}, fallidos {{failed}}",
    syncFailed: "Falló la sincronización de fuentes oficiales",
    title: "Revisión de fuentes oficiales de admisión",
    desc: "Solo las páginas oficiales HTTPS de admisión de grado aprobadas por revisión manual se obtienen a diario. Al guardar se registran el revisor, la hora y la nota.",
    syncNow: "Sincronizar ahora las fuentes revisadas",
    showPending: "Mostrar solo pendientes",
    showVerified: "Mostrar verificadas ({{count}})",
    filterAria: "Filtrar la cola de revisión por país",
    allCountries: "Todos los países",
    counts: "Pendientes de revisión: {{pending}}; mostrando {{shown}}.",
    countsSources: " Fuentes revisadas: {{total}}, de las cuales {{failed}} fallidas y {{stale}} obsoletas/sin éxito (umbral {{hours}} horas).",
    lastScan: "Último análisis: {{start}} · {{status}} · comprobados {{checked}} / cambiados {{changed}} / fallidos {{failed}}",
    completedAt: " · completado {{time}}",
    workerOn: "La tarea de supervisión diaria está activada y consulta cada {{hours}} horas.",
    workerOff: "La tarea de supervisión diaria no está activada; los datos solo se pueden actualizar con «Sincronizar ahora».",
    rank: "{{country}} · puesto en el alcance {{rank}}",
    verified: "Verificada",
    pending: "Pendiente de revisión",
    fetchFailed: "Error de obtención: {{error}}",
    staleHint: "La fuente está obsoleta o aún no tiene una obtención correcta; abra la página oficial, revísela y sincronice ahora.",
    urlPlaceholder: "https://… página oficial de admisión de grado",
    notePlaceholder: "Nota de revisión (indique la categoría de solicitante y el año de ingreso a los que se aplica)",
    openSource: "Abrir fuente registrada",
    updateReview: "Actualizar fuente revisada",
    approve: "Revisar y activar supervisión",
  },
  openOii: {
    requestFailed: "Falló la solicitud de configuración de OpenOii ({{status}})",
    invalidFormat: "OpenOii devolvió un formato de configuración no válido",
    readFailed: "Error al cargar",
    saved: "Configuración de OpenOii guardada",
    restart: "Reinicie el servicio OpenOii para aplicar la configuración: {{keys}}",
    saveFailed: "Error al guardar",
    title: "Configuración de OpenOii",
    tokenLabel: "Token de administrador de OpenOii",
    tokenPlaceholder: "Se requiere un token de administrador para guardar la configuración; solo se usa en esta sesión",
    loading: "Cargando configuración…",
    retry: "Reintentar",
    maskedPlaceholder: "Configurado; déjelo vacío para conservar el valor actual",
    save: "Guardar configuración",
  },
  stockIntelligence: {
    saved: "Ajustes de inteligencia bursátil guardados",
    title: "Inteligencia bursátil",
    intro: "Gestione en un solo lugar los ajustes predeterminados de AlphaSift, Daily Stock Analysis y Vibe Research.",
    alpha: {
      desc: "Establezca la estrategia, el número de candidatos y el contexto de investigación que se rellenan cada vez que se abre el filtro de acciones.",
      strategy: "Estrategia predeterminada",
      strategyPlaceholder: "Déjelo vacío para usar la primera estrategia disponible",
      maxResults: "Número de candidatos predeterminado",
      context: "Contexto de investigación (opcional)",
      contextPlaceholder: "p. ej. centrarse en empresas con alto flujo de caja libre y baja deuda",
      useLlm: "Activar por defecto el ranking con IA",
    },
    dsa: {
      desc: "Opcionalmente introduzca la URL de un servicio Daily Stock Analysis desplegado; déjelo vacío para usar el servicio integrado de escritorio.",
      url: "URL del servicio",
      urlPlaceholder: "p. ej. http://127.0.0.1:8000",
    },
    vibe: {
      desc: "Establezca la URL del espacio de trabajo de Vibe Research. Por defecto se usa el espacio alojado por Hanggent.",
      url: "URL del espacio de trabajo",
      urlPlaceholder: "/vibe-research/ o https://…",
    },
    restored: "Valores predeterminados restaurados",
    restore: "Restaurar valores predeterminados",
    open: "Abrir inteligencia bursátil",
    save: "Guardar ajustes",
    alertsSaved: "Ajustes de alertas guardados; la página de ajustes del proyecto está sincronizada",
    alertsSaveFailed: "Error al guardar los ajustes de Stock Alerts",
    introWithAlerts: "Gestione en un solo lugar los ajustes predeterminados de Stock Alerts, AlphaSift, Daily Stock Analysis y Vibe Research.",
    alerts: {
      desc: "Configure por mercado las alertas de variación de precio y las notificaciones por correo.",
      loading: "Cargando…",
      trigger: "Umbral de activación (%)",
      step: "Umbral progresivo (%)",
      enable: "Activar alertas",
      email: "Notificaciones por correo",
      extended: "Incluir premercado y poscierre",
      save: "Guardar ajustes de {{market}}",
    },
    market: {
      cn: "Acciones A",
      us: "Acciones de EE. UU.",
    },
  },
  subprojects: {
    err: {
      protocol: "Las URL de servicio deben usar HTTP o HTTPS",
      httpsRequired: "Las páginas HTTPS requieren URL de servicio HTTPS",
      comfyui: "Use la URL de proxy de este sitio para ComfyUI",
      url: "Las URL de servicio deben ser HTTP, HTTPS o una ruta de este sitio",
    },
  },
  settingsMisc: {
    saveAlphaDefaults: "Guardar como predeterminado (se sincroniza con los ajustes de Hanggent)",
    openProjectSettings: "Abrir ajustes del proyecto",
    emailService: "Servicio de correo",
  },
  settingsErrors: {
    stockAlertsRequest: "Falló la solicitud de ajustes de Stock Alerts ({{status}})",
    stockAlertsFormat: "Formato de ajustes de Stock Alerts no válido",
    stockAlertsMissing: "Los ajustes de este mercado ya no existen; recargue",
  },
  zhituCareer: {
    heading: "Búsqueda de empleo en China y emparejamiento profesional",
    description: "Suba un currículum, busque empleos en China y preseleccione los mejores puestos con el emparejamiento profesional en chino de Zhitu Career y los informes de evaluación de Career-Ops.",
    runLabel: "Buscar empleos en China",
    resultsLabel: "Fuentes de datos de empleo de China con licencia",
  },
  adminMail: {
    loadFailed: "No se puede leer el estado del servicio de correo",
    verified: "Buzón QQ verificado y guardado",
    saveFailed: "Falló la verificación o el guardado",
    testSent: "Correo de prueba enviado a {{email}}",
    testFailed: "Falló el envío del correo de prueba",
    title: "Servicio de correo unificado",
    desc: "Los resúmenes de admisión, las alertas bursátiles y las alertas de vehículos/mercado comparten esta cuenta remitente de QQ. El código de autorización se guarda cifrado y no se vuelve a mostrar.",
    configured: "Configurado: {{email}}",
    notConfigured: "Aún sin configurar",
    qqEmail: "Buzón QQ",
    emailPlaceholder: "Introduzca su buzón QQ",
    smtpCode: "Código de autorización SMTP",
    hint: "Al guardar se verifica el inicio de sesión SMTP de QQ; el correo de prueba solo se envía al buzón QQ indicado arriba.",
    verifySave: "Verificar y guardar",
    sendTest: "Enviar correo de prueba",
  },
};

const appPage_fr: typeof appPageEn = {
  admissionsDaily: {
    priority: {
      high: "À vérifier en priorité : peut concerner une date limite",
      attention: "Vérification conseillée : peut concerner les conditions de candidature",
      normal: "Contenu de la page mis à jour",
    },
    action: {
      deadline: "Vérifier d'abord la date limite",
      requirements: "Vérifier les pièces et l'éligibilité",
      content: "Lire la modification officielle",
    },
    deliveryStatus: {
      sent: "Envoyé",
      failed: "Échec de l'envoi",
      processing: "En cours de traitement",
    },
    listSeparator: ",",
    partialError: "Certaines données d'admission sont temporairement indisponibles ({{count}} éléments) ; le reste reste consultable. Veuillez actualiser plus tard.",
    title: "Bulletin quotidien des modifications officielles d'admission",
    intro: "Seules les pages officielles d'admission en premier cycle enregistrées sont surveillées. Une première collecte ne signifie pas que l'établissement vient de publier ; les échecs de récupération sont affichés clairement et ne sont jamais considérés comme « aucun changement ».",
    reload: "Recharger",
    coverage: {
      total: "Sources officielles examinées",
      checked: "Vérifié",
      failed: "Échec, nouvelle tentative en attente",
      stale: "Obsolète / sans succès",
      overHours: "Plus de {{hours}} heures",
    },
    lastScan: "Dernière analyse : {{start}} · {{status}} · vérifiés {{checked}} / modifiés {{changed}} / échecs {{failed}}",
    completedAt: " · terminée {{time}}",
    attention: {
      title: "Problèmes d'état des sources nécessitant une action",
      desc: "Les résultats de récupération de ces sources ne suffisent pas à conclure à « aucune mise à jour ». Ouvrez la page officielle pour vérifier ; les administrateurs peuvent réexaminer ou remplacer l'entrée dans la gouvernance des sources.",
      fetchFailed: "Échec de la récupération : {{error}}",
      noSuccess: "Aucune récupération réussie, ou aucune mise à jour depuis plus de {{hours}} heures",
    },
    cohort: {
      title: "Périmètre des établissements et gouvernance des sources",
      body: "{{source}} {{edition}} : {{target}} établissements candidats par défaut (20 chacun aux États-Unis, en Chine et au Canada). {{verified}} sources officielles de premier cycle vérifiées ; les {{pending}} restantes attendent une revue manuelle et ne sont incluses ni dans la collecte ni dans le bulletin quotidien.",
      verified: "Vérifiées {{verified}}/{{target}}",
      pending: "{{pending}} en attente de revue manuelle",
    },
    cohortDaily: {
      title: "Top 20 : mises à jour officielles du jour dans trois pays",
      desc: "Depuis {{start}}, seules les sources officielles de premier cycle examinées sont comptées ; résumé généré {{generated}}. Les établissements en attente de revue ne sont pas comptés comme « aucune mise à jour ».",
      today: "Changements aujourd'hui {{changed}} · couverts {{active}}/{{target}}",
      failedLine: "Échecs de récupération {{failed}} · obsolètes/sans succès {{stale}} · revue en attente {{pending}}",
      none: "Aucun changement de page enregistré aujourd'hui pour les sources examinées ; la référence d'une première analyse n'est pas comptée comme un changement.",
    },
    mail: {
      title: "Résumé quotidien par e-mail",
      desc: "Une fois activé, un résumé quotidien est envoyé à la fin de l'analyse des sources officielles ; il ne contient que les changements des établissements que vous suivez.",
      lastDelivered: " Dernier envoi : {{date}}",
      notConfigured: "Le service e-mail n'a pas encore été configuré par un administrateur ; l'envoi ne peut pas être activé pour le moment.",
      lastFailed: "Échec du dernier envoi : {{error}}",
      contactAdmin: "Veuillez contacter un administrateur pour vérifier la configuration e-mail.",
      setupLink: "Aller aux paramètres du service e-mail (admin)",
    },
    deliveries: {
      title: "Journal d'envoi des e-mails",
      desc: "Dernières tentatives d'envoi du résumé quotidien. Les échecs sont réessayés en toute sécurité lors des analyses suivantes.",
      row: "{{date}} · {{status}} · {{count}} changements",
      sentAt: "Envoyé {{time}}",
      waiting: "En attente de la fin de l'envoi",
    },
    suggestions: {
      title: "Suivre selon vos pays cibles",
      body: "Cibles du profil : {{countries}}. {{count}} sources officielles examinées peuvent être ajoutées ; les résultats d'exploration des filières ne sont pas traités comme des prédictions d'admission.",
      unsupported: "{{countries}} ne fait pas encore partie du périmètre des sources officielles Top 20 États-Unis/Chine/Canada de cette phase ; il ne sera donc pas affiché comme couvert.",
      followAll: "Suivre toutes les sources examinées",
    },
    digest: {
      title: "Lié aux établissements que vous suivez",
      window: "Période du bulletin : depuis {{start}} · généré {{generated}}",
      countryLine: "{{country}} : suivis {{watched}} · modifiés aujourd'hui {{changed}} · échecs {{failed}} · obsolètes/sans succès {{stale}}",
      emptyWatched: "Aucun nouveau changement de page trouvé pour les établissements que vous suivez depuis 00:00 UTC aujourd'hui.",
      emptyNone: "Sélectionnez des établissements ci-dessous et enregistrez pour générer un bulletin personnalisé.",
    },
    suggestedAction: "Suggestion : {{hint}}",
    newContent: "Contenu ajouté à la page",
    removedContent: "Contenu supprimé ou remplacé sur la page",
    viewOfficial: "Voir la source officielle et vérifier",
    changes: {
      title: "Changements de fond détectés sur les pages",
      empty: "Aucun changement enregistré pour l'instant. La première analyse établit une référence ; seules les analyses suivantes produisent des enregistrements de changement vérifiables.",
    },
    watch: {
      title: "Établissements suivis et état de couverture",
      hint: "Sélectionnez et enregistrez ; le bulletin ne résume que les changements de ces établissements.",
      save: "Enregistrer les établissements suivis",
      country: "Pays",
      filterCountry: "Filtrer les établissements suivis par pays",
      status: "État de la source",
      filterHealth: "Filtrer les établissements suivis par santé de la source",
      all: "Tous",
      attention: "À traiter",
      healthy: "Sain",
      showing: "Affichage de {{shown}}/{{total}} établissements",
      officialPage: "Page officielle d'admission en premier cycle",
      lastSuccess: "Dernière récupération réussie : {{time}}",
      noSuccess: "Aucune récupération réussie pour l'instant.",
      staleOver: "Les données datent de plus de {{hours}} heures.",
      noMatch: "Aucune source officielle ne correspond aux filtres actuels.",
    },
  },
  educationCenter: {
    title: "Planification des études et actualités d'admission",
    desc: "Enregistrez un profil d'études, explorez des filières et suivez les admissions à partir de sources officielles. Les résultats ne sont pas des prédictions d'admission.",
    planning: "Planification des études",
    admissions: "Bulletin quotidien d'admission",
  },
  educationPlanning: {
    defaultCountry: "Chine",
    defaultCurriculum: "Programme national chinois du lycée général",
    subject: {
      chinese: "Chinois",
      math: "Mathématiques",
      foreign: "Langue étrangère",
      physics: "Physique",
      history: "Histoire",
      chemistry: "Chimie",
      biology: "Biologie",
      geography: "Géographie",
      politics: "Politique",
    },
    category: {
      strong: "À explorer en premier",
      consider: "À examiner de plus près",
      not_recommended: "Conflits détectés",
      ineligible: "Matières exigées non satisfaites",
    },
    title: "Choix de filière et parcours d'études",
    intro: "Enregistrez d'abord vos cours réels et vos préférences, puis explorez les parcours. L'éligibilité des établissements et les actualités d'admission seront vérifiées selon les règles officielles dans les phases suivantes.",
    readiness: {
      title: "Préparation à la décision d'études",
      progress: "{{done}}/{{total}} terminés. Il s'agit d'une liste d'actions, pas d'une probabilité d'admission.",
      viewAdmissions: "Voir le bulletin quotidien officiel d'admission",
    },
    field: {
      name: "Nom",
      namePlaceholder: "p. ex. Danny",
      province: "Province du gaokao",
      provincePlaceholder: "p. ex. Shaanxi",
      year: "Année d'entrée prévue",
      curriculum: "Programme scolaire",
      countries: "Pays cibles",
      countriesPlaceholder: "Chine, Canada, États-Unis",
      mode: "Nouveau mode du gaokao",
      electives: "Matières optionnelles",
      electivesPlaceholder: "p. ex. 物理, 化学, 生物 (noms de matières en chinois)",
      favorites: "Matières préférées",
      favoritesPlaceholder: "p. ex. 数学, 物理 (noms de matières en chinois)",
      dislikes: "Matières que vous n'aimez pas ou voulez éviter",
      dislikesPlaceholder: "p. ex. 化学 (nom de matière en chinois)",
      interests: "Centres d'intérêt",
      interestsPlaceholder: "IA, santé",
      budget: "Budget ou contraintes (facultatif)",
      budgetPlaceholder: "p. ex. privilégier les bourses, rester à Vancouver.",
    },
    saveProfile: "Enregistrer le profil",
    exploreMajors: "Explorer les filières chinoises selon les notes",
    cmpt: {
      title: "Exploration d'orientation professionnelle (CMPT310)",
      desc: "Un modèle expérimental indépendant de l'exploration de filières du gaokao. Il ne prédit ni l'admission ni l'emploi ; les données soumises sont écrites uniquement dans un CSV temporaire pour un sous-processus et supprimées avant la réponse.",
      unavailable: "Le connecteur est actuellement indisponible.",
      adminRequires: "Un administrateur doit configurer : {{items}}.",
      listSeparator: ",",
      run: "Lancer l'exploration expérimentale d'orientation professionnelle",
      confidence: "Confiance du modèle {{percent}} %",
    },
    results: {
      title: "Résultats de l'exploration des filières",
      nextStep: "Étape suivante : voir le bulletin quotidien officiel d'admission",
      noConflict: "Aucun conflit évident trouvé dans les exigences de matières et de spécialités.",
      match: "Correspondance des matières {{percent}} %",
    },
  },
  privateModel: {
    loadFailed: "Impossible de charger le studio de modèles",
    actionFailed: "Échec de l'action",
    uploaded: "Téléversé, en attente de revue",
    viewFailed: "Impossible d'afficher le contenu",
    logFailed: "Impossible de lire le journal",
    queryFailed: "Échec de la requête au modèle local",
    title: "Studio de modèles privés",
    intro: "Examinez les documents de l'entreprise, entraînez un petit modèle dédié, vérifiez les échantillons réservés, puis publiez-le sur Ollama en local.",
    status: "Entraînement local : {{ready}} · documents approuvés {{documents}} · échantillons d'entraînement approuvés {{examples}}",
    ready: "prêt",
    notReady: "non prêt",
    upload: {
      title: "Téléverser et examiner",
      desc: "Utilisez .txt, .md ou .pdf pour les documents ; JSONL pour les échantillons d'entraînement, avec instruction et output sur chaque ligne. Tout le contenu commence à l'état « en attente de revue ».",
      aria: "Téléverser des données privées",
    },
    item: {
      document: "Document",
      example: "Échantillon",
      viewFull: "Voir le texte intégral",
      approve: "Approuver",
      reject: "Rejeter",
      approved: "Approuvé",
      rejected: "Rejeté",
      empty: "Aucun document pour l'instant",
    },
    qa: {
      title: "Q&R sur les connaissances locales",
      desc: "Recherche uniquement dans les documents approuvés et affiche les sources utilisées. Le modèle s'exécute sur l'Ollama local du serveur.",
      base06: "Modèle de base Qwen3 0.6B",
      base17: "Modèle de base Qwen3 1.7B",
      noModels: "L'Ollama local n'a pas encore de modèle ; installez d'abord un modèle de base, ou terminez l'entraînement et publiez.",
      placeholder: "Posez une question sur les connaissances de l'entreprise",
      ask: "Demander",
      source: "Source : {{filename}} #{{id}}",
    },
    train: {
      title: "Entraîner, évaluer et publier",
      desc: "Approuvez au moins 5 échantillons. L'entraînement met de côté une partie des échantillons pour une évaluation indépendante ; les scores sont indicatifs, vérifiez donc chaque réponse avant de publier.",
      workerNeeded: "Activez le worker sur l'hôte d'entraînement local et installez la CLI LLaMA-Factory et Ollama.",
      start: "Démarrer l'entraînement",
      submitted: "Tâche d'entraînement soumise",
    },
    job: {
      title: "Tâche #{{id}} · {{model}}",
      samples: "{{state}} · {{count}} échantillons",
      viewLog: "Voir le journal local",
      localModel: "Modèle local :",
      evaluate: "Évaluer le modèle candidat",
      evaluating: "Évaluation du modèle candidat démarrée",
      holdout: "Échantillons réservés {{count}} · F1 par jetons {{f1}} (revue manuelle toujours requise)",
      sample: "Échantillon #{{id}} · F1 {{f1}}",
      expected: "Réponse de référence : {{text}}",
      actual: "Réponse du modèle : {{text}}",
      reviewed: "J'ai examiné chaque réponse du modèle sur les échantillons réservés",
      publish: "Approuver et publier",
      publishing: "Publication sur Ollama en local",
      empty: "Aucune tâche d'entraînement pour l'instant",
    },
    dialog: {
      aria: "Texte intégral du document en cours de revue",
      close: "Fermer",
      question: "Question : {{text}}",
      answer: "Réponse : {{text}}",
    },
  },
  adminAdmissions: {
    runStatus: {
      completed: "Terminé",
      partial: "Partiellement terminé",
      failed: "Échec de l'exécution",
      running: "En cours",
    },
    queueNoWorker: "File de revue chargée, mais l'état de la tâche quotidienne est indisponible",
    queueNoHealth: "File de revue chargée, mais l'état de santé des sources est indisponible",
    urlRequired: "Saisissez l'URL HTTPS de la page officielle d'admission en premier cycle",
    enabled: "Surveillance officielle des admissions activée pour {{university}}",
    notSaved: "La revue n'a pas été enregistrée",
    syncDone: "Synchronisation terminée : vérifiés {{checked}}, modifiés {{changed}}, échecs {{failed}}",
    syncFailed: "Échec de la synchronisation des sources officielles",
    title: "Revue des sources officielles d'admission",
    desc: "Seules les pages officielles HTTPS d'admission en premier cycle approuvées par revue manuelle sont récupérées chaque jour. Le relecteur, l'heure et la note sont enregistrés à la sauvegarde.",
    syncNow: "Synchroniser maintenant les sources examinées",
    showPending: "Afficher uniquement les éléments en attente",
    showVerified: "Afficher les vérifiés ({{count}})",
    filterAria: "Filtrer la file de revue par pays",
    allCountries: "Tous les pays",
    counts: "En attente de revue : {{pending}} ; affichés : {{shown}}.",
    countsSources: " Sources examinées : {{total}}, dont {{failed}} en échec et {{stale}} obsolètes/sans succès (seuil {{hours}} heures).",
    lastScan: "Dernière analyse : {{start}} · {{status}} · vérifiés {{checked}} / modifiés {{changed}} / échecs {{failed}}",
    completedAt: " · terminée {{time}}",
    workerOn: "La tâche de surveillance quotidienne est activée, avec une interrogation toutes les {{hours}} heures.",
    workerOff: "La tâche de surveillance quotidienne n'est pas activée ; les données ne peuvent être mises à jour qu'avec « Synchroniser maintenant ».",
    rank: "{{country}} · rang dans le périmètre {{rank}}",
    verified: "Vérifié",
    pending: "En attente de revue",
    fetchFailed: "Échec de la récupération : {{error}}",
    staleHint: "La source est obsolète ou n'a encore jamais été récupérée avec succès ; ouvrez la page officielle, examinez-la, puis synchronisez maintenant.",
    urlPlaceholder: "https://… page officielle d'admission en premier cycle",
    notePlaceholder: "Note de revue (indiquez la catégorie de candidats et l'année d'entrée concernées)",
    openSource: "Ouvrir la source enregistrée",
    updateReview: "Mettre à jour la source examinée",
    approve: "Examiner et activer la surveillance",
  },
  openOii: {
    requestFailed: "Échec de la requête de configuration OpenOii ({{status}})",
    invalidFormat: "OpenOii a renvoyé un format de configuration non valide",
    readFailed: "Échec du chargement",
    saved: "Configuration OpenOii enregistrée",
    restart: "Redémarrez le service OpenOii pour appliquer la configuration : {{keys}}",
    saveFailed: "Échec de l'enregistrement",
    title: "Configuration OpenOii",
    tokenLabel: "Jeton administrateur OpenOii",
    tokenPlaceholder: "Un jeton administrateur est requis pour enregistrer la configuration ; utilisé uniquement pour cette session",
    loading: "Chargement de la configuration…",
    retry: "Réessayer",
    maskedPlaceholder: "Configuré ; laissez vide pour conserver la valeur actuelle",
    save: "Enregistrer la configuration",
  },
  stockIntelligence: {
    saved: "Paramètres d'intelligence boursière enregistrés",
    title: "Intelligence boursière",
    intro: "Gérez en un seul endroit les paramètres par défaut d'AlphaSift, Daily Stock Analysis et Vibe Research.",
    alpha: {
      desc: "Définissez la stratégie, le nombre de candidats et le contexte de recherche préremplis à chaque ouverture du filtre d'actions.",
      strategy: "Stratégie par défaut",
      strategyPlaceholder: "Laisser vide pour utiliser la première stratégie disponible",
      maxResults: "Nombre de candidats par défaut",
      context: "Contexte de recherche (facultatif)",
      contextPlaceholder: "p. ex. se concentrer sur les entreprises à fort flux de trésorerie disponible et faible endettement",
      useLlm: "Activer le classement par IA par défaut",
    },
    dsa: {
      desc: "Saisissez éventuellement l'URL d'un service Daily Stock Analysis déployé ; laissez vide pour utiliser le service intégré du bureau.",
      url: "URL du service",
      urlPlaceholder: "p. ex. http://127.0.0.1:8000",
    },
    vibe: {
      desc: "Définissez l'URL de l'espace de travail Vibe Research. Par défaut, l'espace hébergé par Hanggent est utilisé.",
      url: "URL de l'espace de travail",
      urlPlaceholder: "/vibe-research/ ou https://…",
    },
    restored: "Paramètres par défaut rétablis",
    restore: "Rétablir les valeurs par défaut",
    open: "Ouvrir l'intelligence boursière",
    save: "Enregistrer les paramètres",
    alertsSaved: "Paramètres d'alerte enregistrés ; la page des paramètres du projet est synchronisée",
    alertsSaveFailed: "Échec de l'enregistrement des paramètres Stock Alerts",
    introWithAlerts: "Gérez en un seul endroit les paramètres par défaut de Stock Alerts, AlphaSift, Daily Stock Analysis et Vibe Research.",
    alerts: {
      desc: "Définissez par marché les alertes de variation de prix et les notifications par e-mail.",
      loading: "Chargement…",
      trigger: "Seuil de déclenchement (%)",
      step: "Seuil progressif (%)",
      enable: "Activer les alertes",
      email: "Notifications par e-mail",
      extended: "Inclure pré-marché et après-bourse",
      save: "Enregistrer les paramètres {{market}}",
    },
    market: {
      cn: "Actions A",
      us: "Actions américaines",
    },
  },
  subprojects: {
    err: {
      protocol: "Les URL de service doivent utiliser HTTP ou HTTPS",
      httpsRequired: "Les pages HTTPS nécessitent des URL de service HTTPS",
      comfyui: "Utilisez l'URL de proxy de ce site pour ComfyUI",
      url: "Les URL de service doivent être HTTP, HTTPS ou un chemin de ce site",
    },
  },
  settingsMisc: {
    saveAlphaDefaults: "Enregistrer par défaut (synchronisé avec les paramètres Hanggent)",
    openProjectSettings: "Ouvrir les paramètres du projet",
    emailService: "Service e-mail",
  },
  settingsErrors: {
    stockAlertsRequest: "Échec de la requête des paramètres Stock Alerts ({{status}})",
    stockAlertsFormat: "Format des paramètres Stock Alerts non valide",
    stockAlertsMissing: "Les paramètres de ce marché n'existent plus ; veuillez recharger",
  },
  zhituCareer: {
    heading: "Recherche d'emploi en Chine et correspondance de carrière",
    description: "Téléversez un CV, recherchez des emplois en Chine et présélectionnez les meilleurs postes grâce à la correspondance de carrière en chinois de Zhitu Career et aux rapports d'évaluation Career-Ops.",
    runLabel: "Rechercher des emplois en Chine",
    resultsLabel: "Sources de données d'emplois en Chine sous licence",
  },
  adminMail: {
    loadFailed: "Impossible de lire l'état du service e-mail",
    verified: "Boîte QQ vérifiée et enregistrée",
    saveFailed: "Échec de la vérification ou de l'enregistrement",
    testSent: "E-mail de test envoyé à {{email}}",
    testFailed: "Échec de l'envoi de l'e-mail de test",
    title: "Service e-mail unifié",
    desc: "Les bulletins d'admission, les alertes boursières et les alertes véhicules/marché partagent ce compte expéditeur QQ. Le code d'autorisation est stocké chiffré et n'est plus jamais affiché.",
    configured: "Configuré : {{email}}",
    notConfigured: "Pas encore configuré",
    qqEmail: "Boîte QQ",
    emailPlaceholder: "Saisissez votre boîte QQ",
    smtpCode: "Code d'autorisation SMTP",
    hint: "La connexion SMTP QQ est vérifiée à l'enregistrement ; l'e-mail de test n'est envoyé qu'à la boîte QQ ci-dessus.",
    verifySave: "Vérifier et enregistrer",
    sendTest: "Envoyer un e-mail de test",
  },
};

const appPage_hi: typeof appPageEn = {
  admissionsDaily: {
    priority: {
      high: "पहले जाँचें: यह अंतिम तिथि से संबंधित हो सकता है",
      attention: "जाँचने की सलाह: यह आवेदन आवश्यकताओं से संबंधित हो सकता है",
      normal: "पेज की सामग्री अपडेट हुई",
    },
    action: {
      deadline: "पहले अंतिम तिथि जाँचें",
      requirements: "दस्तावेज़ और पात्रता जाँचें",
      content: "आधिकारिक बदलाव पढ़ें",
    },
    deliveryStatus: {
      sent: "भेजा गया",
      failed: "भेजना विफल",
      processing: "प्रोसेस हो रहा है",
    },
    listSeparator: ",",
    partialError: "कुछ प्रवेश डेटा अस्थायी रूप से उपलब्ध नहीं है ({{count}} आइटम); बाकी सामग्री देखी जा सकती है। कृपया बाद में रीफ़्रेश करें।",
    title: "आधिकारिक प्रवेश परिवर्तन दैनिक सारांश",
    intro: "केवल पंजीकृत आधिकारिक स्नातक प्रवेश पेजों की निगरानी की जाती है। पहली बार दर्ज होने का मतलब यह नहीं कि संस्थान ने अभी प्रकाशित किया है; फ़ेच विफलताएँ स्पष्ट रूप से दिखाई जाती हैं और उन्हें कभी «कोई बदलाव नहीं» नहीं माना जाता।",
    reload: "फिर से लोड करें",
    coverage: {
      total: "समीक्षित आधिकारिक स्रोत",
      checked: "सत्यापित",
      failed: "विफल, पुनः प्रयास की प्रतीक्षा में",
      stale: "पुराना / असफल",
      overHours: "{{hours}} घंटे से अधिक",
    },
    lastScan: "अंतिम स्कैन: {{start}} · {{status}} · जाँचे गए {{checked}} / बदले {{changed}} / विफल {{failed}}",
    completedAt: " · पूर्ण {{time}}",
    attention: {
      title: "ध्यान देने योग्य स्रोत स्वास्थ्य समस्याएँ",
      desc: "इन स्रोतों के फ़ेच परिणाम «कोई अपडेट नहीं» निष्कर्ष निकालने के लिए पर्याप्त नहीं हैं। कृपया सत्यापन के लिए आधिकारिक पेज खोलें; एडमिन स्रोत प्रबंधन में प्रविष्टि की पुनः समीक्षा या बदलाव कर सकते हैं।",
      fetchFailed: "फ़ेच विफल: {{error}}",
      noSuccess: "अभी तक कोई सफल फ़ेच नहीं, या {{hours}} घंटे से अधिक समय से अपडेट नहीं हुआ",
    },
    cohort: {
      title: "संस्थानों का दायरा और स्रोत प्रबंधन",
      body: "{{source}} {{edition}}: {{target}} डिफ़ॉल्ट उम्मीदवार संस्थान (अमेरिका, चीन और कनाडा से 20-20)। {{verified}} आधिकारिक स्नातक स्रोत सत्यापित; शेष {{pending}} मैन्युअल समीक्षा की प्रतीक्षा में हैं और फ़ेच या दैनिक सारांश में शामिल नहीं हैं।",
      verified: "सत्यापित {{verified}}/{{target}}",
      pending: "{{pending}} मैन्युअल समीक्षा की प्रतीक्षा में",
    },
    cohortDaily: {
      title: "तीन देशों के शीर्ष 20 के आज के आधिकारिक अपडेट",
      desc: "{{start}} से, केवल समीक्षित आधिकारिक स्नातक स्रोत गिने गए; सारांश {{generated}} को बना। समीक्षा की प्रतीक्षा वाले संस्थान «कोई अपडेट नहीं» के रूप में नहीं गिने जाते।",
      today: "आज के बदलाव {{changed}} · कवर {{active}}/{{target}}",
      failedLine: "फ़ेच विफल {{failed}} · पुराने/असफल {{stale}} · समीक्षा लंबित {{pending}}",
      none: "आज समीक्षित स्रोतों के कोई पेज बदलाव दर्ज नहीं हुए; पहले स्कैन का आधार-रेखा बदलाव के रूप में नहीं गिना जाता।",
    },
    mail: {
      title: "दैनिक ईमेल सारांश",
      desc: "चालू करने पर, आधिकारिक स्रोतों का स्कैन पूरा होने के बाद दैनिक सारांश भेजा जाता है; इसमें केवल आपके द्वारा फ़ॉलो किए गए संस्थानों के बदलाव होते हैं।",
      lastDelivered: " अंतिम डिलीवरी: {{date}}",
      notConfigured: "ईमेल सेवा अभी किसी एडमिन द्वारा कॉन्फ़िगर नहीं की गई है, इसलिए डिलीवरी अभी चालू नहीं की जा सकती।",
      lastFailed: "अंतिम डिलीवरी विफल: {{error}}",
      contactAdmin: "ईमेल कॉन्फ़िगरेशन जाँचने के लिए कृपया किसी एडमिन से संपर्क करें।",
      setupLink: "ईमेल सेवा सेटिंग्स पर जाएँ (एडमिन)",
    },
    deliveries: {
      title: "ईमेल डिलीवरी लॉग",
      desc: "दैनिक सारांश डिलीवरी के हाल के प्रयास। विफल प्रयास बाद के स्कैन में सुरक्षित रूप से दोहराए जाते हैं।",
      row: "{{date}} · {{status}} · {{count}} बदलाव",
      sentAt: "भेजा गया {{time}}",
      waiting: "डिलीवरी पूरी होने की प्रतीक्षा",
    },
    suggestions: {
      title: "अपने लक्षित देशों के अनुसार फ़ॉलो करें",
      body: "प्रोफ़ाइल लक्ष्य: {{countries}}। {{count}} समीक्षित आधिकारिक स्रोत जोड़े जा सकते हैं; इससे मेजर अन्वेषण के परिणामों को प्रवेश की भविष्यवाणी नहीं माना जाता।",
      unsupported: "{{countries}} अभी इस चरण के अमेरिका/चीन/कनाडा शीर्ष 20 आधिकारिक स्रोत दायरे में नहीं है, इसलिए इसे कवर के रूप में नहीं दिखाया जाएगा।",
      followAll: "सभी समीक्षित स्रोत फ़ॉलो करें",
    },
    digest: {
      title: "आपके फ़ॉलो किए संस्थानों से संबंधित",
      window: "सारांश अवधि: {{start}} से · बनाया गया {{generated}}",
      countryLine: "{{country}}: फ़ॉलो {{watched}} · आज बदले {{changed}} · विफल {{failed}} · पुराने/असफल {{stale}}",
      emptyWatched: "आज 00:00 UTC से आपके फ़ॉलो किए संस्थानों में कोई नया पेज बदलाव नहीं मिला।",
      emptyNone: "नीचे संस्थान चुनें और सहेजें, ताकि व्यक्तिगत सारांश बन सके।",
    },
    suggestedAction: "सुझाव: {{hint}}",
    newContent: "पेज पर जोड़ी गई सामग्री",
    removedContent: "पेज से हटाई या बदली गई सामग्री",
    viewOfficial: "आधिकारिक स्रोत देखें और सत्यापित करें",
    changes: {
      title: "पता लगाए गए महत्वपूर्ण पेज बदलाव",
      empty: "अभी तक कोई बदलाव दर्ज नहीं हुआ। पहला स्कैन आधार-रेखा बनाता है; केवल बाद के स्कैन से जाँचे जा सकने वाले बदलाव रिकॉर्ड बनते हैं।",
    },
    watch: {
      title: "फ़ॉलो किए गए संस्थान और कवरेज स्थिति",
      hint: "चुनें और सहेजें; सारांश केवल इन संस्थानों के बदलाव का होगा।",
      save: "फ़ॉलो किए गए संस्थान सहेजें",
      country: "देश",
      filterCountry: "देश के अनुसार फ़ॉलो किए संस्थान फ़िल्टर करें",
      status: "स्रोत स्थिति",
      filterHealth: "स्रोत स्वास्थ्य के अनुसार फ़ॉलो किए संस्थान फ़िल्टर करें",
      all: "सभी",
      attention: "ध्यान आवश्यक",
      healthy: "स्वस्थ",
      showing: "{{shown}}/{{total}} संस्थान दिखाए जा रहे हैं",
      officialPage: "आधिकारिक स्नातक प्रवेश पेज",
      lastSuccess: "अंतिम सफल फ़ेच: {{time}}",
      noSuccess: "अभी तक कोई सफल फ़ेच नहीं।",
      staleOver: "डेटा {{hours}} घंटे से अधिक पुराना है।",
      noMatch: "वर्तमान फ़िल्टर से कोई आधिकारिक स्रोत मेल नहीं खाता।",
    },
  },
  educationCenter: {
    title: "उच्च शिक्षा योजना और प्रवेश समाचार",
    desc: "अध्ययन प्रोफ़ाइल सहेजें, मेजर खोजें और आधिकारिक स्रोतों से प्रवेश की जानकारी ट्रैक करें। परिणाम प्रवेश की भविष्यवाणी नहीं हैं।",
    planning: "उच्च शिक्षा योजना",
    admissions: "प्रवेश दैनिक सारांश",
  },
  educationPlanning: {
    defaultCountry: "चीन",
    defaultCurriculum: "चीनी राष्ट्रीय उच्च माध्यमिक पाठ्यक्रम",
    subject: {
      chinese: "चीनी",
      math: "गणित",
      foreign: "विदेशी भाषा",
      physics: "भौतिकी",
      history: "इतिहास",
      chemistry: "रसायन विज्ञान",
      biology: "जीव विज्ञान",
      geography: "भूगोल",
      politics: "राजनीति",
    },
    category: {
      strong: "पहले खोजें",
      consider: "और जानने योग्य",
      not_recommended: "टकराव मिले",
      ineligible: "विषय आवश्यकताएँ पूरी नहीं",
    },
    title: "मेजर चयन और अध्ययन मार्ग",
    intro: "पहले अपने वास्तविक पाठ्यक्रम और पसंद दर्ज करें, फिर मेजर के मार्ग खोजें। संस्थानों की पात्रता और प्रवेश समाचार अगले चरणों में आधिकारिक नियमों से सत्यापित किए जाएँगे।",
    readiness: {
      title: "अध्ययन निर्णय की तैयारी",
      progress: "{{done}}/{{total}} पूर्ण। यह कार्य-सूची है, प्रवेश की संभावना नहीं।",
      viewAdmissions: "आधिकारिक प्रवेश दैनिक सारांश देखें",
    },
    field: {
      name: "नाम",
      namePlaceholder: "उदा. Danny",
      province: "गाओकाओ प्रांत",
      provincePlaceholder: "उदा. शानशी",
      year: "अपेक्षित प्रवेश वर्ष",
      curriculum: "पाठ्यक्रम",
      countries: "लक्षित देश",
      countriesPlaceholder: "चीन, कनाडा, संयुक्त राज्य",
      mode: "नया गाओकाओ मोड",
      electives: "वैकल्पिक विषय",
      electivesPlaceholder: "उदा. 物理, 化学, 生物 (चीनी विषय नाम)",
      favorites: "पसंदीदा विषय",
      favoritesPlaceholder: "उदा. 数学, 物理 (चीनी विषय नाम)",
      dislikes: "नापसंद या जिनसे बचना चाहते हैं वे विषय",
      dislikesPlaceholder: "उदा. 化学 (चीनी विषय नाम)",
      interests: "रुचियाँ",
      interestsPlaceholder: "एआई, स्वास्थ्य सेवा",
      budget: "बजट या सीमाएँ (वैकल्पिक)",
      budgetPlaceholder: "उदा. छात्रवृत्ति को प्राथमिकता, वैंकूवर में रहना पसंद।",
    },
    saveProfile: "प्रोफ़ाइल सहेजें",
    exploreMajors: "अंकों के आधार पर चीनी मेजर खोजें",
    cmpt: {
      title: "करियर दिशा अन्वेषण (CMPT310)",
      desc: "गाओकाओ मेजर अन्वेषण से स्वतंत्र एक प्रायोगिक मॉडल। यह प्रवेश या रोज़गार की भविष्यवाणी नहीं करता; सबमिट किया गया डेटा केवल उप-प्रक्रिया की गणना के लिए अस्थायी CSV में लिखा जाता है और प्रतिक्रिया से पहले हटा दिया जाता है।",
      unavailable: "कनेक्टर अभी उपलब्ध नहीं है।",
      adminRequires: "एडमिन को कॉन्फ़िगर करना होगा: {{items}}।",
      listSeparator: ",",
      run: "प्रायोगिक करियर दिशा अन्वेषण चलाएँ",
      confidence: "मॉडल विश्वास {{percent}}%",
    },
    results: {
      title: "मेजर अन्वेषण परिणाम",
      nextStep: "अगला: आधिकारिक प्रवेश दैनिक सारांश देखें",
      noConflict: "विषय और वैकल्पिक विषय आवश्यकताओं में कोई स्पष्ट टकराव नहीं मिला।",
      match: "विषय मिलान {{percent}}%",
    },
  },
  privateModel: {
    loadFailed: "मॉडल स्टूडियो लोड नहीं हो सका",
    actionFailed: "कार्रवाई विफल",
    uploaded: "अपलोड हो गया, समीक्षा की प्रतीक्षा में",
    viewFailed: "सामग्री देखी नहीं जा सकी",
    logFailed: "लॉग पढ़ा नहीं जा सका",
    queryFailed: "स्थानीय मॉडल क्वेरी विफल",
    title: "निजी मॉडल स्टूडियो",
    intro: "कंपनी की सामग्री की समीक्षा करें, समर्पित छोटा मॉडल प्रशिक्षित करें, होल्ड-आउट नमूने जाँचें, फिर उसे स्थानीय Ollama पर प्रकाशित करें।",
    status: "स्थानीय प्रशिक्षण: {{ready}} · स्वीकृत दस्तावेज़ {{documents}} · स्वीकृत प्रशिक्षण नमूने {{examples}}",
    ready: "तैयार",
    notReady: "तैयार नहीं",
    upload: {
      title: "अपलोड और समीक्षा",
      desc: "दस्तावेज़ों के लिए .txt, .md या .pdf उपयोग करें; प्रशिक्षण नमूनों के लिए JSONL उपयोग करें, जिसकी हर पंक्ति में instruction और output हो। सारी सामग्री पहले समीक्षा-प्रतीक्षित स्थिति में रहती है।",
      aria: "निजी डेटा अपलोड करें",
    },
    item: {
      document: "दस्तावेज़",
      example: "नमूना",
      viewFull: "पूरा पाठ देखें",
      approve: "स्वीकृत करें",
      reject: "अस्वीकार करें",
      approved: "स्वीकृत",
      rejected: "अस्वीकृत",
      empty: "अभी कोई सामग्री नहीं",
    },
    qa: {
      title: "स्थानीय ज्ञान प्रश्नोत्तर",
      desc: "केवल स्वीकृत दस्तावेज़ों में खोजता है और उपयोग किए गए स्रोत दिखाता है। मॉडल सर्वर के स्थानीय Ollama पर चलता है।",
      base06: "Qwen3 0.6B बेस मॉडल",
      base17: "Qwen3 1.7B बेस मॉडल",
      noModels: "स्थानीय Ollama में अभी कोई मॉडल नहीं है; पहले बेस मॉडल इंस्टॉल करें, या प्रशिक्षण पूरा करके प्रकाशित करें।",
      placeholder: "कंपनी के ज्ञान के बारे में प्रश्न लिखें",
      ask: "पूछें",
      source: "स्रोत: {{filename}} #{{id}}",
    },
    train: {
      title: "प्रशिक्षण, मूल्यांकन और प्रकाशन",
      desc: "कम से कम 5 नमूने स्वीकृत करें। प्रशिक्षण में कुछ नमूने स्वतंत्र मूल्यांकन के लिए अलग रखे जाते हैं; स्कोर केवल संदर्भ के लिए हैं, इसलिए प्रकाशन से पहले हर उत्तर जाँचें।",
      workerNeeded: "स्थानीय प्रशिक्षण होस्ट पर worker सक्षम करें और LLaMA-Factory CLI तथा Ollama इंस्टॉल करें।",
      start: "प्रशिक्षण शुरू करें",
      submitted: "प्रशिक्षण कार्य सबमिट हुआ",
    },
    job: {
      title: "कार्य #{{id}} · {{model}}",
      samples: "{{state}} · {{count}} नमूने",
      viewLog: "स्थानीय लॉग देखें",
      localModel: "स्थानीय मॉडल:",
      evaluate: "उम्मीदवार मॉडल का मूल्यांकन करें",
      evaluating: "उम्मीदवार मॉडल का मूल्यांकन शुरू हुआ",
      holdout: "होल्ड-आउट नमूने {{count}} · टोकन F1 {{f1}} (मैन्युअल समीक्षा अब भी आवश्यक)",
      sample: "नमूना #{{id}} · F1 {{f1}}",
      expected: "संदर्भ उत्तर: {{text}}",
      actual: "मॉडल का उत्तर: {{text}}",
      reviewed: "मैंने होल्ड-आउट नमूनों के हर मॉडल उत्तर की समीक्षा कर ली है",
      publish: "स्वीकृत करें और प्रकाशित करें",
      publishing: "स्थानीय Ollama पर प्रकाशित किया जा रहा है",
      empty: "अभी कोई प्रशिक्षण कार्य नहीं",
    },
    dialog: {
      aria: "समीक्षाधीन सामग्री का पूरा पाठ",
      close: "बंद करें",
      question: "प्रश्न: {{text}}",
      answer: "उत्तर: {{text}}",
    },
  },
  adminAdmissions: {
    runStatus: {
      completed: "पूर्ण",
      partial: "आंशिक रूप से पूर्ण",
      failed: "रन विफल",
      running: "चल रहा है",
    },
    queueNoWorker: "समीक्षा कतार लोड हुई, लेकिन दैनिक कार्य की स्थिति उपलब्ध नहीं है",
    queueNoHealth: "समीक्षा कतार लोड हुई, लेकिन स्रोत स्वास्थ्य उपलब्ध नहीं है",
    urlRequired: "आधिकारिक स्नातक प्रवेश पेज का HTTPS URL दर्ज करें",
    enabled: "{{university}} के लिए आधिकारिक प्रवेश निगरानी सक्षम की गई",
    notSaved: "समीक्षा सहेजी नहीं गई",
    syncDone: "सिंक पूरा: जाँचे गए {{checked}}, बदले {{changed}}, विफल {{failed}}",
    syncFailed: "आधिकारिक स्रोत सिंक विफल",
    title: "प्रवेश आधिकारिक स्रोतों की समीक्षा",
    desc: "केवल मैन्युअल समीक्षा से स्वीकृत HTTPS आधिकारिक स्नातक प्रवेश पेज रोज़ फ़ेच किए जाते हैं। सहेजते समय समीक्षक, समय और टिप्पणी दर्ज होती है।",
    syncNow: "समीक्षित स्रोत अभी सिंक करें",
    showPending: "केवल लंबित दिखाएँ",
    showVerified: "सत्यापित दिखाएँ ({{count}})",
    filterAria: "देश के अनुसार समीक्षा कतार फ़िल्टर करें",
    allCountries: "सभी देश",
    counts: "समीक्षा लंबित: {{pending}}; दिखाए जा रहे: {{shown}}।",
    countsSources: " समीक्षित स्रोत: {{total}}, जिनमें {{failed}} विफल और {{stale}} पुराने/असफल (सीमा {{hours}} घंटे)।",
    lastScan: "अंतिम स्कैन: {{start}} · {{status}} · जाँचे गए {{checked}} / बदले {{changed}} / विफल {{failed}}",
    completedAt: " · पूर्ण {{time}}",
    workerOn: "दैनिक निगरानी कार्य सक्षम है, हर {{hours}} घंटे पर पोल करता है।",
    workerOff: "दैनिक निगरानी कार्य सक्षम नहीं है; डेटा केवल «अभी सिंक करें» से अपडेट हो सकता है।",
    rank: "{{country}} · दायरे में क्रम {{rank}}",
    verified: "सत्यापित",
    pending: "समीक्षा लंबित",
    fetchFailed: "फ़ेच विफल: {{error}}",
    staleHint: "स्रोत पुराना है या अभी तक सफल फ़ेच नहीं हुआ; आधिकारिक पेज खोलें, समीक्षा करें और फिर अभी सिंक करें।",
    urlPlaceholder: "https://… आधिकारिक स्नातक प्रवेश पेज",
    notePlaceholder: "समीक्षा टिप्पणी (आवेदक श्रेणी और प्रवेश वर्ष लिखना उचित है जिन पर यह लागू होती है)",
    openSource: "पंजीकृत स्रोत खोलें",
    updateReview: "समीक्षित स्रोत अपडेट करें",
    approve: "समीक्षा करें और निगरानी सक्षम करें",
  },
  openOii: {
    requestFailed: "OpenOii कॉन्फ़िगरेशन अनुरोध विफल ({{status}})",
    invalidFormat: "OpenOii ने अमान्य कॉन्फ़िगरेशन प्रारूप लौटाया",
    readFailed: "लोड करना विफल",
    saved: "OpenOii कॉन्फ़िगरेशन सहेजा गया",
    restart: "कॉन्फ़िगरेशन लागू करने के लिए OpenOii सेवा रीस्टार्ट करें: {{keys}}",
    saveFailed: "सहेजना विफल",
    title: "OpenOii कॉन्फ़िगरेशन",
    tokenLabel: "OpenOii एडमिन टोकन",
    tokenPlaceholder: "कॉन्फ़िगरेशन सहेजने के लिए एडमिन टोकन आवश्यक है; केवल इस सत्र में उपयोग होगा",
    loading: "कॉन्फ़िगरेशन लोड हो रहा है…",
    retry: "पुनः प्रयास करें",
    maskedPlaceholder: "कॉन्फ़िगर किया गया; वर्तमान मान रखने के लिए खाली छोड़ें",
    save: "कॉन्फ़िगरेशन सहेजें",
  },
  stockIntelligence: {
    saved: "स्टॉक इंटेलिजेंस सेटिंग्स सहेजी गईं",
    title: "स्टॉक इंटेलिजेंस",
    intro: "AlphaSift, Daily Stock Analysis और Vibe Research की डिफ़ॉल्ट सेटिंग्स एक ही जगह प्रबंधित करें।",
    alpha: {
      desc: "स्टॉक स्क्रीनर खुलने पर हर बार पहले से भरी जाने वाली रणनीति, उम्मीदवार संख्या और शोध संदर्भ सेट करें।",
      strategy: "डिफ़ॉल्ट रणनीति",
      strategyPlaceholder: "पहली उपलब्ध रणनीति के लिए खाली छोड़ें",
      maxResults: "डिफ़ॉल्ट उम्मीदवार संख्या",
      context: "शोध संदर्भ (वैकल्पिक)",
      contextPlaceholder: "उदा. उच्च फ्री कैश फ्लो और कम कर्ज वाली कंपनियों पर ध्यान दें",
      useLlm: "डिफ़ॉल्ट रूप से AI रैंकिंग सक्षम करें",
    },
    dsa: {
      desc: "वैकल्पिक रूप से तैनात Daily Stock Analysis सेवा का URL दर्ज करें; डेस्कटॉप की अंतर्निहित सेवा के लिए खाली छोड़ें।",
      url: "सेवा URL",
      urlPlaceholder: "उदा. http://127.0.0.1:8000",
    },
    vibe: {
      desc: "Vibe Research वर्कस्पेस का URL सेट करें। डिफ़ॉल्ट रूप से Hanggent द्वारा होस्ट किया गया वर्कस्पेस उपयोग होता है।",
      url: "वर्कस्पेस URL",
      urlPlaceholder: "/vibe-research/ या https://…",
    },
    restored: "डिफ़ॉल्ट बहाल किए गए",
    restore: "डिफ़ॉल्ट बहाल करें",
    open: "स्टॉक इंटेलिजेंस खोलें",
    save: "सेटिंग्स सहेजें",
    alertsSaved: "अलर्ट सेटिंग्स सहेजी गईं; परियोजना सेटिंग्स पेज सिंक है",
    alertsSaveFailed: "Stock Alerts सेटिंग्स सहेजना विफल",
    introWithAlerts: "Stock Alerts, AlphaSift, Daily Stock Analysis और Vibe Research की डिफ़ॉल्ट सेटिंग्स एक ही जगह प्रबंधित करें।",
    alerts: {
      desc: "हर बाज़ार के लिए मूल्य-परिवर्तन अलर्ट और ईमेल सूचनाएँ सेट करें।",
      loading: "लोड हो रहा है…",
      trigger: "ट्रिगर सीमा (%)",
      step: "चरणबद्ध सीमा (%)",
      enable: "अलर्ट सक्षम करें",
      email: "ईमेल सूचनाएँ",
      extended: "प्री-मार्केट और आफ्टर-आवर्स शामिल करें",
      save: "{{market}} सेटिंग्स सहेजें",
    },
    market: {
      cn: "A शेयर",
      us: "अमेरिकी शेयर",
    },
  },
  subprojects: {
    err: {
      protocol: "सेवा URL में HTTP या HTTPS होना चाहिए",
      httpsRequired: "HTTPS पेजों के लिए HTTPS सेवा URL आवश्यक हैं",
      comfyui: "ComfyUI के लिए इस साइट का प्रॉक्सी URL उपयोग करें",
      url: "सेवा URL HTTP, HTTPS या इस साइट का पथ होना चाहिए",
    },
  },
  settingsMisc: {
    saveAlphaDefaults: "डिफ़ॉल्ट के रूप में सहेजें (Hanggent सेटिंग्स के साथ सिंक होता है)",
    openProjectSettings: "परियोजना सेटिंग्स खोलें",
    emailService: "ईमेल सेवा",
  },
  settingsErrors: {
    stockAlertsRequest: "Stock Alerts सेटिंग्स अनुरोध विफल ({{status}})",
    stockAlertsFormat: "Stock Alerts सेटिंग्स का प्रारूप अमान्य है",
    stockAlertsMissing: "इस बाज़ार की सेटिंग्स अब मौजूद नहीं हैं; कृपया रीलोड करें",
  },
  zhituCareer: {
    heading: "चीन में नौकरी खोज और करियर मिलान",
    description: "रिज़्यूमे अपलोड करें, चीन की नौकरियाँ खोजें, और Zhitu Career के चीनी करियर मिलान तथा Career-Ops मूल्यांकन रिपोर्ट से सबसे उपयुक्त पद चुनें।",
    runLabel: "चीन की नौकरियाँ खोजें",
    resultsLabel: "लाइसेंस प्राप्त चीन नौकरी डेटा स्रोत",
  },
  adminMail: {
    loadFailed: "ईमेल सेवा की स्थिति पढ़ी नहीं जा सकी",
    verified: "QQ मेलबॉक्स सत्यापित होकर सहेजा गया",
    saveFailed: "सत्यापन या सहेजना विफल",
    testSent: "परीक्षण ईमेल {{email}} पर भेजा गया",
    testFailed: "परीक्षण ईमेल भेजना विफल",
    title: "एकीकृत ईमेल सेवा",
    desc: "प्रवेश दैनिक सारांश, स्टॉक अलर्ट और वाहन/बाज़ार अलर्ट इस QQ प्रेषक खाते को साझा करते हैं। प्राधिकरण कोड एन्क्रिप्ट करके सहेजा जाता है और दोबारा नहीं दिखाया जाता।",
    configured: "कॉन्फ़िगर किया गया: {{email}}",
    notConfigured: "अभी कॉन्फ़िगर नहीं",
    qqEmail: "QQ मेलबॉक्स",
    emailPlaceholder: "अपना QQ मेलबॉक्स दर्ज करें",
    smtpCode: "SMTP प्राधिकरण कोड",
    hint: "सहेजते समय QQ SMTP लॉगिन सत्यापित होता है; परीक्षण ईमेल केवल ऊपर दिए QQ मेलबॉक्स पर भेजा जाता है।",
    verifySave: "सत्यापित करें और सहेजें",
    sendTest: "परीक्षण ईमेल भेजें",
  },
};

const appPage_it: typeof appPageEn = {
  admissionsDaily: {
    priority: {
      high: "Da verificare per primo: potrebbe riguardare una scadenza",
      attention: "Verifica consigliata: potrebbe riguardare i requisiti di candidatura",
      normal: "Contenuto della pagina aggiornato",
    },
    action: {
      deadline: "Controlla prima la scadenza",
      requirements: "Controlla documenti e idoneità",
      content: "Leggi la modifica ufficiale",
    },
    deliveryStatus: {
      sent: "Inviato",
      failed: "Invio non riuscito",
      processing: "In elaborazione",
    },
    listSeparator: ",",
    partialError: "Alcuni dati di ammissione sono temporaneamente non disponibili ({{count}} elementi); il resto è comunque consultabile. Riprova ad aggiornare più tardi.",
    title: "Riepilogo giornaliero delle modifiche ufficiali di ammissione",
    intro: "Vengono monitorate solo le pagine ufficiali di ammissione ai corsi di laurea registrate. Una prima acquisizione non significa che l'ateneo l'abbia appena pubblicato; i recuperi non riusciti vengono mostrati esplicitamente e non sono mai considerati «nessuna modifica».",
    reload: "Ricarica",
    coverage: {
      total: "Fonti ufficiali verificate",
      checked: "Verificato",
      failed: "Non riuscito, in attesa di nuovo tentativo",
      stale: "Obsoleto / senza successo",
      overHours: "Oltre {{hours}} ore",
    },
    lastScan: "Ultima scansione: {{start}} · {{status}} · controllati {{checked}} / modificati {{changed}} / falliti {{failed}}",
    completedAt: " · completata {{time}}",
    attention: {
      title: "Problemi di stato delle fonti che richiedono attenzione",
      desc: "I risultati di recupero di queste fonti non bastano per concludere «nessun aggiornamento». Apri la pagina ufficiale per verificare; gli amministratori possono riesaminare o sostituire la voce nella gestione delle fonti.",
      fetchFailed: "Recupero non riuscito: {{error}}",
      noSuccess: "Nessun recupero riuscito finora, o non aggiornata da oltre {{hours}} ore",
    },
    cohort: {
      title: "Ambito degli atenei e gestione delle fonti",
      body: "{{source}} {{edition}}: {{target}} atenei candidati predefiniti (20 ciascuno per Stati Uniti, Cina e Canada). {{verified}} fonti ufficiali per i corsi di laurea verificate; le restanti {{pending}} attendono la revisione manuale e non sono incluse nel recupero né nel riepilogo giornaliero.",
      verified: "Verificate {{verified}}/{{target}}",
      pending: "{{pending}} in attesa di revisione manuale",
    },
    cohortDaily: {
      title: "Aggiornamenti ufficiali di oggi dei Top 20 in tre paesi",
      desc: "Dal {{start}}, conteggiate solo le fonti ufficiali per i corsi di laurea verificate; riepilogo generato {{generated}}. Gli atenei in attesa di revisione non vengono contati come «nessun aggiornamento».",
      today: "Modifiche oggi {{changed}} · coperte {{active}}/{{target}}",
      failedLine: "Recupero non riuscito {{failed}} · obsolete/senza successo {{stale}} · revisione in sospeso {{pending}}",
      none: "Oggi non sono state registrate modifiche di pagina per le fonti verificate; la base di partenza di una prima scansione non conta come modifica.",
    },
    mail: {
      title: "Riepilogo giornaliero via e-mail",
      desc: "Se attivato, un riepilogo giornaliero viene inviato al termine della scansione delle fonti ufficiali; include solo le modifiche degli atenei che segui.",
      lastDelivered: " Ultima consegna: {{date}}",
      notConfigured: "Il servizio e-mail non è stato ancora configurato da un amministratore, quindi la consegna non può essere attivata.",
      lastFailed: "Ultima consegna non riuscita: {{error}}",
      contactAdmin: "Contatta un amministratore per controllare la configurazione e-mail.",
      setupLink: "Vai alle impostazioni del servizio e-mail (amministratore)",
    },
    deliveries: {
      title: "Registro delle consegne e-mail",
      desc: "Tentativi recenti di consegna del riepilogo giornaliero. Quelli falliti vengono ritentati in sicurezza nelle scansioni successive.",
      row: "{{date}} · {{status}} · {{count}} modifiche",
      sentAt: "Inviato {{time}}",
      waiting: "In attesa del completamento della consegna",
    },
    suggestions: {
      title: "Segui in base ai tuoi paesi di destinazione",
      body: "Obiettivi del profilo: {{countries}}. È possibile aggiungere {{count}} fonti ufficiali verificate; i risultati dell'esplorazione dei corsi non vengono trattati come previsioni di ammissione.",
      unsupported: "{{countries}} non rientra ancora nell'ambito delle fonti ufficiali Top 20 di USA/Cina/Canada di questa fase, quindi non verrà mostrato come coperto.",
      followAll: "Segui tutte le fonti verificate",
    },
    digest: {
      title: "Relativo agli atenei che segui",
      window: "Finestra del riepilogo: dal {{start}} · generato {{generated}}",
      countryLine: "{{country}}: seguiti {{watched}} · modificati oggi {{changed}} · falliti {{failed}} · obsoleti/senza successo {{stale}}",
      emptyWatched: "Dalle 00:00 UTC di oggi non sono state trovate nuove modifiche di pagina per gli atenei che segui.",
      emptyNone: "Seleziona gli atenei qui sotto e salva per generare un riepilogo personalizzato.",
    },
    suggestedAction: "Suggerimento: {{hint}}",
    newContent: "Contenuto aggiunto alla pagina",
    removedContent: "Contenuto rimosso o sostituito nella pagina",
    viewOfficial: "Vedi la fonte ufficiale e verifica",
    changes: {
      title: "Modifiche sostanziali rilevate nelle pagine",
      empty: "Nessuna modifica registrata finora. La prima scansione stabilisce una base di partenza; solo le scansioni successive producono registri di modifica verificabili.",
    },
    watch: {
      title: "Atenei seguiti e stato di copertura",
      hint: "Seleziona e salva; il riepilogo sintetizza solo le modifiche di questi atenei.",
      save: "Salva gli atenei seguiti",
      country: "Paese",
      filterCountry: "Filtra gli atenei seguiti per paese",
      status: "Stato della fonte",
      filterHealth: "Filtra gli atenei seguiti per salute della fonte",
      all: "Tutti",
      attention: "Richiede attenzione",
      healthy: "In buono stato",
      showing: "Visualizzati {{shown}}/{{total}} atenei",
      officialPage: "Pagina ufficiale di ammissione ai corsi di laurea",
      lastSuccess: "Ultimo recupero riuscito: {{time}}",
      noSuccess: "Nessun recupero riuscito finora.",
      staleOver: "I dati hanno più di {{hours}} ore.",
      noMatch: "Nessuna fonte ufficiale corrisponde ai filtri attuali.",
    },
  },
  educationCenter: {
    title: "Pianificazione degli studi e novità sulle ammissioni",
    desc: "Salva un profilo di studio, esplora i corsi e segui le ammissioni da fonti ufficiali. I risultati non sono previsioni di ammissione.",
    planning: "Pianificazione degli studi",
    admissions: "Riepilogo giornaliero ammissioni",
  },
  educationPlanning: {
    defaultCountry: "Cina",
    defaultCurriculum: "Curricolo nazionale cinese della scuola superiore",
    subject: {
      chinese: "Cinese",
      math: "Matematica",
      foreign: "Lingua straniera",
      physics: "Fisica",
      history: "Storia",
      chemistry: "Chimica",
      biology: "Biologia",
      geography: "Geografia",
      politics: "Politica",
    },
    category: {
      strong: "Esplora per primo",
      consider: "Da approfondire",
      not_recommended: "Conflitti rilevati",
      ineligible: "Requisiti di materie non soddisfatti",
    },
    title: "Scelta del corso e percorsi di studio",
    intro: "Registra prima i tuoi corsi reali e le preferenze, poi esplora i percorsi. L'idoneità degli atenei e le novità sulle ammissioni saranno verificate con le regole ufficiali nelle fasi successive.",
    readiness: {
      title: "Prontezza per la decisione di studio",
      progress: "{{done}}/{{total}} completati. È una lista di azioni, non una probabilità di ammissione.",
      viewAdmissions: "Vedi il riepilogo giornaliero ufficiale delle ammissioni",
    },
    field: {
      name: "Nome",
      namePlaceholder: "es. Danny",
      province: "Provincia del gaokao",
      provincePlaceholder: "es. Shaanxi",
      year: "Anno di immatricolazione previsto",
      curriculum: "Curricolo",
      countries: "Paesi di destinazione",
      countriesPlaceholder: "Cina, Canada, Stati Uniti",
      mode: "Nuova modalità del gaokao",
      electives: "Materie opzionali",
      electivesPlaceholder: "es. 物理, 化学, 生物 (nomi delle materie in cinese)",
      favorites: "Materie preferite",
      favoritesPlaceholder: "es. 数学, 物理 (nomi delle materie in cinese)",
      dislikes: "Materie che non ti piacciono o che vuoi evitare",
      dislikesPlaceholder: "es. 化学 (nome della materia in cinese)",
      interests: "Interessi",
      interestsPlaceholder: "IA, sanità",
      budget: "Budget o vincoli (facoltativo)",
      budgetPlaceholder: "es. dare priorità alle borse di studio, restare a Vancouver.",
    },
    saveProfile: "Salva profilo",
    exploreMajors: "Esplora i corsi cinesi in base ai voti",
    cmpt: {
      title: "Esplorazione dell'orientamento professionale (CMPT310)",
      desc: "Un modello sperimentale indipendente dall'esplorazione dei corsi del gaokao. Non prevede ammissione né occupazione; i dati inviati vengono scritti solo in un CSV temporaneo per un sottoprocesso ed eliminati prima della risposta.",
      unavailable: "Il connettore non è attualmente disponibile.",
      adminRequires: "Un amministratore deve configurare: {{items}}.",
      listSeparator: ",",
      run: "Avvia l'esplorazione sperimentale dell'orientamento professionale",
      confidence: "Affidabilità del modello {{percent}}%",
    },
    results: {
      title: "Risultati dell'esplorazione dei corsi",
      nextStep: "Avanti: vedi il riepilogo giornaliero ufficiale delle ammissioni",
      noConflict: "Nessun conflitto evidente trovato nei requisiti di materie e opzioni.",
      match: "Corrispondenza delle materie {{percent}}%",
    },
  },
  privateModel: {
    loadFailed: "Impossibile caricare lo studio dei modelli",
    actionFailed: "Azione non riuscita",
    uploaded: "Caricato, in attesa di revisione",
    viewFailed: "Impossibile visualizzare il contenuto",
    logFailed: "Impossibile leggere il registro",
    queryFailed: "Interrogazione del modello locale non riuscita",
    title: "Studio dei modelli privati",
    intro: "Esamina i materiali aziendali, addestra un piccolo modello dedicato, controlla i campioni riservati e poi pubblicalo su Ollama locale.",
    status: "Addestramento locale: {{ready}} · documenti approvati {{documents}} · campioni di addestramento approvati {{examples}}",
    ready: "pronto",
    notReady: "non pronto",
    upload: {
      title: "Carica e rivedi",
      desc: "Usa .txt, .md o .pdf per i documenti; usa JSONL per i campioni di addestramento, con instruction e output su ogni riga. Tutti i contenuti iniziano nello stato in attesa di revisione.",
      aria: "Carica dati privati",
    },
    item: {
      document: "Documento",
      example: "Campione",
      viewFull: "Visualizza testo completo",
      approve: "Approva",
      reject: "Rifiuta",
      approved: "Approvato",
      rejected: "Rifiutato",
      empty: "Nessun materiale per ora",
    },
    qa: {
      title: "Domande e risposte sulla conoscenza locale",
      desc: "Cerca solo nei documenti approvati e mostra le fonti usate. Il modello viene eseguito sull'Ollama locale del server.",
      base06: "Modello base Qwen3 0.6B",
      base17: "Modello base Qwen3 1.7B",
      noModels: "L'Ollama locale non ha ancora modelli; installa prima un modello base, oppure completa l'addestramento e pubblica.",
      placeholder: "Fai una domanda sulla conoscenza aziendale",
      ask: "Chiedi",
      source: "Fonte: {{filename}} #{{id}}",
    },
    train: {
      title: "Addestra, valuta e pubblica",
      desc: "Approva almeno 5 campioni. L'addestramento riserva alcuni campioni per una valutazione indipendente; i punteggi sono solo indicativi, quindi controlla ogni risposta prima di pubblicare.",
      workerNeeded: "Abilita il worker sull'host di addestramento locale e installa la CLI di LLaMA-Factory e Ollama.",
      start: "Avvia addestramento",
      submitted: "Attività di addestramento inviata",
    },
    job: {
      title: "Attività #{{id}} · {{model}}",
      samples: "{{state}} · {{count}} campioni",
      viewLog: "Visualizza registro locale",
      localModel: "Modello locale:",
      evaluate: "Valuta il modello candidato",
      evaluating: "Valutazione del modello candidato avviata",
      holdout: "Campioni riservati {{count}} · F1 sui token {{f1}} (la revisione manuale è comunque necessaria)",
      sample: "Campione #{{id}} · F1 {{f1}}",
      expected: "Risposta di riferimento: {{text}}",
      actual: "Risposta del modello: {{text}}",
      reviewed: "Ho rivisto ogni risposta del modello sui campioni riservati",
      publish: "Approva e pubblica",
      publishing: "Pubblicazione su Ollama locale",
      empty: "Nessuna attività di addestramento per ora",
    },
    dialog: {
      aria: "Testo completo del materiale in revisione",
      close: "Chiudi",
      question: "Domanda: {{text}}",
      answer: "Risposta: {{text}}",
    },
  },
  adminAdmissions: {
    runStatus: {
      completed: "Completato",
      partial: "Parzialmente completato",
      failed: "Esecuzione non riuscita",
      running: "In esecuzione",
    },
    queueNoWorker: "Coda di revisione caricata, ma lo stato dell'attività giornaliera non è disponibile",
    queueNoHealth: "Coda di revisione caricata, ma lo stato di salute delle fonti non è disponibile",
    urlRequired: "Inserisci l'URL HTTPS della pagina ufficiale di ammissione ai corsi di laurea",
    enabled: "Monitoraggio ufficiale delle ammissioni attivato per {{university}}",
    notSaved: "La revisione non è stata salvata",
    syncDone: "Sincronizzazione completata: controllati {{checked}}, modificati {{changed}}, falliti {{failed}}",
    syncFailed: "Sincronizzazione delle fonti ufficiali non riuscita",
    title: "Revisione delle fonti ufficiali di ammissione",
    desc: "Solo le pagine ufficiali HTTPS di ammissione ai corsi di laurea approvate con revisione manuale vengono recuperate ogni giorno. Al salvataggio vengono registrati revisore, ora e nota.",
    syncNow: "Sincronizza ora le fonti verificate",
    showPending: "Mostra solo in attesa",
    showVerified: "Mostra verificate ({{count}})",
    filterAria: "Filtra la coda di revisione per paese",
    allCountries: "Tutti i paesi",
    counts: "In attesa di revisione: {{pending}}; visualizzati {{shown}}.",
    countsSources: " Fonti verificate: {{total}}, di cui {{failed}} fallite e {{stale}} obsolete/senza successo (soglia {{hours}} ore).",
    lastScan: "Ultima scansione: {{start}} · {{status}} · controllati {{checked}} / modificati {{changed}} / falliti {{failed}}",
    completedAt: " · completata {{time}}",
    workerOn: "L'attività di monitoraggio giornaliero è attiva e interroga ogni {{hours}} ore.",
    workerOff: "L'attività di monitoraggio giornaliero non è attiva; i dati si possono aggiornare solo con «Sincronizza ora».",
    rank: "{{country}} · posizione nell'ambito {{rank}}",
    verified: "Verificata",
    pending: "In attesa di revisione",
    fetchFailed: "Recupero non riuscito: {{error}}",
    staleHint: "La fonte è obsoleta o non ha ancora un recupero riuscito; apri la pagina ufficiale, rivedila e poi sincronizza ora.",
    urlPlaceholder: "https://… pagina ufficiale di ammissione ai corsi di laurea",
    notePlaceholder: "Nota di revisione (indica la categoria di candidati e l'anno di immatricolazione a cui si applica)",
    openSource: "Apri la fonte registrata",
    updateReview: "Aggiorna la fonte verificata",
    approve: "Rivedi e attiva il monitoraggio",
  },
  openOii: {
    requestFailed: "Richiesta di configurazione OpenOii non riuscita ({{status}})",
    invalidFormat: "OpenOii ha restituito un formato di configurazione non valido",
    readFailed: "Caricamento non riuscito",
    saved: "Configurazione OpenOii salvata",
    restart: "Riavvia il servizio OpenOii per applicare la configurazione: {{keys}}",
    saveFailed: "Salvataggio non riuscito",
    title: "Configurazione OpenOii",
    tokenLabel: "Token amministratore OpenOii",
    tokenPlaceholder: "Per salvare la configurazione serve un token amministratore; usato solo per questa sessione",
    loading: "Caricamento della configurazione…",
    retry: "Riprova",
    maskedPlaceholder: "Configurato; lascia vuoto per mantenere il valore attuale",
    save: "Salva configurazione",
  },
  stockIntelligence: {
    saved: "Impostazioni di intelligence azionaria salvate",
    title: "Intelligence azionaria",
    intro: "Gestisci in un unico posto le impostazioni predefinite di AlphaSift, Daily Stock Analysis e Vibe Research.",
    alpha: {
      desc: "Imposta la strategia, il numero di candidati e il contesto di ricerca precompilati a ogni apertura dello screener azionario.",
      strategy: "Strategia predefinita",
      strategyPlaceholder: "Lascia vuoto per usare la prima strategia disponibile",
      maxResults: "Numero predefinito di candidati",
      context: "Contesto di ricerca (facoltativo)",
      contextPlaceholder: "es. concentrarsi su aziende con alto flusso di cassa libero e basso debito",
      useLlm: "Abilita per impostazione predefinita la classifica con IA",
    },
    dsa: {
      desc: "Inserisci facoltativamente l'URL di un servizio Daily Stock Analysis distribuito; lascia vuoto per usare il servizio integrato del desktop.",
      url: "URL del servizio",
      urlPlaceholder: "es. http://127.0.0.1:8000",
    },
    vibe: {
      desc: "Imposta l'URL dello spazio di lavoro Vibe Research. Per impostazione predefinita viene usato lo spazio ospitato da Hanggent.",
      url: "URL dello spazio di lavoro",
      urlPlaceholder: "/vibe-research/ oppure https://…",
    },
    restored: "Impostazioni predefinite ripristinate",
    restore: "Ripristina predefiniti",
    open: "Apri intelligence azionaria",
    save: "Salva impostazioni",
    alertsSaved: "Impostazioni degli avvisi salvate; la pagina delle impostazioni del progetto è sincronizzata",
    alertsSaveFailed: "Salvataggio delle impostazioni Stock Alerts non riuscito",
    introWithAlerts: "Gestisci in un unico posto le impostazioni predefinite di Stock Alerts, AlphaSift, Daily Stock Analysis e Vibe Research.",
    alerts: {
      desc: "Imposta per mercato gli avvisi di variazione di prezzo e le notifiche e-mail.",
      loading: "Caricamento…",
      trigger: "Soglia di attivazione (%)",
      step: "Soglia progressiva (%)",
      enable: "Abilita avvisi",
      email: "Notifiche e-mail",
      extended: "Includi pre-mercato e after-hours",
      save: "Salva impostazioni {{market}}",
    },
    market: {
      cn: "Azioni A",
      us: "Azioni USA",
    },
  },
  subprojects: {
    err: {
      protocol: "Gli URL dei servizi devono usare HTTP o HTTPS",
      httpsRequired: "Le pagine HTTPS richiedono URL di servizio HTTPS",
      comfyui: "Usa l'URL proxy di questo sito per ComfyUI",
      url: "Gli URL dei servizi devono essere HTTP, HTTPS o un percorso di questo sito",
    },
  },
  settingsMisc: {
    saveAlphaDefaults: "Salva come predefinito (sincronizza con le impostazioni di Hanggent)",
    openProjectSettings: "Apri impostazioni del progetto",
    emailService: "Servizio e-mail",
  },
  settingsErrors: {
    stockAlertsRequest: "Richiesta delle impostazioni Stock Alerts non riuscita ({{status}})",
    stockAlertsFormat: "Formato delle impostazioni Stock Alerts non valido",
    stockAlertsMissing: "Le impostazioni per questo mercato non esistono più; ricarica",
  },
  zhituCareer: {
    heading: "Ricerca di lavoro in Cina e abbinamento di carriera",
    description: "Carica un CV, cerca lavori in Cina e seleziona le posizioni migliori con l'abbinamento di carriera in cinese di Zhitu Career e i report di valutazione Career-Ops.",
    runLabel: "Cerca lavori in Cina",
    resultsLabel: "Fonti dati di lavoro in Cina con licenza",
  },
  adminMail: {
    loadFailed: "Impossibile leggere lo stato del servizio e-mail",
    verified: "Casella QQ verificata e salvata",
    saveFailed: "Verifica o salvataggio non riusciti",
    testSent: "E-mail di prova inviata a {{email}}",
    testFailed: "Invio dell'e-mail di prova non riuscito",
    title: "Servizio e-mail unificato",
    desc: "I riepiloghi delle ammissioni, gli avvisi azionari e gli avvisi di veicoli/mercato condividono questo account mittente QQ. Il codice di autorizzazione è salvato cifrato e non viene più mostrato.",
    configured: "Configurato: {{email}}",
    notConfigured: "Non ancora configurato",
    qqEmail: "Casella QQ",
    emailPlaceholder: "Inserisci la tua casella QQ",
    smtpCode: "Codice di autorizzazione SMTP",
    hint: "Al salvataggio viene verificato l'accesso SMTP di QQ; l'e-mail di prova viene inviata solo alla casella QQ indicata sopra.",
    verifySave: "Verifica e salva",
    sendTest: "Invia e-mail di prova",
  },
};

const appPage_ja: typeof appPageEn = {
  admissionsDaily: {
    priority: {
      high: "優先して確認：締切に関わる可能性があります",
      attention: "確認を推奨：出願要件に関わる可能性があります",
      normal: "ページ内容が更新されました",
    },
    action: {
      deadline: "まず締切を確認",
      requirements: "書類と資格を確認",
      content: "公式の変更を読む",
    },
    deliveryStatus: {
      sent: "送信済み",
      failed: "送信失敗",
      processing: "処理中",
    },
    listSeparator: "、",
    partialError: "一部の入試データを現在取得できません（{{count}} 件）。その他の内容は引き続き閲覧できます。後でもう一度更新してください。",
    title: "公式入試変更デイリー",
    intro: "登録済みの公式学部入試ページのみを監視します。初回の取り込みは学校が公開したばかりであることを意味しません。取得失敗は明示的に表示され、「変更なし」とは見なされません。",
    reload: "再読み込み",
    coverage: {
      total: "審査済みの公式ソース",
      checked: "確認済み",
      failed: "失敗・再試行待ち",
      stale: "古い／未成功",
      overHours: "{{hours}} 時間超",
    },
    lastScan: "最終スキャン：{{start}} · {{status}} · 確認 {{checked}} / 変更 {{changed}} / 失敗 {{failed}}",
    completedAt: " · 完了 {{time}}",
    attention: {
      title: "対応が必要なソースの状態の問題",
      desc: "これらのソースの取得結果では「更新なし」と判断できません。公式ページを開いて確認してください。管理者はソース管理で入口を再審査または差し替えできます。",
      fetchFailed: "取得失敗：{{error}}",
      noSuccess: "取得成功の記録がない、または {{hours}} 時間以上更新されていません",
    },
    cohort: {
      title: "対象大学の範囲とソース管理",
      body: "{{source}} {{edition}}：既定の候補大学 {{target}} 校（米・中・加 各 20 校）。公式学部ソースを確認済み {{verified}} 校。残り {{pending}} 校は手動審査待ちで、取得やデイリーには含まれません。",
      verified: "確認済み {{verified}}/{{target}}",
      pending: "手動審査待ち {{pending}} 校",
    },
    cohortDaily: {
      title: "3 か国トップ 20 の本日の公式更新",
      desc: "{{start}} 以降、審査済みの公式学部ソースのみを集計。サマリー生成：{{generated}}。審査待ちの学校は「更新なし」とは数えません。",
      today: "本日の変更 {{changed}} · カバー {{active}}/{{target}}",
      failedLine: "取得失敗 {{failed}} · 古い／未成功 {{stale}} · 審査待ち {{pending}}",
      none: "審査済みソースのページ変更は本日まだ記録されていません。初回スキャンのベースラインは変更として数えません。",
    },
    mail: {
      title: "毎日のメールダイジェスト",
      desc: "有効にすると、公式ソースのスキャン完了後にその日のダイジェストを送信します。フォロー中の学校の変更のみが含まれます。",
      lastDelivered: " 最終配信：{{date}}",
      notConfigured: "メールサービスが管理者によって設定されていないため、まだ配信を有効にできません。",
      lastFailed: "直近の配信に失敗しました：{{error}}",
      contactAdmin: "管理者にメール設定の確認を依頼してください。",
      setupLink: "メールサービス設定へ移動（管理者）",
    },
    deliveries: {
      title: "メール配信履歴",
      desc: "最近のダイジェスト配信の試行。失敗したものは次回以降のスキャンで安全に再試行されます。",
      row: "{{date}} · {{status}} · 変更 {{count}} 件",
      sentAt: "送信日時 {{time}}",
      waiting: "配信完了待ち",
    },
    suggestions: {
      title: "志望国でフォロー",
      body: "プロフィールの志望国：{{countries}}。審査済みの公式ソース {{count}} 件を追加できます。専攻探索の結果を合格予測と見なすものではありません。",
      unsupported: "{{countries}} は今回の米・中・加トップ 20 公式ソースの範囲に含まれていないため、カバー済みとは表示されません。",
      followAll: "審査済みソースをすべてフォロー",
    },
    digest: {
      title: "フォロー中の学校に関連",
      window: "ダイジェスト期間：{{start}} 以降 · 生成 {{generated}}",
      countryLine: "{{country}}：フォロー {{watched}} · 本日の変更 {{changed}} · 失敗 {{failed}} · 古い／未成功 {{stale}}",
      emptyWatched: "本日 00:00 UTC 以降、フォロー中の学校に新しいページ変更は見つかっていません。",
      emptyNone: "下で学校を選んで保存すると、パーソナライズされたダイジェストが生成されます。",
    },
    suggestedAction: "おすすめ：{{hint}}",
    newContent: "ページに追加された内容",
    removedContent: "ページから削除または置換された内容",
    viewOfficial: "公式ソースを開いて確認",
    changes: {
      title: "検出された実質的なページ変更",
      empty: "記録された変更はまだありません。初回スキャンでベースラインを作成し、以降のスキャンで確認可能な変更記録が作られます。",
    },
    watch: {
      title: "フォロー中の学校とカバー状況",
      hint: "選択して保存します。ダイジェストはこれらの学校の変更のみをまとめます。",
      save: "フォロー中の学校を保存",
      country: "国",
      filterCountry: "国でフォロー中の学校を絞り込む",
      status: "ソースの状態",
      filterHealth: "ソースの健全性でフォロー中の学校を絞り込む",
      all: "すべて",
      attention: "要対応",
      healthy: "正常",
      showing: "{{shown}}/{{total}} 校を表示",
      officialPage: "公式学部入試ページ",
      lastSuccess: "最終取得成功：{{time}}",
      noSuccess: "取得に成功した記録はまだありません。",
      staleOver: "データが {{hours}} 時間以上古くなっています。",
      noMatch: "現在の絞り込み条件に一致する公式ソースはありません。",
    },
  },
  educationCenter: {
    title: "進学計画と入試情報",
    desc: "学習プロフィールを保存し、専攻を探索し、公式ソースに基づいて入試情報を追跡します。結果は合格予測ではありません。",
    planning: "進学計画",
    admissions: "入試デイリー",
  },
  educationPlanning: {
    defaultCountry: "中国",
    defaultCurriculum: "中国の普通高校課程",
    subject: {
      chinese: "国語",
      math: "数学",
      foreign: "外国語",
      physics: "物理",
      history: "歴史",
      chemistry: "化学",
      biology: "生物",
      geography: "地理",
      politics: "政治",
    },
    category: {
      strong: "まず探索",
      consider: "さらに調べる価値あり",
      not_recommended: "競合あり",
      ineligible: "選択科目の要件を満たしていません",
    },
    title: "専攻選択と進学ルート",
    intro: "まず実際の履修科目と希望を記録し、次に専攻ルートを探索します。大学の出願資格や入試情報は、後の段階で公式ルールに基づいて確認されます。",
    readiness: {
      title: "進学判断の準備状況",
      progress: "{{done}}/{{total}} 件完了。これは行動チェックリストであり、合格確率ではありません。",
      viewAdmissions: "公式入試デイリーを見る",
    },
    field: {
      name: "呼び名",
      namePlaceholder: "例：Danny",
      province: "高考の省",
      provincePlaceholder: "例：陝西",
      year: "入学予定年",
      curriculum: "カリキュラム",
      countries: "志望国",
      countriesPlaceholder: "中国, カナダ, アメリカ",
      mode: "新高考モード",
      electives: "選択科目",
      electivesPlaceholder: "例：物理, 化学, 生物（中国語の科目名）",
      favorites: "好きな科目",
      favoritesPlaceholder: "例：数学, 物理（中国語の科目名）",
      dislikes: "苦手・避けたい科目",
      dislikesPlaceholder: "例：化学（中国語の科目名）",
      interests: "興味のある分野",
      interestsPlaceholder: "AI, ヘルスケア",
      budget: "予算や制約（任意）",
      budgetPlaceholder: "例：奨学金を優先、バンクーバーに残りたい。",
    },
    saveProfile: "プロフィールを保存",
    exploreMajors: "成績から中国の専攻を探索",
    cmpt: {
      title: "キャリア方向の探索（CMPT310）",
      desc: "高考の専攻探索とは独立した実験的モデルです。合格や就職は予測しません。送信したデータはサブプロセスの計算用に一時 CSV にのみ書き込まれ、応答前に削除されます。",
      unavailable: "このコネクタは現在利用できません。",
      adminRequires: "管理者による設定が必要です：{{items}}。",
      listSeparator: "、",
      run: "実験的キャリア方向探索を実行",
      confidence: "モデルの信頼度 {{percent}}%",
    },
    results: {
      title: "専攻探索の結果",
      nextStep: "次へ：公式入試デイリーを見る",
      noConflict: "科目と選択科目の条件に明らかな競合は見つかりませんでした。",
      match: "科目の一致度 {{percent}}%",
    },
  },
  privateModel: {
    loadFailed: "モデルスタジオを読み込めません",
    actionFailed: "操作に失敗しました",
    uploaded: "アップロードしました。審査待ちです",
    viewFailed: "内容を表示できません",
    logFailed: "ログを読み取れません",
    queryFailed: "ローカルモデルへの問い合わせに失敗しました",
    title: "プライベートモデルスタジオ",
    intro: "社内資料を審査し、専用の小型モデルを学習し、ホールドアウトサンプルを確認してから、ローカルの Ollama に公開します。",
    status: "ローカル学習：{{ready}} · 承認済みドキュメント {{documents}} · 承認済み学習サンプル {{examples}}",
    ready: "準備完了",
    notReady: "未準備",
    upload: {
      title: "アップロードと審査",
      desc: "ドキュメントは .txt、.md、.pdf、学習サンプルは JSONL（各行に instruction と output）を使用します。すべての内容はまず審査待ち状態になります。",
      aria: "プライベートデータをアップロード",
    },
    item: {
      document: "ドキュメント",
      example: "サンプル",
      viewFull: "全文を表示",
      approve: "承認",
      reject: "却下",
      approved: "承認済み",
      rejected: "却下済み",
      empty: "資料はまだありません",
    },
    qa: {
      title: "ローカルナレッジ Q&A",
      desc: "承認済みのドキュメントのみを検索し、使用した出典を表示します。モデルはサーバーのローカル Ollama 上で動作します。",
      base06: "Qwen3 0.6B ベースモデル",
      base17: "Qwen3 1.7B ベースモデル",
      noModels: "ローカルの Ollama にモデルがありません。先にベースモデルをインストールするか、学習を完了して公開してください。",
      placeholder: "社内ナレッジについて質問を入力",
      ask: "質問する",
      source: "出典：{{filename}} #{{id}}",
    },
    train: {
      title: "学習・評価・公開",
      desc: "サンプルを 5 件以上承認してください。学習では一部のサンプルを独立評価用に取り分けます。スコアは参考値のため、公開前に各回答を確認してください。",
      workerNeeded: "ローカル学習ホストで worker を有効にし、LLaMA-Factory CLI と Ollama をインストールする必要があります。",
      start: "学習を開始",
      submitted: "学習ジョブを送信しました",
    },
    job: {
      title: "ジョブ #{{id}} · {{model}}",
      samples: "{{state}} · サンプル {{count}} 件",
      viewLog: "ローカルログを表示",
      localModel: "ローカルモデル：",
      evaluate: "候補モデルを評価",
      evaluating: "候補モデルの評価を開始しました",
      holdout: "ホールドアウトサンプル {{count}} 件 · トークン F1 {{f1}}（手動確認は引き続き必要）",
      sample: "サンプル #{{id}} · F1 {{f1}}",
      expected: "参照回答：{{text}}",
      actual: "モデルの回答：{{text}}",
      reviewed: "ホールドアウトサンプルのモデル回答を 1 件ずつ確認しました",
      publish: "承認して公開",
      publishing: "ローカルの Ollama に公開中",
      empty: "学習ジョブはまだありません",
    },
    dialog: {
      aria: "審査中の資料の全文",
      close: "閉じる",
      question: "質問：{{text}}",
      answer: "回答：{{text}}",
    },
  },
  adminAdmissions: {
    runStatus: {
      completed: "完了",
      partial: "一部完了",
      failed: "実行失敗",
      running: "実行中",
    },
    queueNoWorker: "審査キューを読み込みましたが、日次ジョブの状態を取得できません",
    queueNoHealth: "審査キューを読み込みましたが、ソースの健全性を取得できません",
    urlRequired: "公式学部入試ページの HTTPS URL を入力してください",
    enabled: "{{university}} の公式入試監視を有効にしました",
    notSaved: "審査は保存されませんでした",
    syncDone: "同期完了：確認 {{checked}}、変更 {{changed}}、失敗 {{failed}}",
    syncFailed: "公式ソースの同期に失敗しました",
    title: "入試公式ソースの審査",
    desc: "手動審査で承認された HTTPS の公式学部入試ページのみが毎日取得されます。保存時に審査者、日時、メモが記録されます。",
    syncNow: "審査済みソースを今すぐ同期",
    showPending: "審査待ちのみ表示",
    showVerified: "確認済みを表示 ({{count}})",
    filterAria: "国で審査キューを絞り込む",
    allCountries: "すべての国",
    counts: "審査待ち {{pending}} 校、現在 {{shown}} 校を表示。",
    countsSources: " 審査済みソース {{total}} 件、うち失敗 {{failed}} 件、古い／未成功 {{stale}} 件（しきい値 {{hours}} 時間）。",
    lastScan: "最終スキャン：{{start}} · {{status}} · 確認 {{checked}} / 変更 {{changed}} / 失敗 {{failed}}",
    completedAt: " · 完了 {{time}}",
    workerOn: "日次監視ジョブは有効で、{{hours}} 時間ごとにポーリングします。",
    workerOff: "日次監視ジョブは現在無効です。「今すぐ同期」でのみデータを更新できます。",
    rank: "{{country}} · 範囲内の順位 {{rank}}",
    verified: "確認済み",
    pending: "審査待ち",
    fetchFailed: "取得失敗：{{error}}",
    staleHint: "ソースが古い、または取得成功の記録がありません。公式ページを開いて確認し、今すぐ同期してください。",
    urlPlaceholder: "https://… 公式学部入試ページ",
    notePlaceholder: "審査メモ（対象となる出願者区分と入学年を記載することを推奨）",
    openSource: "登録済みソースを開く",
    updateReview: "審査済みソースを更新",
    approve: "審査して監視を有効化",
  },
  openOii: {
    requestFailed: "OpenOii 設定リクエストに失敗しました ({{status}})",
    invalidFormat: "OpenOii が無効な設定形式を返しました",
    readFailed: "読み込みに失敗しました",
    saved: "OpenOii の設定を保存しました",
    restart: "設定を反映するには OpenOii サービスの再起動が必要です：{{keys}}",
    saveFailed: "保存に失敗しました",
    title: "OpenOii 設定",
    tokenLabel: "OpenOii 管理者トークン",
    tokenPlaceholder: "設定の保存には管理者トークンが必要です。このセッションでのみ使用されます",
    loading: "設定を読み込み中…",
    retry: "再試行",
    maskedPlaceholder: "設定済み。空欄のままにすると現在の値を保持します",
    save: "設定を保存",
  },
  stockIntelligence: {
    saved: "株式インテリジェンスの設定を保存しました",
    title: "株式インテリジェンス",
    intro: "AlphaSift、Daily Stock Analysis、Vibe Research の既定設定をまとめて管理します。",
    alpha: {
      desc: "銘柄スクリーナーを開くたびに事前入力される戦略、候補数、調査コンテキストを設定します。",
      strategy: "既定の戦略",
      strategyPlaceholder: "空欄の場合は最初に利用可能な戦略を使用",
      maxResults: "既定の候補数",
      context: "調査コンテキスト（任意）",
      contextPlaceholder: "例：フリーキャッシュフローが高く負債の少ない企業に注目",
      useLlm: "AI ランキングを既定で有効にする",
    },
    dsa: {
      desc: "デプロイ済みの Daily Stock Analysis サービスの URL を入力できます。空欄の場合はデスクトップ内蔵サービスを使用します。",
      url: "サービス URL",
      urlPlaceholder: "例：http://127.0.0.1:8000",
    },
    vibe: {
      desc: "Vibe Research ワークスペースの URL を設定します。既定では Hanggent がホストするワークスペースを使用します。",
      url: "ワークスペース URL",
      urlPlaceholder: "/vibe-research/ または https://…",
    },
    restored: "既定値に戻しました",
    restore: "既定値に戻す",
    open: "株式インテリジェンスを開く",
    save: "設定を保存",
    alertsSaved: "アラート設定を保存しました。プロジェクト設定ページにも同期されます",
    alertsSaveFailed: "Stock Alerts 設定の保存に失敗しました",
    introWithAlerts: "Stock Alerts、AlphaSift、Daily Stock Analysis、Vibe Research の既定設定をまとめて管理します。",
    alerts: {
      desc: "市場ごとに価格変動アラートとメール通知を設定します。",
      loading: "読み込み中…",
      trigger: "トリガーしきい値 (%)",
      step: "段階しきい値 (%)",
      enable: "アラートを有効化",
      email: "メール通知",
      extended: "プレマーケットと時間外を含む",
      save: "{{market}}の設定を保存",
    },
    market: {
      cn: "A 株",
      us: "米国株",
    },
  },
  subprojects: {
    err: {
      protocol: "サービス URL は HTTP または HTTPS を使用する必要があります",
      httpsRequired: "HTTPS ページでは HTTPS のサービス URL が必要です",
      comfyui: "ComfyUI には本サイトのプロキシ URL を使用してください",
      url: "サービス URL は HTTP、HTTPS、またはこのサイト内のパスである必要があります",
    },
  },
  settingsMisc: {
    saveAlphaDefaults: "既定として保存（Hanggent 設定と同期）",
    openProjectSettings: "プロジェクト設定を開く",
    emailService: "メールサービス",
  },
  settingsErrors: {
    stockAlertsRequest: "Stock Alerts 設定リクエストに失敗しました ({{status}})",
    stockAlertsFormat: "Stock Alerts 設定の形式が無効です",
    stockAlertsMissing: "この市場の設定は存在しなくなりました。再読み込みしてください",
  },
  zhituCareer: {
    heading: "中国の求人検索とキャリアマッチング",
    description: "履歴書をアップロードして中国の求人を検索し、Zhitu Career の中国語キャリアマッチングと Career-Ops 評価レポートで最適なポジションを絞り込みます。",
    runLabel: "中国の求人を検索",
    resultsLabel: "ライセンス済みの中国求人データソース",
  },
  adminMail: {
    loadFailed: "メールサービスの状態を読み取れません",
    verified: "QQ メールを確認して保存しました",
    saveFailed: "確認または保存に失敗しました",
    testSent: "テストメールを {{email}} に送信しました",
    testFailed: "テストメールの送信に失敗しました",
    title: "統合メールサービス",
    desc: "入試デイリー、株式アラート、車両／マーケットのアラートは、この QQ 送信アカウントを共有します。認証コードは暗号化して保存され、再表示されません。",
    configured: "設定済み：{{email}}",
    notConfigured: "未設定",
    qqEmail: "QQ メール",
    emailPlaceholder: "QQ メールを入力してください",
    smtpCode: "SMTP 認証コード",
    hint: "保存時に QQ SMTP ログインを確認します。テストメールは上記の QQ メールにのみ送信されます。",
    verifySave: "確認して保存",
    sendTest: "テストメールを送信",
  },
};

const appPage_ko: typeof appPageEn = {
  admissionsDaily: {
    priority: {
      high: "우선 확인: 마감일과 관련될 수 있습니다",
      attention: "확인 권장: 지원 요건과 관련될 수 있습니다",
      normal: "페이지 내용 업데이트됨",
    },
    action: {
      deadline: "먼저 마감일 확인",
      requirements: "제출 서류와 자격 확인",
      content: "공식 변경 사항 읽기",
    },
    deliveryStatus: {
      sent: "전송됨",
      failed: "전송 실패",
      processing: "처리 중",
    },
    listSeparator: ",",
    partialError: "일부 입시 데이터를 일시적으로 사용할 수 없습니다({{count}}개 항목). 나머지 내용은 계속 볼 수 있습니다. 나중에 새로고침해 주세요.",
    title: "공식 입시 변경 일일 보고",
    intro: "등록된 공식 학부 입시 페이지만 모니터링합니다. 처음 수집된 내용이 학교가 방금 게시했다는 뜻은 아닙니다. 수집 실패는 명확히 표시되며 \"변경 없음\"으로 간주되지 않습니다.",
    reload: "다시 불러오기",
    coverage: {
      total: "검토된 공식 출처",
      checked: "확인 완료",
      failed: "실패, 재시도 대기",
      stale: "오래됨 / 미성공",
      overHours: "{{hours}}시간 초과",
    },
    lastScan: "최근 스캔: {{start}} · {{status}} · 확인 {{checked}} / 변경 {{changed}} / 실패 {{failed}}",
    completedAt: " · 완료 {{time}}",
    attention: {
      title: "조치가 필요한 출처 상태 문제",
      desc: "이 출처들의 수집 결과로는 \"업데이트 없음\"을 판단하기에 부족합니다. 공식 페이지를 열어 확인하세요. 관리자는 출처 관리에서 항목을 재검토하거나 교체할 수 있습니다.",
      fetchFailed: "수집 실패: {{error}}",
      noSuccess: "성공한 수집 기록이 없거나 {{hours}}시간 넘게 업데이트되지 않음",
    },
    cohort: {
      title: "대학 범위 및 출처 관리",
      body: "{{source}} {{edition}}: 기본 후보 대학 {{target}}곳(미국·중국·캐나다 각 20곳). 공식 학부 출처 {{verified}}곳 확인 완료, 나머지 {{pending}}곳은 수동 검토 대기 중이며 수집 및 일일 보고에 포함되지 않습니다.",
      verified: "확인 완료 {{verified}}/{{target}}",
      pending: "수동 검토 대기 {{pending}}곳",
    },
    cohortDaily: {
      title: "3개국 Top 20 오늘의 공식 업데이트",
      desc: "{{start}}부터 검토된 공식 학부 출처만 집계합니다. 요약 생성 시각: {{generated}}. 검토 대기 중인 학교는 \"업데이트 없음\"으로 집계되지 않습니다.",
      today: "오늘 변경 {{changed}} · 포함 {{active}}/{{target}}",
      failedLine: "수집 실패 {{failed}} · 오래됨/미성공 {{stale}} · 검토 대기 {{pending}}",
      none: "검토된 출처에서 오늘 기록된 페이지 변경이 아직 없습니다. 첫 스캔의 기준선은 변경으로 집계되지 않습니다.",
    },
    mail: {
      title: "일일 이메일 요약",
      desc: "켜면 공식 출처 스캔이 끝난 뒤 그날의 요약을 보냅니다. 팔로우한 학교의 변경 사항만 포함됩니다.",
      lastDelivered: " 최근 발송: {{date}}",
      notConfigured: "관리자가 이메일 서비스를 아직 설정하지 않아 발송을 켤 수 없습니다.",
      lastFailed: "최근 발송 실패: {{error}}",
      contactAdmin: "관리자에게 이메일 설정 확인을 요청하세요.",
      setupLink: "이메일 서비스 설정으로 이동(관리자)",
    },
    deliveries: {
      title: "이메일 발송 기록",
      desc: "최근 일일 요약 발송 시도입니다. 실패한 항목은 이후 스캔에서 안전하게 재시도됩니다.",
      row: "{{date}} · {{status}} · 변경 {{count}}건",
      sentAt: "{{time}}에 전송됨",
      waiting: "발송 완료 대기 중",
    },
    suggestions: {
      title: "목표 국가별로 팔로우",
      body: "프로필 목표: {{countries}}. 검토된 공식 출처 {{count}}곳을 추가할 수 있습니다. 전공 탐색 결과를 합격 예측으로 오해하지 않도록 구분됩니다.",
      unsupported: "{{countries}}은(는) 이번 단계의 미국/중국/캐나다 Top 20 공식 출처 범위에 아직 포함되지 않아 포함됨으로 표시되지 않습니다.",
      followAll: "검토된 출처 모두 팔로우",
    },
    digest: {
      title: "팔로우한 학교 관련",
      window: "요약 기간: {{start}}부터 · 생성 {{generated}}",
      countryLine: "{{country}}: 팔로우 {{watched}} · 오늘 변경 {{changed}} · 실패 {{failed}} · 오래됨/미성공 {{stale}}",
      emptyWatched: "오늘 00:00 UTC 이후 팔로우한 학교의 새 페이지 변경이 발견되지 않았습니다.",
      emptyNone: "아래에서 학교를 선택하고 저장하면 맞춤 요약이 생성됩니다.",
    },
    suggestedAction: "제안: {{hint}}",
    newContent: "페이지에 추가된 내용",
    removedContent: "페이지에서 제거되거나 교체된 내용",
    viewOfficial: "공식 출처 보기 및 확인",
    changes: {
      title: "감지된 실질적 페이지 변경",
      empty: "기록된 변경이 아직 없습니다. 첫 스캔은 기준선을 만들고, 이후 스캔부터 검토 가능한 변경 기록이 생성됩니다.",
    },
    watch: {
      title: "팔로우한 학교 및 포함 상태",
      hint: "선택 후 저장하세요. 요약에는 이 학교들의 변경만 포함됩니다.",
      save: "팔로우한 학교 저장",
      country: "국가",
      filterCountry: "국가별로 팔로우한 학교 필터",
      status: "출처 상태",
      filterHealth: "출처 상태별로 팔로우한 학교 필터",
      all: "전체",
      attention: "조치 필요",
      healthy: "정상",
      showing: "{{shown}}/{{total}}곳 표시 중",
      officialPage: "공식 학부 입시 페이지",
      lastSuccess: "마지막 수집 성공: {{time}}",
      noSuccess: "아직 성공한 수집이 없습니다.",
      staleOver: "데이터가 {{hours}}시간 넘게 오래되었습니다.",
      noMatch: "현재 필터와 일치하는 공식 출처가 없습니다.",
    },
  },
  educationCenter: {
    title: "진학 계획 및 입시 소식",
    desc: "학습 프로필을 저장하고 전공을 탐색하며 공식 출처 기준으로 입시 정보를 추적합니다. 결과는 합격 예측이 아닙니다.",
    planning: "진학 계획",
    admissions: "입시 일일 보고",
  },
  educationPlanning: {
    defaultCountry: "중국",
    defaultCurriculum: "중국 일반 고등학교 교육과정",
    subject: {
      chinese: "국어",
      math: "수학",
      foreign: "외국어",
      physics: "물리",
      history: "역사",
      chemistry: "화학",
      biology: "생물",
      geography: "지리",
      politics: "정치",
    },
    category: {
      strong: "우선 탐색",
      consider: "더 알아볼 만함",
      not_recommended: "충돌 있음",
      ineligible: "선택 과목 요건 미충족",
    },
    title: "전공 선택과 진학 경로",
    intro: "실제 수강 과목과 선호를 먼저 기록한 뒤 전공 경로를 탐색하세요. 대학 자격과 입시 소식은 이후 단계에서 공식 규정에 따라 확인됩니다.",
    readiness: {
      title: "진학 결정 준비도",
      progress: "{{done}}/{{total}}개 완료. 이것은 실행 체크리스트이며 합격 확률이 아닙니다.",
      viewAdmissions: "공식 입시 일일 보고 보기",
    },
    field: {
      name: "호칭",
      namePlaceholder: "예: Danny",
      province: "가오카오 성(省)",
      provincePlaceholder: "예: 산시",
      year: "입학 예정 연도",
      curriculum: "교육과정",
      countries: "목표 국가",
      countriesPlaceholder: "중국, 캐나다, 미국",
      mode: "신가오카오 방식",
      electives: "선택 과목",
      electivesPlaceholder: "예: 物理, 化学, 生物 (중국어 과목명)",
      favorites: "좋아하는 과목",
      favoritesPlaceholder: "예: 数学, 物理 (중국어 과목명)",
      dislikes: "싫어하거나 피하고 싶은 과목",
      dislikesPlaceholder: "예: 化学 (중국어 과목명)",
      interests: "관심 분야",
      interestsPlaceholder: "AI, 헬스케어",
      budget: "예산 또는 제약 (선택)",
      budgetPlaceholder: "예: 장학금 우선, 밴쿠버에 머물기를 희망.",
    },
    saveProfile: "프로필 저장",
    exploreMajors: "성적으로 중국 전공 탐색",
    cmpt: {
      title: "진로 방향 탐색 (CMPT310)",
      desc: "가오카오 전공 탐색과는 별개의 실험적 모델입니다. 합격이나 취업을 예측하지 않으며, 제출한 데이터는 하위 프로세스 계산용 임시 CSV에만 기록되고 응답 전에 삭제됩니다.",
      unavailable: "커넥터를 현재 사용할 수 없습니다.",
      adminRequires: "관리자 설정이 필요합니다: {{items}}.",
      listSeparator: ",",
      run: "실험적 진로 방향 탐색 실행",
      confidence: "모델 신뢰도 {{percent}}%",
    },
    results: {
      title: "전공 탐색 결과",
      nextStep: "다음: 공식 입시 일일 보고 보기",
      noConflict: "과목 및 선택 과목 요건에서 뚜렷한 충돌이 발견되지 않았습니다.",
      match: "과목 일치도 {{percent}}%",
    },
  },
  privateModel: {
    loadFailed: "모델 스튜디오를 불러올 수 없습니다",
    actionFailed: "작업에 실패했습니다",
    uploaded: "업로드됨, 검토 대기 중",
    viewFailed: "내용을 볼 수 없습니다",
    logFailed: "로그를 읽을 수 없습니다",
    queryFailed: "로컬 모델 조회에 실패했습니다",
    title: "프라이빗 모델 스튜디오",
    intro: "기업 자료를 검토하고, 전용 소형 모델을 학습시키고, 홀드아웃 샘플을 확인한 뒤 로컬 Ollama에 게시합니다.",
    status: "로컬 학습: {{ready}} · 승인된 문서 {{documents}} · 승인된 학습 샘플 {{examples}}",
    ready: "준비됨",
    notReady: "준비 안 됨",
    upload: {
      title: "업로드 및 검토",
      desc: "문서는 .txt, .md, .pdf를, 학습 샘플은 각 줄에 instruction과 output이 있는 JSONL을 사용하세요. 모든 내용은 먼저 검토 대기 상태가 됩니다.",
      aria: "비공개 데이터 업로드",
    },
    item: {
      document: "문서",
      example: "샘플",
      viewFull: "전문 보기",
      approve: "승인",
      reject: "거부",
      approved: "승인됨",
      rejected: "거부됨",
      empty: "자료가 아직 없습니다",
    },
    qa: {
      title: "로컬 지식 Q&A",
      desc: "승인된 문서만 검색하고 사용한 출처를 표시합니다. 모델은 서버의 로컬 Ollama에서 실행됩니다.",
      base06: "Qwen3 0.6B 기본 모델",
      base17: "Qwen3 1.7B 기본 모델",
      noModels: "로컬 Ollama에 모델이 없습니다. 먼저 기본 모델을 설치하거나 학습을 마치고 게시하세요.",
      placeholder: "기업 지식에 대한 질문을 입력하세요",
      ask: "질문하기",
      source: "출처: {{filename}} #{{id}}",
    },
    train: {
      title: "학습, 평가 및 게시",
      desc: "샘플을 최소 5개 승인하세요. 학습 시 일부 샘플을 독립 평가용으로 남겨 둡니다. 점수는 참고용이므로 게시 전에 각 답변을 확인하세요.",
      workerNeeded: "로컬 학습 호스트에서 worker를 활성화하고 LLaMA-Factory CLI와 Ollama를 설치해야 합니다.",
      start: "학습 시작",
      submitted: "학습 작업이 제출되었습니다",
    },
    job: {
      title: "작업 #{{id}} · {{model}}",
      samples: "{{state}} · 샘플 {{count}}개",
      viewLog: "로컬 로그 보기",
      localModel: "로컬 모델:",
      evaluate: "후보 모델 평가",
      evaluating: "후보 모델 평가를 시작했습니다",
      holdout: "홀드아웃 샘플 {{count}}개 · 토큰 F1 {{f1}} (수동 검토는 여전히 필요)",
      sample: "샘플 #{{id}} · F1 {{f1}}",
      expected: "참조 답변: {{text}}",
      actual: "모델 답변: {{text}}",
      reviewed: "홀드아웃 샘플의 모델 답변을 하나씩 검토했습니다",
      publish: "승인 및 게시",
      publishing: "로컬 Ollama에 게시 중",
      empty: "학습 작업이 아직 없습니다",
    },
    dialog: {
      aria: "검토 중인 자료 전문",
      close: "닫기",
      question: "질문: {{text}}",
      answer: "답변: {{text}}",
    },
  },
  adminAdmissions: {
    runStatus: {
      completed: "완료됨",
      partial: "부분 완료",
      failed: "실행 실패",
      running: "실행 중",
    },
    queueNoWorker: "검토 대기열을 불러왔지만 일일 작업 상태를 확인할 수 없습니다",
    queueNoHealth: "검토 대기열을 불러왔지만 출처 상태를 확인할 수 없습니다",
    urlRequired: "공식 학부 입시 페이지의 HTTPS URL을 입력하세요",
    enabled: "{{university}}의 공식 입시 모니터링이 활성화되었습니다",
    notSaved: "검토가 저장되지 않았습니다",
    syncDone: "동기화 완료: 확인 {{checked}}, 변경 {{changed}}, 실패 {{failed}}",
    syncFailed: "공식 출처 동기화에 실패했습니다",
    title: "입시 공식 출처 검토",
    desc: "수동 검토를 통과한 HTTPS 공식 학부 입시 페이지만 매일 수집됩니다. 저장 시 검토자, 시간 및 메모가 기록됩니다.",
    syncNow: "검토된 출처 지금 동기화",
    showPending: "검토 대기만 표시",
    showVerified: "확인 완료 표시 ({{count}})",
    filterAria: "국가별로 검토 대기열 필터",
    allCountries: "모든 국가",
    counts: "검토 대기 {{pending}}곳, 현재 {{shown}}곳 표시 중.",
    countsSources: " 검토된 출처 {{total}}곳, 이 중 실패 {{failed}}곳, 오래됨/미성공 {{stale}}곳 (기준 {{hours}}시간).",
    lastScan: "최근 스캔: {{start}} · {{status}} · 확인 {{checked}} / 변경 {{changed}} / 실패 {{failed}}",
    completedAt: " · 완료 {{time}}",
    workerOn: "일일 모니터링 작업이 활성화되어 있으며 {{hours}}시간마다 폴링합니다.",
    workerOff: "일일 모니터링 작업이 활성화되어 있지 않습니다. \"지금 동기화\"로만 데이터를 업데이트할 수 있습니다.",
    rank: "{{country}} · 범위 내 순번 {{rank}}",
    verified: "확인 완료",
    pending: "검토 대기",
    fetchFailed: "수집 실패: {{error}}",
    staleHint: "출처가 오래되었거나 성공한 수집이 없습니다. 공식 페이지를 열어 검토한 뒤 지금 동기화하세요.",
    urlPlaceholder: "https://… 공식 학부 입시 페이지",
    notePlaceholder: "검토 메모 (해당되는 지원자 유형과 입학 연도를 명시하는 것을 권장)",
    openSource: "등록된 출처 열기",
    updateReview: "검토된 출처 업데이트",
    approve: "검토 후 모니터링 활성화",
  },
  openOii: {
    requestFailed: "OpenOii 설정 요청에 실패했습니다 ({{status}})",
    invalidFormat: "OpenOii가 잘못된 설정 형식을 반환했습니다",
    readFailed: "불러오지 못했습니다",
    saved: "OpenOii 설정이 저장되었습니다",
    restart: "설정을 적용하려면 OpenOii 서비스를 다시 시작해야 합니다: {{keys}}",
    saveFailed: "저장에 실패했습니다",
    title: "OpenOii 설정",
    tokenLabel: "OpenOii 관리자 토큰",
    tokenPlaceholder: "설정을 저장하려면 관리자 토큰이 필요하며 이번 세션에서만 사용됩니다",
    loading: "설정을 불러오는 중…",
    retry: "다시 시도",
    maskedPlaceholder: "설정됨. 비워 두면 기존 값을 유지합니다",
    save: "설정 저장",
  },
  stockIntelligence: {
    saved: "주식 인텔리전스 설정이 저장되었습니다",
    title: "주식 인텔리전스",
    intro: "AlphaSift, Daily Stock Analysis, Vibe Research의 기본 설정을 한곳에서 관리합니다.",
    alpha: {
      desc: "종목 선별 페이지를 열 때마다 미리 채워지는 전략, 후보 수, 리서치 맥락을 설정합니다.",
      strategy: "기본 전략",
      strategyPlaceholder: "비워 두면 사용 가능한 첫 번째 전략을 사용합니다",
      maxResults: "기본 후보 수",
      context: "리서치 맥락 (선택)",
      contextPlaceholder: "예: 잉여현금흐름이 높고 부채가 낮은 기업에 주목",
      useLlm: "기본적으로 AI 순위 사용",
    },
    dsa: {
      desc: "배포된 Daily Stock Analysis 서비스 URL을 입력할 수 있습니다. 비워 두면 데스크톱 내장 서비스를 사용합니다.",
      url: "서비스 URL",
      urlPlaceholder: "예: http://127.0.0.1:8000",
    },
    vibe: {
      desc: "Vibe Research 워크스페이스 URL을 설정합니다. 기본값은 Hanggent가 호스팅하는 워크스페이스입니다.",
      url: "워크스페이스 URL",
      urlPlaceholder: "/vibe-research/ 또는 https://…",
    },
    restored: "기본값으로 복원되었습니다",
    restore: "기본값 복원",
    open: "주식 인텔리전스 열기",
    save: "설정 저장",
    alertsSaved: "알림 설정이 저장되었으며 프로젝트 설정 페이지에도 동기화됩니다",
    alertsSaveFailed: "Stock Alerts 설정 저장에 실패했습니다",
    introWithAlerts: "Stock Alerts, AlphaSift, Daily Stock Analysis, Vibe Research의 기본 설정을 한곳에서 관리합니다.",
    alerts: {
      desc: "시장별로 가격 변동 알림과 이메일 알림을 설정합니다.",
      loading: "불러오는 중…",
      trigger: "트리거 임계값 (%)",
      step: "단계 임계값 (%)",
      enable: "알림 사용",
      email: "이메일 알림",
      extended: "프리마켓 및 시간외 포함",
      save: "{{market}} 설정 저장",
    },
    market: {
      cn: "A주",
      us: "미국 주식",
    },
  },
  subprojects: {
    err: {
      protocol: "서비스 URL은 HTTP 또는 HTTPS를 사용해야 합니다",
      httpsRequired: "HTTPS 페이지에는 HTTPS 서비스 URL이 필요합니다",
      comfyui: "ComfyUI에는 이 사이트의 프록시 URL을 사용하세요",
      url: "서비스 URL은 HTTP, HTTPS 또는 이 사이트의 경로여야 합니다",
    },
  },
  settingsMisc: {
    saveAlphaDefaults: "기본값으로 저장 (Hanggent 설정과 동기화)",
    openProjectSettings: "프로젝트 설정 열기",
    emailService: "이메일 서비스",
  },
  settingsErrors: {
    stockAlertsRequest: "Stock Alerts 설정 요청에 실패했습니다 ({{status}})",
    stockAlertsFormat: "Stock Alerts 설정 형식이 잘못되었습니다",
    stockAlertsMissing: "이 시장의 설정이 더 이상 존재하지 않습니다. 다시 불러오세요",
  },
  zhituCareer: {
    heading: "중국 채용 검색 및 직무 매칭",
    description: "이력서를 업로드하고 중국 채용 공고를 검색하며, Zhitu Career의 중국어 직무 매칭과 Career-Ops 평가 보고서로 가장 적합한 포지션을 추립니다.",
    runLabel: "중국 채용 검색",
    resultsLabel: "라이선스된 중국 채용 데이터 소스",
  },
  adminMail: {
    loadFailed: "이메일 서비스 상태를 읽을 수 없습니다",
    verified: "QQ 메일이 확인되어 저장되었습니다",
    saveFailed: "확인 또는 저장에 실패했습니다",
    testSent: "테스트 메일을 {{email}}(으)로 보냈습니다",
    testFailed: "테스트 메일 전송에 실패했습니다",
    title: "통합 이메일 서비스",
    desc: "입시 일일 보고, 주식 알림, 차량/시장 알림이 이 QQ 발신 계정을 공유합니다. 인증 코드는 암호화되어 저장되며 다시 표시되지 않습니다.",
    configured: "설정됨: {{email}}",
    notConfigured: "아직 설정되지 않음",
    qqEmail: "QQ 메일",
    emailPlaceholder: "QQ 메일을 입력하세요",
    smtpCode: "SMTP 인증 코드",
    hint: "저장할 때 QQ SMTP 로그인을 확인합니다. 테스트 메일은 위의 QQ 메일로만 전송됩니다.",
    verifySave: "확인 후 저장",
    sendTest: "테스트 메일 보내기",
  },
};

const appPage_ru: typeof appPageEn = {
  admissionsDaily: {
    priority: {
      high: "Проверьте в первую очередь: может касаться крайнего срока",
      attention: "Рекомендуется проверить: может касаться требований к подаче заявки",
      normal: "Содержимое страницы обновлено",
    },
    action: {
      deadline: "Сначала проверьте крайний срок",
      requirements: "Проверьте документы и соответствие требованиям",
      content: "Прочитайте официальное изменение",
    },
    deliveryStatus: {
      sent: "Отправлено",
      failed: "Не удалось отправить",
      processing: "Обрабатывается",
    },
    listSeparator: ",",
    partialError: "Часть данных о поступлении временно недоступна (элементов: {{count}}); остальное по-прежнему можно просматривать. Обновите страницу позже.",
    title: "Ежедневная сводка официальных изменений по поступлению",
    intro: "Отслеживаются только зарегистрированные официальные страницы поступления на программы бакалавриата. Первичный сбор не означает, что вуз только что опубликовал информацию; ошибки получения данных показываются явно и никогда не считаются «без изменений».",
    reload: "Перезагрузить",
    coverage: {
      total: "Проверенные официальные источники",
      checked: "Подтверждено",
      failed: "Сбой, ожидает повтора",
      stale: "Устарело / безуспешно",
      overHours: "Более {{hours}} ч",
    },
    lastScan: "Последнее сканирование: {{start}} · {{status}} · проверено {{checked}} / изменено {{changed}} / сбоев {{failed}}",
    completedAt: " · завершено {{time}}",
    attention: {
      title: "Проблемы с состоянием источников, требующие внимания",
      desc: "Результатов получения данных из этих источников недостаточно, чтобы заключить «обновлений нет». Откройте официальную страницу и проверьте; администраторы могут пересмотреть или заменить запись в управлении источниками.",
      fetchFailed: "Не удалось получить данные: {{error}}",
      noSuccess: "Успешного получения данных пока нет, либо обновлений не было более {{hours}} ч",
    },
    cohort: {
      title: "Охват вузов и управление источниками",
      body: "{{source}} {{edition}}: {{target}} вузов-кандидатов по умолчанию (по 20 в США, Китае и Канаде). Подтверждено официальных источников бакалавриата: {{verified}}; остальные {{pending}} ожидают ручной проверки и не включены ни в сбор данных, ни в ежедневную сводку.",
      verified: "Подтверждено {{verified}}/{{target}}",
      pending: "{{pending}} ожидают ручной проверки",
    },
    cohortDaily: {
      title: "Официальные обновления дня: топ-20 трёх стран",
      desc: "С {{start}} учитываются только проверенные официальные источники бакалавриата; сводка создана {{generated}}. Вузы, ожидающие проверки, не считаются «без обновлений».",
      today: "Изменений сегодня {{changed}} · охвачено {{active}}/{{target}}",
      failedLine: "Сбоев получения {{failed}} · устарело/безуспешно {{stale}} · ожидают проверки {{pending}}",
      none: "Сегодня для проверенных источников не зафиксировано изменений страниц; базовая версия при первом сканировании не считается изменением.",
    },
    mail: {
      title: "Ежедневная сводка по электронной почте",
      desc: "Если включено, после завершения сканирования официальных источников отправляется ежедневная сводка; в неё входят только изменения вузов, на которые вы подписаны.",
      lastDelivered: " Последняя доставка: {{date}}",
      notConfigured: "Почтовая служба ещё не настроена администратором, поэтому доставку пока включить нельзя.",
      lastFailed: "Последняя доставка не удалась: {{error}}",
      contactAdmin: "Обратитесь к администратору, чтобы проверить настройки почты.",
      setupLink: "Перейти к настройкам почтовой службы (администратор)",
    },
    deliveries: {
      title: "Журнал доставки писем",
      desc: "Последние попытки доставки ежедневной сводки. Неудачные безопасно повторяются при следующих сканированиях.",
      row: "{{date}} · {{status}} · изменений: {{count}}",
      sentAt: "Отправлено {{time}}",
      waiting: "Ожидание завершения доставки",
    },
    suggestions: {
      title: "Подписаться по целевым странам",
      body: "Цели профиля: {{countries}}. Можно добавить проверенных официальных источников: {{count}}; результаты изучения направлений не принимаются за прогноз поступления.",
      unsupported: "{{countries}} пока не входит в охват официальных источников топ-20 США/Китая/Канады на этом этапе, поэтому не будет показано как охваченное.",
      followAll: "Подписаться на все проверенные источники",
    },
    digest: {
      title: "Относится к вузам, на которые вы подписаны",
      window: "Период сводки: с {{start}} · создана {{generated}}",
      countryLine: "{{country}}: подписок {{watched}} · изменено сегодня {{changed}} · сбоев {{failed}} · устарело/безуспешно {{stale}}",
      emptyWatched: "С 00:00 UTC сегодня новых изменений страниц у вузов, на которые вы подписаны, не обнаружено.",
      emptyNone: "Выберите вузы ниже и сохраните, чтобы получить персональную сводку.",
    },
    suggestedAction: "Рекомендация: {{hint}}",
    newContent: "Контент, добавленный на страницу",
    removedContent: "Контент, удалённый или заменённый на странице",
    viewOfficial: "Открыть официальный источник и проверить",
    changes: {
      title: "Обнаруженные существенные изменения страниц",
      empty: "Записанных изменений пока нет. Первое сканирование создаёт базовую версию; только последующие сканирования формируют проверяемые записи об изменениях.",
    },
    watch: {
      title: "Отслеживаемые вузы и состояние охвата",
      hint: "Выберите и сохраните; сводка включает только изменения этих вузов.",
      save: "Сохранить отслеживаемые вузы",
      country: "Страна",
      filterCountry: "Фильтровать отслеживаемые вузы по стране",
      status: "Состояние источника",
      filterHealth: "Фильтровать отслеживаемые вузы по состоянию источника",
      all: "Все",
      attention: "Требует внимания",
      healthy: "В порядке",
      showing: "Показано {{shown}}/{{total}} вузов",
      officialPage: "Официальная страница поступления на бакалавриат",
      lastSuccess: "Последнее успешное получение: {{time}}",
      noSuccess: "Успешного получения данных пока нет.",
      staleOver: "Данным больше {{hours}} ч.",
      noMatch: "Нет официальных источников, соответствующих текущим фильтрам.",
    },
  },
  educationCenter: {
    title: "Планирование обучения и новости поступления",
    desc: "Сохраните учебный профиль, изучайте направления и следите за поступлением по официальным источникам. Результаты не являются прогнозом поступления.",
    planning: "Планирование обучения",
    admissions: "Ежедневная сводка по поступлению",
  },
  educationPlanning: {
    defaultCountry: "Китай",
    defaultCurriculum: "Китайская общеобразовательная программа старшей школы",
    subject: {
      chinese: "Китайский язык",
      math: "Математика",
      foreign: "Иностранный язык",
      physics: "Физика",
      history: "История",
      chemistry: "Химия",
      biology: "Биология",
      geography: "География",
      politics: "Политика",
    },
    category: {
      strong: "Изучить в первую очередь",
      consider: "Стоит рассмотреть подробнее",
      not_recommended: "Есть конфликты",
      ineligible: "Требования к предметам не выполнены",
    },
    title: "Выбор направления и пути обучения",
    intro: "Сначала запишите свои реальные курсы и предпочтения, затем изучайте направления. Соответствие требованиям вузов и новости поступления будут проверяться по официальным правилам на следующих этапах.",
    readiness: {
      title: "Готовность к решению об обучении",
      progress: "Выполнено {{done}}/{{total}}. Это список действий, а не вероятность поступления.",
      viewAdmissions: "Открыть официальную ежедневную сводку по поступлению",
    },
    field: {
      name: "Имя",
      namePlaceholder: "напр., Danny",
      province: "Провинция гаокао",
      provincePlaceholder: "напр., Шэньси",
      year: "Ожидаемый год поступления",
      curriculum: "Учебная программа",
      countries: "Целевые страны",
      countriesPlaceholder: "Китай, Канада, США",
      mode: "Режим нового гаокао",
      electives: "Предметы по выбору",
      electivesPlaceholder: "напр., 物理, 化学, 生物 (названия предметов на китайском)",
      favorites: "Любимые предметы",
      favoritesPlaceholder: "напр., 数学, 物理 (названия предметов на китайском)",
      dislikes: "Предметы, которые вам не нравятся или которых вы хотите избежать",
      dislikesPlaceholder: "напр., 化学 (название предмета на китайском)",
      interests: "Интересы",
      interestsPlaceholder: "ИИ, здравоохранение",
      budget: "Бюджет или ограничения (необязательно)",
      budgetPlaceholder: "напр., приоритет стипендиям, хочу остаться в Ванкувере.",
    },
    saveProfile: "Сохранить профиль",
    exploreMajors: "Изучить китайские направления по баллам",
    cmpt: {
      title: "Изучение профессионального направления (CMPT310)",
      desc: "Экспериментальная модель, независимая от изучения направлений гаокао. Она не прогнозирует поступление или трудоустройство; отправленные данные записываются только во временный CSV для подпроцесса и удаляются до ответа.",
      unavailable: "Коннектор сейчас недоступен.",
      adminRequires: "Администратору необходимо настроить: {{items}}.",
      listSeparator: ",",
      run: "Запустить экспериментальное изучение профессионального направления",
      confidence: "Уверенность модели {{percent}}%",
    },
    results: {
      title: "Результаты изучения направлений",
      nextStep: "Далее: открыть официальную ежедневную сводку по поступлению",
      noConflict: "Явных конфликтов в требованиях к предметам и предметам по выбору не найдено.",
      match: "Совпадение по предметам {{percent}}%",
    },
  },
  privateModel: {
    loadFailed: "Не удалось загрузить студию моделей",
    actionFailed: "Не удалось выполнить действие",
    uploaded: "Загружено, ожидает проверки",
    viewFailed: "Не удалось просмотреть содержимое",
    logFailed: "Не удалось прочитать журнал",
    queryFailed: "Не удалось выполнить запрос к локальной модели",
    title: "Студия приватных моделей",
    intro: "Проверяйте корпоративные материалы, обучайте собственную небольшую модель, проверяйте отложенные примеры и публикуйте её в локальный Ollama.",
    status: "Локальное обучение: {{ready}} · одобренных документов {{documents}} · одобренных обучающих примеров {{examples}}",
    ready: "готово",
    notReady: "не готово",
    upload: {
      title: "Загрузка и проверка",
      desc: "Для документов используйте .txt, .md или .pdf; для обучающих примеров — JSONL, где в каждой строке есть instruction и output. Весь контент сначала получает статус «ожидает проверки».",
      aria: "Загрузить приватные данные",
    },
    item: {
      document: "Документ",
      example: "Пример",
      viewFull: "Показать полный текст",
      approve: "Одобрить",
      reject: "Отклонить",
      approved: "Одобрено",
      rejected: "Отклонено",
      empty: "Материалов пока нет",
    },
    qa: {
      title: "Вопросы и ответы по локальным знаниям",
      desc: "Поиск ведётся только по одобренным документам, с отображением использованных источников. Модель работает на локальном Ollama сервера.",
      base06: "Базовая модель Qwen3 0.6B",
      base17: "Базовая модель Qwen3 1.7B",
      noModels: "В локальном Ollama пока нет моделей; сначала установите базовую модель или завершите обучение и опубликуйте.",
      placeholder: "Введите вопрос о корпоративных знаниях",
      ask: "Спросить",
      source: "Источник: {{filename}} #{{id}}",
    },
    train: {
      title: "Обучение, оценка и публикация",
      desc: "Одобрите не менее 5 примеров. При обучении часть примеров откладывается для независимой оценки; оценки носят справочный характер, поэтому перед публикацией проверьте каждый ответ.",
      workerNeeded: "Включите worker на локальном обучающем хосте и установите LLaMA-Factory CLI и Ollama.",
      start: "Начать обучение",
      submitted: "Задача обучения отправлена",
    },
    job: {
      title: "Задача #{{id}} · {{model}}",
      samples: "{{state}} · примеров: {{count}}",
      viewLog: "Показать локальный журнал",
      localModel: "Локальная модель:",
      evaluate: "Оценить модель-кандидат",
      evaluating: "Оценка модели-кандидата запущена",
      holdout: "Отложенных примеров {{count}} · F1 по токенам {{f1}} (по-прежнему требуется ручная проверка)",
      sample: "Пример #{{id}} · F1 {{f1}}",
      expected: "Эталонный ответ: {{text}}",
      actual: "Ответ модели: {{text}}",
      reviewed: "Я проверил(а) каждый ответ модели на отложенных примерах",
      publish: "Одобрить и опубликовать",
      publishing: "Публикация в локальный Ollama",
      empty: "Задач обучения пока нет",
    },
    dialog: {
      aria: "Полный текст материала на проверке",
      close: "Закрыть",
      question: "Вопрос: {{text}}",
      answer: "Ответ: {{text}}",
    },
  },
  adminAdmissions: {
    runStatus: {
      completed: "Завершено",
      partial: "Частично завершено",
      failed: "Запуск не удался",
      running: "Выполняется",
    },
    queueNoWorker: "Очередь проверки загружена, но статус ежедневной задачи недоступен",
    queueNoHealth: "Очередь проверки загружена, но состояние источников недоступно",
    urlRequired: "Введите HTTPS-адрес официальной страницы поступления на бакалавриат",
    enabled: "Для {{university}} включён мониторинг официального поступления",
    notSaved: "Проверка не сохранена",
    syncDone: "Синхронизация завершена: проверено {{checked}}, изменено {{changed}}, сбоев {{failed}}",
    syncFailed: "Не удалось синхронизировать официальные источники",
    title: "Проверка официальных источников поступления",
    desc: "Ежедневно загружаются только официальные HTTPS-страницы поступления на бакалавриат, одобренные ручной проверкой. При сохранении записываются проверяющий, время и примечание.",
    syncNow: "Синхронизировать проверенные источники сейчас",
    showPending: "Показывать только ожидающие",
    showVerified: "Показать подтверждённые ({{count}})",
    filterAria: "Фильтровать очередь проверки по стране",
    allCountries: "Все страны",
    counts: "Ожидают проверки: {{pending}}; показано: {{shown}}.",
    countsSources: " Проверенных источников: {{total}}, из них сбоев {{failed}} и устаревших/безуспешных {{stale}} (порог {{hours}} ч).",
    lastScan: "Последнее сканирование: {{start}} · {{status}} · проверено {{checked}} / изменено {{changed}} / сбоев {{failed}}",
    completedAt: " · завершено {{time}}",
    workerOn: "Ежедневная задача мониторинга включена, опрос каждые {{hours}} ч.",
    workerOff: "Ежедневная задача мониторинга не включена; данные можно обновить только кнопкой «Синхронизировать сейчас».",
    rank: "{{country}} · номер в охвате {{rank}}",
    verified: "Подтверждено",
    pending: "Ожидает проверки",
    fetchFailed: "Не удалось получить данные: {{error}}",
    staleHint: "Источник устарел или ещё не был успешно получен; откройте официальную страницу, проверьте её и затем синхронизируйте.",
    urlPlaceholder: "https://… официальная страница поступления на бакалавриат",
    notePlaceholder: "Примечание проверяющего (укажите категорию абитуриентов и год поступления, к которым оно относится)",
    openSource: "Открыть зарегистрированный источник",
    updateReview: "Обновить проверенный источник",
    approve: "Проверить и включить мониторинг",
  },
  openOii: {
    requestFailed: "Не удалось выполнить запрос конфигурации OpenOii ({{status}})",
    invalidFormat: "OpenOii вернул недопустимый формат конфигурации",
    readFailed: "Не удалось загрузить",
    saved: "Конфигурация OpenOii сохранена",
    restart: "Перезапустите службу OpenOii, чтобы применить конфигурацию: {{keys}}",
    saveFailed: "Не удалось сохранить",
    title: "Конфигурация OpenOii",
    tokenLabel: "Токен администратора OpenOii",
    tokenPlaceholder: "Для сохранения конфигурации нужен токен администратора; используется только в этом сеансе",
    loading: "Загрузка конфигурации…",
    retry: "Повторить",
    maskedPlaceholder: "Настроено; оставьте поле пустым, чтобы сохранить текущее значение",
    save: "Сохранить конфигурацию",
  },
  stockIntelligence: {
    saved: "Настройки биржевой аналитики сохранены",
    title: "Биржевая аналитика",
    intro: "Управляйте настройками по умолчанию для AlphaSift, Daily Stock Analysis и Vibe Research в одном месте.",
    alpha: {
      desc: "Задайте стратегию, количество кандидатов и исследовательский контекст, подставляемые при каждом открытии скринера акций.",
      strategy: "Стратегия по умолчанию",
      strategyPlaceholder: "Оставьте пустым, чтобы использовать первую доступную стратегию",
      maxResults: "Количество кандидатов по умолчанию",
      context: "Исследовательский контекст (необязательно)",
      contextPlaceholder: "напр., сосредоточиться на компаниях с высоким свободным денежным потоком и низким долгом",
      useLlm: "Включить ранжирование с помощью ИИ по умолчанию",
    },
    dsa: {
      desc: "При желании укажите адрес развёрнутой службы Daily Stock Analysis; оставьте пустым, чтобы использовать встроенную службу настольного приложения.",
      url: "Адрес службы",
      urlPlaceholder: "напр., http://127.0.0.1:8000",
    },
    vibe: {
      desc: "Задайте адрес рабочей области Vibe Research. По умолчанию используется рабочая область, размещённая Hanggent.",
      url: "Адрес рабочей области",
      urlPlaceholder: "/vibe-research/ или https://…",
    },
    restored: "Значения по умолчанию восстановлены",
    restore: "Восстановить значения по умолчанию",
    open: "Открыть биржевую аналитику",
    save: "Сохранить настройки",
    alertsSaved: "Настройки оповещений сохранены; страница настроек проекта синхронизирована",
    alertsSaveFailed: "Не удалось сохранить настройки Stock Alerts",
    introWithAlerts: "Управляйте настройками по умолчанию для Stock Alerts, AlphaSift, Daily Stock Analysis и Vibe Research в одном месте.",
    alerts: {
      desc: "Настройте оповещения об изменении цены и уведомления по почте для каждого рынка.",
      loading: "Загрузка…",
      trigger: "Порог срабатывания (%)",
      step: "Ступенчатый порог (%)",
      enable: "Включить оповещения",
      email: "Уведомления по почте",
      extended: "Включая премаркет и постмаркет",
      save: "Сохранить настройки: {{market}}",
    },
    market: {
      cn: "A-акции",
      us: "Акции США",
    },
  },
  subprojects: {
    err: {
      protocol: "Адреса служб должны использовать HTTP или HTTPS",
      httpsRequired: "Для HTTPS-страниц нужны HTTPS-адреса служб",
      comfyui: "Для ComfyUI используйте адрес прокси этого сайта",
      url: "Адреса служб должны быть HTTP, HTTPS или путём на этом сайте",
    },
  },
  settingsMisc: {
    saveAlphaDefaults: "Сохранить как значения по умолчанию (синхронизируется с настройками Hanggent)",
    openProjectSettings: "Открыть настройки проекта",
    emailService: "Почтовая служба",
  },
  settingsErrors: {
    stockAlertsRequest: "Не удалось выполнить запрос настроек Stock Alerts ({{status}})",
    stockAlertsFormat: "Недопустимый формат настроек Stock Alerts",
    stockAlertsMissing: "Настроек для этого рынка больше нет; перезагрузите страницу",
  },
  zhituCareer: {
    heading: "Поиск работы в Китае и подбор карьеры",
    description: "Загрузите резюме, ищите вакансии в Китае и отбирайте лучшие позиции с помощью китайского подбора карьеры Zhitu Career и отчётов оценки Career-Ops.",
    runLabel: "Искать вакансии в Китае",
    resultsLabel: "Лицензированные источники данных о вакансиях в Китае",
  },
  adminMail: {
    loadFailed: "Не удалось прочитать состояние почтовой службы",
    verified: "Почтовый ящик QQ проверен и сохранён",
    saveFailed: "Не удалось проверить или сохранить",
    testSent: "Тестовое письмо отправлено на {{email}}",
    testFailed: "Не удалось отправить тестовое письмо",
    title: "Единая почтовая служба",
    desc: "Ежедневные сводки по поступлению, биржевые оповещения и оповещения об автомобилях/рынке используют этот отправляющий аккаунт QQ. Код авторизации хранится в зашифрованном виде и больше не показывается.",
    configured: "Настроено: {{email}}",
    notConfigured: "Пока не настроено",
    qqEmail: "Почтовый ящик QQ",
    emailPlaceholder: "Введите почтовый ящик QQ",
    smtpCode: "Код авторизации SMTP",
    hint: "При сохранении проверяется вход в QQ SMTP; тестовое письмо отправляется только на указанный выше ящик QQ.",
    verifySave: "Проверить и сохранить",
    sendTest: "Отправить тестовое письмо",
  },
};

const appPage_zh_Hant: typeof appPageEn = {
  admissionsDaily: {
    priority: {
      high: "優先核驗：可能涉及截止日期",
      attention: "建議核驗：可能涉及申請要求",
      normal: "頁面內容更新",
    },
    action: {
      deadline: "先核對截止日期",
      requirements: "核對材料與資格",
      content: "閱讀官方變更",
    },
    deliveryStatus: {
      sent: "已發送",
      failed: "發送失敗",
      processing: "處理中",
    },
    listSeparator: "、",
    partialError: "部分招生資料暫時無法取得（{{count}} 項）；其餘內容仍可查看。請稍後重新整理。",
    title: "官方招生變更日報",
    intro: "僅監測登記的官方大學部招生入口。首次收錄不等同於學校剛發布；擷取失敗會明確顯示，不會被視為「無變化」。",
    reload: "重新載入",
    coverage: {
      total: "已審核官方來源",
      checked: "已完成核驗",
      failed: "失敗待重試",
      stale: "資料陳舊 / 未成功",
      overHours: "超過 {{hours}} 小時",
    },
    lastScan: "最近掃描：{{start}} · {{status}} · 檢查 {{checked}} / 變化 {{changed}} / 失敗 {{failed}}",
    completedAt: " · 完成於 {{time}}",
    attention: {
      title: "需要處理的來源健康問題",
      desc: "這些來源的擷取結果不足以支持「暫無更新」的判斷。請開啟官方頁面核驗；管理員可在來源治理中複核或替換入口。",
      fetchFailed: "擷取失敗：{{error}}",
      noSuccess: "尚無成功擷取或超過 {{hours}} 小時未更新",
    },
    cohort: {
      title: "院校範圍與來源治理",
      body: "{{source}} {{edition}}：{{target}} 所預設候選院校（美、中、加各 20 所）。已核驗官方大學部來源 {{verified}} 所；其餘 {{pending}} 所仍待人工複核，未加入擷取或日報。",
      verified: "已核驗 {{verified}}/{{target}}",
      pending: "待人工複核 {{pending}} 所",
    },
    cohortDaily: {
      title: "三國 Top20 當日官方更新",
      desc: "自 {{start}} 起，僅統計已審核官方大學部來源；摘要產生於 {{generated}}。待複核學校不會被計為「無更新」。",
      today: "今日變化 {{changed}} · 已覆蓋 {{active}}/{{target}}",
      failedLine: "擷取失敗 {{failed}} · 陳舊/未成功 {{stale}} · 待複核 {{pending}}",
      none: "當日尚未記錄到已審核來源的頁面變更；首次掃描建立基線不計為變更。",
    },
    mail: {
      title: "每日郵件摘要",
      desc: "開啟後，完成官方來源掃描時會發送當日摘要；僅包含你關注學校的變化。",
      lastDelivered: " 最近投遞：{{date}}",
      notConfigured: "郵件服務尚未由管理員設定，暫時無法開啟投遞。",
      lastFailed: "最近投遞失敗：{{error}}",
      contactAdmin: "請聯絡管理員檢查郵件設定。",
      setupLink: "前往郵件服務設定（管理員）",
    },
    deliveries: {
      title: "郵件投遞記錄",
      desc: "最近的日報投遞嘗試。失敗記錄會在後續掃描中安全重試。",
      row: "{{date}} · {{status}} · {{count}} 項變化",
      sentAt: "發送於 {{time}}",
      waiting: "等待發送完成",
    },
    suggestions: {
      title: "依你的目標國家關注",
      body: "檔案目標：{{countries}}。可加入 {{count}} 所已審核官方來源；這不會把專業探索結果誤當作錄取預測。",
      unsupported: "{{countries}} 暫未納入本期美、中、加 Top20 官方來源範圍，因此不會顯示為已覆蓋。",
      followAll: "關注全部已審核來源",
    },
    digest: {
      title: "與你關注學校相關",
      window: "日報視窗：{{start}} 起 · 產生於 {{generated}}",
      countryLine: "{{country}}：關注 {{watched}} · 今日變更 {{changed}} · 失敗 {{failed}} · 陳舊/未成功 {{stale}}",
      emptyWatched: "自今日 UTC 00:00 起，暫未發現你關注學校的新頁面變更。",
      emptyNone: "在下方勾選學校並儲存，即可產生個人化日報。",
    },
    suggestedAction: "建議：{{hint}}",
    newContent: "頁面新增內容",
    removedContent: "頁面移除或替換內容",
    viewOfficial: "查看官方來源並核驗",
    changes: {
      title: "偵測到的實質頁面變更",
      empty: "尚無已記錄變更。首次掃描會建立基線；後續掃描才會形成可複查的變化記錄。",
    },
    watch: {
      title: "關注學校與覆蓋狀態",
      hint: "勾選後儲存；日報只彙總這些學校的變更。",
      save: "儲存關注學校",
      country: "國家",
      filterCountry: "依國家篩選關注學校",
      status: "來源狀態",
      filterHealth: "依來源健康篩選關注學校",
      all: "全部",
      attention: "需處理",
      healthy: "健康",
      showing: "顯示 {{shown}}/{{total}} 所",
      officialPage: "官方大學部招生頁",
      lastSuccess: "最近成功擷取：{{time}}",
      noSuccess: "尚無成功擷取。",
      staleOver: "資料已超過 {{hours}} 小時。",
      noMatch: "沒有符合目前篩選條件的官方來源。",
    },
  },
  educationCenter: {
    title: "升學規劃與招生動態",
    desc: "儲存學習檔案、探索專業，並依官方來源追蹤招生資訊。結果不代表錄取預測。",
    planning: "升學規劃",
    admissions: "招生日報",
  },
  educationPlanning: {
    defaultCountry: "中國",
    defaultCurriculum: "中國普通高中",
    subject: {
      chinese: "語文",
      math: "數學",
      foreign: "外語",
      physics: "物理",
      history: "歷史",
      chemistry: "化學",
      biology: "生物",
      geography: "地理",
      politics: "政治",
    },
    category: {
      strong: "優先探索",
      consider: "可進一步了解",
      not_recommended: "存在衝突",
      ineligible: "目前不符合選考科目",
    },
    title: "專業選擇與升學路徑",
    intro: "先記錄真實課程與偏好，再探索專業路徑。院校資格和招生動態將在後續階段以官方規則核驗。",
    readiness: {
      title: "升學決策就緒度",
      progress: "完成 {{done}}/{{total}} 項。這是行動清單，不是錄取機率。",
      viewAdmissions: "查看官方招生日報",
    },
    field: {
      name: "稱呼",
      namePlaceholder: "例如：Danny",
      province: "高考省份",
      provincePlaceholder: "例如：陝西",
      year: "預計入學年份",
      curriculum: "課程體系",
      countries: "目標國家",
      countriesPlaceholder: "中國, 加拿大, 美國",
      mode: "新高考模式",
      electives: "選考科目",
      electivesPlaceholder: "例如：物理, 化學, 生物（中文科目名稱）",
      favorites: "喜歡的科目",
      favoritesPlaceholder: "例如：數學, 物理（中文科目名稱）",
      dislikes: "不喜歡或想避免的科目",
      dislikesPlaceholder: "例如：化學（中文科目名稱）",
      interests: "興趣方向",
      interestsPlaceholder: "人工智慧, 醫療健康",
      budget: "預算或限制（選填）",
      budgetPlaceholder: "例如：優先獎學金、希望留在溫哥華。",
    },
    saveProfile: "儲存檔案",
    exploreMajors: "依成績探索中國專業",
    cmpt: {
      title: "職業方向探索（CMPT310）",
      desc: "獨立於高考專業探索的實驗性模型。它不預測錄取或就業；提交的資料僅寫入暫時 CSV 供子程序計算，回應前刪除。",
      unavailable: "目前連接器無法使用。",
      adminRequires: "管理員需設定：{{items}}。",
      listSeparator: "、",
      run: "實驗性職業方向探索",
      confidence: "模型信心度 {{percent}}%",
    },
    results: {
      title: "專業探索結果",
      nextStep: "下一步：查看官方招生日報",
      noConflict: "學科和選考條件沒有發現明顯衝突。",
      match: "學科符合度 {{percent}}%",
    },
  },
  privateModel: {
    loadFailed: "無法讀取模型工作台",
    actionFailed: "操作失敗",
    uploaded: "已上傳，等待審核",
    viewFailed: "無法查看內容",
    logFailed: "無法讀取日誌",
    queryFailed: "本地模型查詢失敗",
    title: "私有模型工作台",
    intro: "審核企業資料，訓練專屬小模型，檢查保留樣本，再發布到本地 Ollama。",
    status: "本地訓練：{{ready}} · 已批准文件 {{documents}} · 已批准訓練樣本 {{examples}}",
    ready: "就緒",
    notReady: "未就緒",
    upload: {
      title: "上傳與審核",
      desc: "文件使用 .txt、.md 或 .pdf；訓練樣本使用 JSONL，每行包含 instruction 和 output。所有內容先進入待審核狀態。",
      aria: "上傳私有資料",
    },
    item: {
      document: "文件",
      example: "樣本",
      viewFull: "查看全文",
      approve: "批准",
      reject: "拒絕",
      approved: "已批准",
      rejected: "已拒絕",
      empty: "暫無資料",
    },
    qa: {
      title: "本地知識問答",
      desc: "只檢索已批准的文件，並顯示使用的來源。模型運行在伺服器本機 Ollama。",
      base06: "Qwen3 0.6B 基礎模型",
      base17: "Qwen3 1.7B 基礎模型",
      noModels: "本機 Ollama 尚無模型；請先安裝基礎模型，或完成訓練並發布。",
      placeholder: "輸入企業知識問題",
      ask: "提問",
      source: "來源：{{filename}} #{{id}}",
    },
    train: {
      title: "訓練、評測與發布",
      desc: "至少批准 5 筆樣本。訓練會保留一部分樣本用於獨立評測；評測分數僅供參考，發布前請逐筆檢查回答。",
      workerNeeded: "需要在本地訓練主機啟用 worker，並安裝 LLaMA-Factory CLI 與 Ollama。",
      start: "開始訓練",
      submitted: "訓練任務已提交",
    },
    job: {
      title: "任務 #{{id}} · {{model}}",
      samples: "{{state}} · {{count}} 筆樣本",
      viewLog: "查看本地日誌",
      localModel: "本地模型：",
      evaluate: "評測候選模型",
      evaluating: "開始評測候選模型",
      holdout: "保留樣本 {{count}} 筆 · 詞項 F1 {{f1}}（仍需人工複核）",
      sample: "樣本 #{{id}} · F1 {{f1}}",
      expected: "參考答案：{{text}}",
      actual: "模型回答：{{text}}",
      reviewed: "我已逐筆複核保留樣本的模型回答",
      publish: "審核通過並發布",
      publishing: "正在發布到本地 Ollama",
      empty: "暫無訓練任務",
    },
    dialog: {
      aria: "審核資料全文",
      close: "關閉",
      question: "問題：{{text}}",
      answer: "答案：{{text}}",
    },
  },
  adminAdmissions: {
    runStatus: {
      completed: "已完成",
      partial: "部分完成",
      failed: "執行失敗",
      running: "執行中",
    },
    queueNoWorker: "審核佇列已載入，但每日任務狀態暫時無法取得",
    queueNoHealth: "審核佇列已載入，但來源健康狀態暫時無法取得",
    urlRequired: "請填寫官方大學部招生頁 HTTPS URL",
    enabled: "{{university}} 已啟用官方招生監測",
    notSaved: "審核未儲存",
    syncDone: "同步完成：檢查 {{checked}}，變化 {{changed}}，失敗 {{failed}}",
    syncFailed: "官方來源同步失敗",
    title: "招生官方來源審核",
    desc: "只有經人工審核通過的 HTTPS 官方大學部招生頁才會被每日擷取。儲存時會記錄審核人、時間與說明。",
    syncNow: "立即同步已審核來源",
    showPending: "僅顯示待審核",
    showVerified: "顯示已核驗 ({{count}})",
    filterAria: "依國家篩選審核佇列",
    allCountries: "全部國家",
    counts: "待審核 {{pending}} 所；目前顯示 {{shown}} 所。",
    countsSources: " 已審核來源 {{total}} 所，其中失敗 {{failed}} 所、陳舊/未成功 {{stale}} 所（閾值 {{hours}} 小時）。",
    lastScan: "最近掃描：{{start}} · {{status}} · 檢查 {{checked}} / 變化 {{changed}} / 失敗 {{failed}}",
    completedAt: " · 完成於 {{time}}",
    workerOn: "每日監測任務已啟用，輪詢週期 {{hours}} 小時。",
    workerOff: "每日監測任務目前未啟用；只能依靠「立即同步」更新資料。",
    rank: "{{country}} · 範圍內序號 {{rank}}",
    verified: "已核驗",
    pending: "待審核",
    fetchFailed: "擷取失敗：{{error}}",
    staleHint: "來源陳舊或尚無成功擷取；請開啟官方頁面複核後立即同步。",
    urlPlaceholder: "https://… 官方大學部招生頁",
    notePlaceholder: "審核說明（建議寫明適用申請人類別與入學年份）",
    openSource: "開啟已登記來源",
    updateReview: "更新審核來源",
    approve: "審核並啟用監測",
  },
  openOii: {
    requestFailed: "OpenOii 設定請求失敗 ({{status}})",
    invalidFormat: "OpenOii 回傳的設定格式無效",
    readFailed: "讀取失敗",
    saved: "OpenOii 設定已儲存",
    restart: "需要重新啟動 OpenOii 服務以套用設定：{{keys}}",
    saveFailed: "儲存失敗",
    title: "OpenOii 設定",
    tokenLabel: "OpenOii 管理員權杖",
    tokenPlaceholder: "儲存設定需要管理員權杖；僅用於本次工作階段",
    loading: "正在讀取設定…",
    retry: "重試",
    maskedPlaceholder: "已設定；留空保留原值",
    save: "儲存設定",
  },
  stockIntelligence: {
    saved: "股票智慧設定已儲存",
    title: "股票智慧",
    intro: "集中管理 AlphaSift、Daily Stock Analysis 與 Vibe Research 的預設設定。",
    alpha: {
      desc: "設定每次開啟選股頁面時預填的策略、候選數量和研究背景。",
      strategy: "預設策略",
      strategyPlaceholder: "留空以使用第一個可用策略",
      maxResults: "預設候選數量",
      context: "研究背景（選填）",
      contextPlaceholder: "例如：關注高自由現金流與低負債公司",
      useLlm: "預設啟用 AI 排名",
    },
    dsa: {
      desc: "可選填已部署的 Daily Stock Analysis 服務位址；留空則使用桌面端內建服務。",
      url: "服務位址",
      urlPlaceholder: "例如：http://127.0.0.1:8000",
    },
    vibe: {
      desc: "設定 Vibe Research 工作區位址。預設使用 Hanggent 託管的工作區。",
      url: "工作區位址",
      urlPlaceholder: "/vibe-research/ 或 https://…",
    },
    restored: "已還原預設設定",
    restore: "還原預設",
    open: "開啟股票智慧",
    save: "儲存設定",
    alertsSaved: "警示設定已儲存，專案設定頁同步更新",
    alertsSaveFailed: "儲存 Stock Alerts 設定失敗",
    introWithAlerts: "集中管理 Stock Alerts、AlphaSift、Daily Stock Analysis 與 Vibe Research 的預設設定。",
    alerts: {
      desc: "依市場設定價格變動提醒與郵件通知。",
      loading: "正在載入…",
      trigger: "觸發閾值 (%)",
      step: "遞進閾值 (%)",
      enable: "啟用警示",
      email: "郵件通知",
      extended: "包含盤前和盤後",
      save: "儲存 {{market}}設定",
    },
    market: {
      cn: "A 股",
      us: "美股",
    },
  },
  subprojects: {
    err: {
      protocol: "服務位址必須使用 HTTP 或 HTTPS",
      httpsRequired: "HTTPS 頁面需要 HTTPS 服務位址",
      comfyui: "ComfyUI 請使用本站代理位址",
      url: "服務位址必須是 HTTP、HTTPS 位址或本站路徑",
    },
  },
  settingsMisc: {
    saveAlphaDefaults: "儲存為預設設定（同步 Hanggent Settings）",
    openProjectSettings: "開啟專案設定頁",
    emailService: "郵件服務",
  },
  settingsErrors: {
    stockAlertsRequest: "Stock Alerts 設定請求失敗 ({{status}})",
    stockAlertsFormat: "Stock Alerts 設定格式無效",
    stockAlertsMissing: "該市場的設定已不存在，請重新載入",
  },
  zhituCareer: {
    heading: "中國職位搜尋與職業媒合",
    description: "上傳履歷，搜尋中國職位，並使用 Zhitu Career 的中文職業媒合與 Career-Ops 評估報告篩選最合適的職缺。",
    runLabel: "搜尋中國職位",
    resultsLabel: "授權中國職位資料來源",
  },
  adminMail: {
    loadFailed: "無法讀取郵件服務狀態",
    verified: "QQ 信箱已驗證並儲存",
    saveFailed: "驗證或儲存失敗",
    testSent: "測試郵件已發送到 {{email}}",
    testFailed: "測試郵件發送失敗",
    title: "統一郵件服務",
    desc: "招生日報、股票提醒和車輛/市場提醒共用此 QQ 寄件帳號。授權碼加密儲存，不會再顯示。",
    configured: "已設定：{{email}}",
    notConfigured: "尚未設定",
    qqEmail: "QQ 信箱",
    emailPlaceholder: "請輸入 QQ 信箱",
    smtpCode: "SMTP 授權碼",
    hint: "儲存時驗證 QQ SMTP 登入；測試郵件僅發送到上述 QQ 信箱。",
    verifySave: "驗證並儲存",
    sendTest: "發送測試郵件",
  },
};

export const appPageTranslations: Record<string, typeof appPageEn> = {
  "en-US": appPageEn,
  "zh-Hans": appPageZhHans,
  "ar": appPage_ar,
  "de": appPage_de,
  "es": appPage_es,
  "fr": appPage_fr,
  "hi": appPage_hi,
  "it": appPage_it,
  "ja": appPage_ja,
  "ko": appPage_ko,
  "ru": appPage_ru,
  "zh-Hant": appPage_zh_Hant,
};
